if (!customElements.get('product-marking-selector')) {
  const RECENT_LOGO_LIMIT = 4;
  const RECENT_LOGO_DB_NAME = 'todaymerch-studio';
  const RECENT_LOGO_DB_VERSION = 1;
  const RECENT_LOGO_STORE_NAME = 'recentLogoState';
  const RECENT_LOGO_STATE_ID = 'logos';
  const RECENT_LOGO_CONTENT_KEYS = new WeakMap();
  const STUDIO_KEY_CACHE = new Map();
  const STUDIO_PLACEMENT_ALIASES = Object.freeze({
    '360': 'wrap', face: 'front', devant: 'front', zone_face: 'front', dos: 'back', rear: 'back',
    zone_dos: 'back', zone_360: 'wrap', tour_complet: 'wrap', marquage_360: 'wrap', cote: 'side',
    lateral: 'side', zone_cote: 'side', interior: 'inside', interieur: 'inside', coeur: 'heart',
    left_chest: 'heart', anti_coeur: 'chest', poitrine: 'chest', manche: 'sleeve',
    manche_gauche: 'left_sleeve', manche_droite: 'right_sleeve',
  });
  const STUDIO_PREVIEW_MAX_SIDE = 1800;
  const STUDIO_PREVIEW_MAX_BYTES = 25 * 1024 * 1024;
  const ARTWORK_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
  const QUOTE_MOCKUP_PREPARE_TIMEOUT_MS = 10000;

  class ProductMarkingSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.abortController = new AbortController();
      this.prefersReducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
      this.staticOpen = this.dataset.staticOpen === 'true';
      this.studioPreviewUrls = new Map();
      this.studioPreviewOverrides = new Map();
      this.studioVectorizedFiles = new Map();
      this.pdfJsModulePromise = null;
      this.variantLogoStates = new Map();
      this.designTransforms = new Map();
      this.recentLogos = [];
      this.recentLogoPreviewUrls = new Set();
      this.recentLogoPreviewTasks = new Map();
      this.recentLogoSequence = 0;
      this.recentLogoStore = this.getRecentLogoStore();
      this.pendingLogoAppend = null;
      this.isRestoringVariantState = false;
      this.generatedQuoteAssetControls = [];
      this.quoteAssetsPreparing = false;
      this.quoteAssetsResubmitting = false;
      this.initialStudioZoneKey = undefined;
      this.hasExplicitStudioZoneChoice = false;
      this.bound = this.createBoundHandlers();

      this.cacheElements();
      this.cacheStrings();
      this.activeVariantId = this.getInitialVariantId();
      this.currentVariant = this.getSelectedVariantFromDocument();
      this.refreshVariantAvailability();
      this.bindEvents();
      this.initializeDrawer();
      this.setupStudioLight();
      this.syncUI();
      this.markInitialized();
      this.initializeQuotePreparation();
      this.loadRecentLogos();
    }

    disconnectedCallback() {
      this.disposeQuotePreparation();
      this.scopedLogoImportController?.abort();
      this.abortController?.abort();
      this.abortController = undefined;
      if (this.imageBindingsUpdateFrame !== undefined) window.cancelAnimationFrame(this.imageBindingsUpdateFrame);
      this.imageBindingsUpdateFrame = undefined;
      this.drawer?.destroy();
      this.drawer = undefined;
      this.revokeStudioPreviewUrls();
      this.pendingStudioPreviewInput?.remove();
      this.pendingStudioPreviewInput = null;
      this.revokeRecentLogoPreviewUrls();
      this.recentLogoCards = null;
      this.studioFileCards = null;
      this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
      if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = undefined;
      this.bound = undefined;
      this.initialized = false;
    }

    createBoundHandlers() {
      return {
        onChange: this.onChange.bind(this),
        onClick: this.onClick.bind(this),
        onGalleryMediaViewSelected: this.onGalleryMediaViewSelected.bind(this),
        onGalleryVariantMediaUpdated: this.onGalleryVariantMediaUpdated.bind(this),
        onGalleryDesignZoneSelected: this.onGalleryDesignZoneSelected.bind(this),
        onGalleryClearLogo: this.onGalleryClearLogo.bind(this),
        onGalleryUpdateLogo: this.onGalleryUpdateLogo.bind(this),
        onGalleryDesignTransformChange: this.onGalleryDesignTransformChange.bind(this),
        onThemeVariantChange: this.onThemeVariantChange.bind(this),
        onVariantInputChange: this.onVariantInputChange.bind(this),
        onUploadDragOver: this.onUploadDragOver.bind(this),
        onUploadDragLeave: this.onUploadDragLeave.bind(this),
        onUploadDrop: this.onUploadDrop.bind(this),
        onProductFormSubmit: this.onProductFormSubmit.bind(this),
      };
    }

    cacheElements() {
      this.trigger = this.querySelector('[data-role="trigger"]');
      this.studioAddDesign = this.querySelector('[data-role="studio-add-design"]');
      this.panel = this.querySelector('[data-role="panel"]');
      this.summaryValue = this.querySelector('[data-role="summary-value"]');
      this.summaryMeta = this.querySelector('[data-role="summary-meta"]');
      this.optionWraps = Array.from(this.querySelectorAll('[data-role="option-wrap"]'));
      this.inputs = Array.from(this.querySelectorAll('[data-role="marking-input"]'));
      this.fileInputs = Array.from(this.querySelectorAll('[data-role="logo-file-input"]'));
      this.studioLight = this.querySelector('[data-role="studio-light"]');
      this.studioTabs = this.querySelector('[data-role="studio-tabs"]');
      this.studioUploadZone = this.querySelector('[data-role="studio-upload-zone"]');
      this.studioExclusivityNotice = this.querySelector('[data-role="studio-exclusivity-notice"]');
      this.studioExclusivityText = this.querySelector('[data-role="studio-exclusivity-text"]');
      this.studioExclusivityAction = this.querySelector('[data-role="studio-exclusivity-action"]');
      this.brandEntry = this.querySelector('.product-brand-entry, [data-brand-entry-disclosure]');
      this.studioUploadEmpty = this.querySelector('[data-role="studio-upload-empty"]');
      this.studioUploadTitle = this.querySelector('[data-role="studio-upload-title"]');
      this.studioUploadError = this.querySelector('[data-role="studio-upload-error"]');
      this.studioFileCard = this.querySelector('[data-role="studio-file-card"]');
      this.studioFileReplace = this.querySelector('[data-role="studio-file-replace"]');
      this.studioFileRemove = this.querySelector('[data-role="studio-file-remove"]');
      this.studioFileName = this.querySelector('[data-role="studio-file-name"]');
      this.studioFileZone = this.querySelector('[data-role="studio-file-zone"]');
      this.studioFilePreview = this.querySelector('[data-role="studio-file-preview"]');
      this.studioFilePlaceholder = this.querySelector('[data-role="studio-file-placeholder"]');
      this.recentLogosEl = this.querySelector('[data-role="recent-logos"]');
      this.recentLogosList = this.querySelector('[data-role="recent-logos-list"]');
      this.wrapper = this.closest('[data-role="marking-selector-wrapper"]') || this.parentElement;

      this.selectionProperty = this.querySelector('[data-role="property-selection"]');
      this.logoProperty = this.querySelector('[data-role="property-logo"]');
      this.estimateProperty = this.querySelector('[data-role="property-estimate"]');
      this.profileProperty = this.querySelector('[data-role="property-profile"]');
      this.zoneKeysProperty = this.querySelector('[data-role="property-zone-keys"]');
      this.logoZoneKeysProperty = this.querySelector('[data-role="property-logo-zone-keys"]');
      this.artworkTransformProperty = this.querySelector('[data-role="property-artwork-transform"]');

      this.productRoot = this.closest('.section-main-product') || this.closest('product-info') || document;
      this.variantInput = this.getVariantInput();
      this.allOptionWraps = this.optionWraps.slice();
    }

    cacheStrings() {
      const labelDecoder = document.createElement('textarea');
      const previewLabel = (value) => {
        labelDecoder.innerHTML = value || '';
        return labelDecoder.value;
      };
      this.strings = {
        summaryEmpty: this.dataset.summaryEmpty || '',
        summaryOptional: this.dataset.summaryOptional || '',
        logoDefault: this.dataset.labelLogoDefault || '',
        logoActive: this.dataset.labelLogoActive || '',
        countSingular: this.dataset.labelCountSingular || '',
        countPlural: this.dataset.labelCountPlural || '',
        estimateSingle: this.dataset.estimateSingle || '',
        estimateMultiple: this.dataset.estimateMultiple || '',
        estimateCustom: this.dataset.estimateCustom || '',
        addAnotherDesign: this.dataset.labelAddAnotherDesign || '',
        recentLogoAdd: this.dataset.labelRecentLogoAdd || '',
        previewPreparing: previewLabel(this.dataset.labelPreviewPreparing),
        fileAttached: this.dataset.labelFileAttached || '',
        previewUnavailable: previewLabel(this.dataset.labelPreviewUnavailable),
        previewHelp: this.dataset.labelPreviewHelp || '',
        previewReady: this.dataset.labelPreviewReady || '',
        previewFirstPage: this.dataset.labelPreviewFirstPage || '',
        previewTooLarge: this.dataset.labelPreviewTooLarge || '',
        addPreview: this.dataset.labelAddPreview || '',
        addPreviewFor: this.dataset.labelAddPreviewFor || '',
        designNumber: this.dataset.labelDesignNumber || '',
        removeDesign: this.dataset.labelRemoveDesign || '',
        logoFallback: this.dataset.labelLogoFallback || '',
        viewFront: this.dataset.labelViewFront || '',
        viewBack: this.dataset.labelViewBack || '',
        viewRightSleeve: this.dataset.labelViewRightSleeve || '',
        viewLeftSleeve: this.dataset.labelViewLeftSleeve || '',
        viewSleeve: this.dataset.labelViewSleeve || '',
        viewCustom: this.dataset.labelViewCustom || '',
        uploadTitle: this.dataset.labelUploadTitle || '',
        uploadInvalid: this.dataset.labelUploadInvalid || '',
        exclusivityWrapBlocks: this.dataset.labelExclusivityWrapBlocks || '',
        exclusivityOtherBlocks: this.dataset.labelExclusivityOtherBlocks || '',
        exclusivityAction: this.dataset.labelExclusivityAction || '',
        vectorize: this.dataset.labelVectorize || '',
        vectorizeFor: this.dataset.labelVectorizeFor || '',
        vectorizedReady: this.dataset.labelVectorizedReady || '',
        vectorizeUndo: this.dataset.labelVectorizeUndo || '',
      };
    }

    formatString(key, replacements = {}) {
      const template = this.strings?.[key] || '';
      return String(template).replace(/__([A-Z0-9_]+)__/g, (match, token) => (
        Object.prototype.hasOwnProperty.call(replacements, token) ? replacements[token] : match
      ));
    }

    updateAvailabilityVisibility() {
      const hasVisibleOptions = this.productRoot?.dataset.personalizationExperience === 'xd-v2' || (this.optionWraps || []).length > 0;
      if (this.wrapper && this.wrapper !== this) this.wrapper.hidden = !hasVisibleOptions;
      this.hidden = !hasVisibleOptions;

      if (hasVisibleOptions) {
        this.removeAttribute('aria-hidden');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    }

    markInitialized() {
      this.updateAvailabilityVisibility();
      delete this.dataset.initializing;
      this.removeAttribute('inert');
      this.removeAttribute('aria-busy');
      this.classList.add('is-ready');
    }

    bindEvents() {
      if (!this.abortController || !this.bound) return;

      const { signal } = this.abortController;
      this.addEventListener('change', this.bound.onChange, { signal });
      this.addEventListener('click', this.bound.onClick, { signal });
      if (!window.TodayMerchBrandPlacement?.selectPlacement) {
        document.addEventListener('todaymerch:studio-placement-ready', () => this.applyInitialStudioRecommendation(), {
          signal, once: true,
        });
      }

      if (this.studioUploadZone) {
        this.studioUploadZone.addEventListener('dragover', this.bound.onUploadDragOver, { signal });
        this.studioUploadZone.addEventListener('dragleave', this.bound.onUploadDragLeave, { signal });
        this.studioUploadZone.addEventListener('drop', this.bound.onUploadDrop, { signal });
      }

      this.productRoot.addEventListener('todaymerch:product-media-view-selected', this.bound.onGalleryMediaViewSelected, {
        signal,
      });
      this.productRoot.addEventListener('todaymerch:variant-media-updated', this.bound.onGalleryVariantMediaUpdated, {
        signal,
      });
      const mediaGallery = this.getMediaGallery();
      const refreshImageBindings = event => {
        if (!event.target?.matches?.('.product__media > img')
          || event.target.closest('media-gallery') !== mediaGallery
          || !this.productRoot.contains(mediaGallery)) return;
        if (this.imageBindingsUpdateFrame !== undefined) return;
        this.imageBindingsUpdateFrame = window.requestAnimationFrame(() => {
          this.imageBindingsUpdateFrame = undefined;
          if (!this.isConnected || !this.initialized) return;
          // Loading another gallery image does not change the selected Studio
          // zones. Rebuild the form only when a real binding becomes available
          // or fails, and merge the images completing within the same frame.
          if (!this.refreshVariantAvailability({ onlyIfChanged: true })) return;
          this.setupStudioLight();
          this.syncUI();
        });
      };
      for (const name of ['load', 'error']) {
        mediaGallery?.addEventListener(name, refreshImageBindings, { signal, capture: true });
      }
      this.productRoot.addEventListener('todaymerch:product-design-zone-selected', this.bound.onGalleryDesignZoneSelected, {
        signal,
      });
      this.productRoot.addEventListener('todaymerch:product-design-clear-logo', this.bound.onGalleryClearLogo, {
        signal,
      });
      this.productRoot.addEventListener('todaymerch:product-design-update-logo', this.bound.onGalleryUpdateLogo, {
        signal,
      });
      this.productRoot.addEventListener(
        'todaymerch:product-design-transform-change',
        this.bound.onGalleryDesignTransformChange,
        { signal },
      );
      if (this.productRoot !== document) {
        document.addEventListener('todaymerch:product-design-clear-logo', this.bound.onGalleryClearLogo, { signal });
        document.addEventListener('todaymerch:product-design-update-logo', this.bound.onGalleryUpdateLogo, { signal });
      }
      this.productRoot?.addEventListener?.('input', this.bound.onVariantInputChange, { signal, capture: true });
      this.productRoot?.addEventListener?.('change', this.bound.onVariantInputChange, { signal, capture: true });
      document.addEventListener('variant:changed', this.bound.onThemeVariantChange, { signal });
      document.addEventListener('submit', this.bound.onProductFormSubmit, { signal, capture: true });
      this.variantChangeUnsubscriber = window.FoxTheme?.pubsub?.subscribe?.(
        window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange,
        this.bound.onThemeVariantChange,
      );
    }

    getProductFormId() {
      return String(this.fileInputs?.[0]?.getAttribute('form') || '').trim();
    }

    getProductForm() {
      const formId = this.getProductFormId();
      if (!formId) return null;
      const associatedForm = this.fileInputs?.[0]?.form || null;
      if (associatedForm) return associatedForm;
      if (typeof Document !== 'undefined' && Document.prototype.getElementById) {
        return Document.prototype.getElementById.call(document, formId) || null;
      }
      return document.getElementById?.(formId) || null;
    }

    isOwnProductForm(form) {
      const formId = this.getProductFormId();
      if (!form || !formId) return false;

      const associatedForm = this.getProductForm();
      if (associatedForm && form === associatedForm) return true;
      return String(form.getAttribute?.('id') || '').trim() === formId;
    }

    getQuoteSafeZoneLabel(value) {
      return String(value || '')
        .replace(/[\[\]\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 90) || 'Zone';
    }

    getQuoteDesignPropertyName(index) {
      const designIndex = Math.max(1, Number.parseInt(index, 10) || 1);
      return `properties[_tm_branding_design_${designIndex}]`;
    }

    getQuoteMockupPropertyName(index) {
      const mockupIndex = Math.max(1, Number.parseInt(index, 10) || 1);
      return `properties[_tm_branding_mockup_${mockupIndex}]`;
    }

    getQuoteLogoZones() {
      return (this.optionWraps || []).map((wrap) => {
        const markingInput = wrap.querySelector('[data-role="marking-input"]');
        const files = this.getLogoFiles(wrap);
        if (!markingInput?.checked || !files.length) return null;

        return {
          key: String(wrap.dataset.zoneKey || '').trim(),
          label: this.getQuoteSafeZoneLabel(wrap.dataset.zoneLabel || wrap.dataset.zoneKey),
          viewKey: String(wrap.dataset.zoneView || '').trim(),
          files,
          sourceInput: wrap.querySelector('[data-role="logo-file-input"]'),
        };
      }).filter(Boolean);
    }

    restoreQuoteSourceInputNames() {
      (this.fileInputs || []).forEach((input) => {
        const originalName = String(input.dataset.quoteOriginalName || '').trim();
        if (originalName) input.name = originalName;
      });
    }

    clearGeneratedQuoteAssetControls({ restoreSourceNames = false } = {}) {
      (this.generatedQuoteAssetControls || []).forEach((control) => control?.remove?.());
      this.generatedQuoteAssetControls = [];
      if (restoreSourceNames) this.restoreQuoteSourceInputNames();
    }

    createQuoteFileControl(name, file) {
      const formId = this.getProductFormId();
      if (!formId || !file || typeof DataTransfer === 'undefined') return null;

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        const input = document.createElement('input');
        input.type = 'file';
        input.name = name;
        input.setAttribute('form', formId);
        input.setAttribute('aria-hidden', 'true');
        input.className = 'visually-hidden';
        input.dataset.quoteAssetControl = 'true';
        input.files = transfer.files;
        this.append(input);
        this.generatedQuoteAssetControls.push(input);
        return input;
      } catch (error) {
        return null;
      }
    }

    createQuoteStatusControl(status) {
      const formId = this.getProductFormId();
      if (!formId) return null;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_tm_mockup_status]';
      input.value = JSON.stringify(status);
      input.setAttribute('form', formId);
      input.dataset.quoteAssetControl = 'true';
      this.append(input);
      this.generatedQuoteAssetControls.push(input);
      return input;
    }

    createQuotePreviewManifestControl(manifest) {
      const formId = this.getProductFormId();
      if (!formId || !manifest || !Array.isArray(manifest.views) || !manifest.views.length) return null;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_tm_studio_preview_manifest]';
      input.value = JSON.stringify(manifest);
      input.setAttribute('form', formId);
      input.dataset.quoteAssetControl = 'true';
      this.append(input);
      this.generatedQuoteAssetControls.push(input);
      return input;
    }

    createQuoteCompanionControl(assetName, suffix, value) {
      const formId = this.getProductFormId();
      const stableValue = String(value || '').trim();
      const propertyMatch = String(assetName || '').match(/^properties\[([^\]]+)\]$/u);
      if (!formId || !stableValue || !propertyMatch) return null;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[_tm_${propertyMatch[1].replace(/^_tm_/u, '')}_${suffix}]`;
      input.value = stableValue;
      input.setAttribute('form', formId);
      input.dataset.quoteAssetControl = 'true';
      this.append(input);
      this.generatedQuoteAssetControls.push(input);
      return input;
    }

    getQuoteArtworkSources(zone) {
      let gallery;
      try { gallery = this.getMediaGallery?.(); } catch (_) { /* Detached source owners have no gallery. */ }
      const nativeZone = gallery?.designStudioState?.zones?.find(value => value.key === zone.key) || zone;
      const artworks = gallery?.getDesignArtworkItems?.(nativeZone) || nativeZone.artworks || [];
      if (!artworks.length) return (zone.files || []).map((file, index) => ({ file, fileIndex: index, artworkId: index ? `uploaded-${index}` : 'base', sourceArtworkId: index ? `uploaded-${index}` : 'base' }));
      return artworks.map(artwork => {
        const copy = gallery?.getDesignArtworkCopy?.(zone.key, artwork.id);
        const sourceArtworkId = copy?.sourceUploadId || artwork.sourceUploadId || artwork.id || 'base';
        const uploaded = (nativeZone.artworks || []).find(value => value.id === sourceArtworkId);
        const indexes = (zone.files || []).map((file, index) => this.getLogoFileKey(file) === artwork.fileKey ? index : -1).filter(index => index >= 0);
        const fileIndex = Number.isSafeInteger(uploaded?.index) ? uploaded.index : sourceArtworkId === 'base' ? 0 : indexes.length === 1 ? indexes[0] : -1;
        return { file: zone.files?.[fileIndex], fileIndex, artworkId: artwork.id || 'base', sourceArtworkId };
      });
    }

    syncQuoteOriginalDesignControls(zones) {
      this.quoteProductionSourceUnavailable = false;
      let designIndex = 0;
      zones.forEach((zone) => {
        const sourceInput = zone.sourceInput;
        if (!sourceInput) return;
        const generatedStartIndex = this.generatedQuoteAssetControls.length;
        const sourcePropertyName = this.getQuoteDesignPropertyName(designIndex + 1);
        sourceInput.dataset.quoteOriginalName = sourcePropertyName;
        sourceInput.name = sourcePropertyName;

        const artworkSources = this.getQuoteArtworkSources(zone);
        const createdControls = artworkSources.map(({ file, fileIndex, artworkId, sourceArtworkId }) => {
          if (!file) return null;
          designIndex += 1;
          const assetName = this.getQuoteDesignPropertyName(designIndex);
          const production = this.getArtworkProductionSource(file, zone.key, { artworkId });
          const control = this.createQuoteFileControl(assetName, production.file);
          if (control) {
            this.createQuoteCompanionControl(assetName, 'zone_id', zone.key);
            this.createQuoteCompanionControl(assetName, 'view_id', zone.viewKey);
            this.createQuoteCompanionControl(assetName, 'artwork_id', artworkId);
            this.createQuoteCompanionControl(assetName, 'source_artwork_id', sourceArtworkId);
            this.createQuoteCompanionControl(assetName, 'file_index', String(fileIndex));
            if (production.file !== file) {
              const sourceName = `properties[_tm_branding_source_${designIndex}]`;
              const sourceControl = this.createQuoteFileControl(sourceName, file);
              if (!sourceControl) return null;
              this.createQuoteCompanionControl(sourceName, 'zone_id', zone.key);
              this.createQuoteCompanionControl(sourceName, 'view_id', zone.viewKey);
              this.createQuoteCompanionControl(sourceName, 'artwork_id', artworkId);
              this.createQuoteCompanionControl(sourceName, 'source_artwork_id', sourceArtworkId);
              this.createQuoteCompanionControl(sourceName, 'file_index', String(fileIndex));
            }
          }
          return control;
        });
        const allCreated = createdControls.length === artworkSources.length && createdControls.every(Boolean);

        if (allCreated) {
          sourceInput.removeAttribute('name');
          return;
        }

        this.generatedQuoteAssetControls.slice(generatedStartIndex).forEach((control) => {
          control.remove();
          this.generatedQuoteAssetControls = this.generatedQuoteAssetControls.filter((entry) => entry !== control);
        });
        const originalName = String(sourceInput.dataset.quoteOriginalName || '').trim();
        if (originalName) sourceInput.name = originalName;
        if (artworkSources.some(({ file, artworkId }) => !file || this.getArtworkProductionSource(file, zone.key, { artworkId }).file !== file) || artworkSources.length !== zone.files.length) this.quoteProductionSourceUnavailable = true;
      });
    }

    quoteMockupFailureZones(zones, reason) {
      return zones.map((zone) => {
        const fallbackImage = this.getQuoteFallbackImageForZone(zone);
        return {
          key: zone.key,
          status: 'unavailable',
          reason,
          fileName: '',
          size: 0,
          ...(fallbackImage ? { fallbackImage } : {}),
        };
      });
    }

    getQuoteFallbackImageForZone(zone) {
      const gallery = this.getMediaGallery();
      const slide = gallery?.getMediaSlideForView?.(zone?.viewKey) || gallery?.getActiveMediaSlide?.();
      const container = slide?.querySelector?.('.product__media-item-container') || slide;
      const image = typeof gallery?.getDesignPreviewProductImage === 'function'
        ? gallery.getDesignPreviewProductImage(container)
        : container?.querySelector?.('img');
      return String(image?.currentSrc || image?.src || '').trim();
    }

    getQuoteActivePreviewZoneKey(zones) {
      const candidates = [
        this.activeZoneKey,
        this.activeZoneWrap?.dataset?.zoneKey,
        this.getMediaGallery()?.designStudioState?.activeZoneKey,
      ].map((value) => String(value || '').trim()).filter(Boolean);
      const active = zones.find((zone) => candidates.includes(String(zone.key || '').trim()));
      return String(active?.key || zones[0]?.key || '').trim();
    }

    withQuoteAssetTimeout(promise, timeoutMs = QUOTE_MOCKUP_PREPARE_TIMEOUT_MS) {
      return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('quote_mockup_timeout')), timeoutMs);
        Promise.resolve(promise).then(
          (value) => {
            window.clearTimeout(timer);
            resolve(value);
          },
          (error) => {
            window.clearTimeout(timer);
            reject(error);
          },
        );
      });
    }

    initializeQuotePreparation() {
      this.quoteMockupCache = new Map();
      this.quotePreparationFileIds = new WeakMap();
      this.quotePreparationFileSequence = 0;
      this.quotePreparationDisposed = false;
      const root = this.closest?.('product-info') || this.productRoot;
      const schedule = () => this.scheduleQuotePreparation();
      for (const name of ['input', 'change', 'pointerup', 'pointercancel', 'supplier-pricing:change', 'quantity-pricing:change',
        'todaymerch:marking-state-change', 'todaymerch:personalization-selection-change', 'todaymerch:artwork-compatibility-change',
        'todaymerch:artwork-production-source-change', 'todaymerch:product-design-transform-change',
        'todaymerch:product-media-view-selected', 'todaymerch:product-design-zone-selected', 'todaymerch:variant-media-updated',
        'todaymerch:pad-acceptance-change', 'todaymerch:production-priority-change', 'todaymerch:artwork-quote-change']) {
        root?.addEventListener(name, schedule, { signal: this.abortController?.signal });
      }
      window.addEventListener('resize', schedule, { signal: this.abortController?.signal });
      this.scheduleQuotePreparation();
    }

    disposeQuotePreparation() {
      this.quotePreparationDisposed = true;
      window.clearTimeout(this.quotePreparationTimer);
      this.quotePreparationWait?.abort();
      this.quotePreparationTask = null;
      this.quotePreparationKey = null;
      this.quoteMockupCache?.clear();
    }

    scheduleQuotePreparation() {
      if (this.quotePreparationDisposed || this.quoteAssetsPreparing) return;
      const root = this.closest?.('product-info') || this.productRoot;
      // Readiness belongs to the current selection, including during the debounce.
      if (root?.dataset) root.dataset.quotePreparation = 'pending';
      window.clearTimeout(this.quotePreparationTimer);
      this.quotePreparationTimer = window.setTimeout(() => this.warmQuotePreparation(), 450);
    }

    quotePreparationFileId(file) {
      if (!file || typeof file !== 'object') return '';
      this.quotePreparationFileIds ||= new WeakMap();
      this.quotePreparationFileSequence ||= 0;
      if (!this.quotePreparationFileIds.has(file)) this.quotePreparationFileIds.set(file, ++this.quotePreparationFileSequence);
      return this.quotePreparationFileIds.get(file);
    }

    getQuoteMockupKey(zone) {
      const gallery = this.getMediaGallery();
      return JSON.stringify({ variant: this.getVariantInput()?.value || this.activeVariantId,
        zone: [zone.key, zone.viewKey, zone.label],
        sources: this.getQuoteArtworkSources(zone).map(({ file, artworkId }) => {
          const source = this.getArtworkProductionSource(file, zone.key, { artworkId });
          return [artworkId, this.quotePreparationFileId(file), this.quotePreparationFileId(source.file),
            this.quotePreparationFileId(this.getStudioPreviewSource?.(file, zone.key, { artworkId })), source.productionFileKey, source.revision];
        }), render: gallery?.getQuoteMockupRenderKey?.(zone) });
    }

    getQuotePricingState() {
      const root = this.closest?.('product-info') || this.productRoot;
      const pricing = root?.querySelector('product-quantity-pricing-selector');
      const quantity = Number(root?.querySelector('input[name="quantity"]')?.value);
      const value = pricing?.getSupplierPricingState?.(quantity) || pricing?.state;
      const validUntil = pricing?.supplierPricing?.validUntil || value?.validUntil || value?.expiresAt;
      const ready = value?.supplierStatus === 'priced' && Number(value.quantity) === quantity
        && (!validUntil || Date.parse(validUntil) > Date.now());
      return { value, validUntil, ready };
    }

    getQuotePreparationKey(zones) {
      const root = this.closest?.('product-info') || this.productRoot;
      const { value: price, validUntil, ready } = this.getQuotePricingState();
      return JSON.stringify([this.getQuoteSubmissionIntent(zones).key,
        window.TodayMerchProductDesignAdmission?.read(root)?.key,
        zones.map(zone => [this.getQuoteMockupKey(zone), this.getMediaGallery()?.canPrepareQuoteMockup?.(zone)]),
        price?.supplierStatus, price?.quantity, price?.totalPriceCents, price?.unitPriceCents,
        price?.technicalFeesCents, price?.preparationFeesCents, validUntil, ready]);
    }

    async warmQuotePreparation() {
      window.clearTimeout(this.quotePreparationTimer);
      const root = this.closest?.('product-info') || this.productRoot;
      const gallery = this.getMediaGallery();
      if (this.quotePreparationDisposed || this.isConnected === false || this.quoteAssetsPreparing
        || document.hidden || gallery?.designInteraction || gallery?.designImagePanInteraction) return;
      if (this.quotePreparationTask) {
        try {
          if (this.getQuotePreparationKey(this.getQuoteLogoZones()) !== this.quotePreparationTask.key) this.quotePreparationRerun = true;
        } catch { /* A later selection event will retry an incomplete scope. */ }
        return this.quotePreparationTask.promise;
      }
      const zones = this.getQuoteLogoZones();
      const admission = window.TodayMerchProductDesignAdmission?.read(root);
      const preparation = window.TodayMerchPersonalizationOrchestrator?.get(root);
      if (typeof preparation?.settleAutomaticPreparation === 'function'
        && window.TodayMerchProductImagePreparation?.backgroundSettlement !== true) {
        if (root?.dataset) root.dataset.quotePreparation = 'idle';
        return;
      }
      if (!zones.length || !admission?.canConfirm || typeof gallery?.canPrepareQuoteMockup !== 'function'
        || typeof gallery?.getQuoteMockupRenderKey !== 'function') {
        if (root?.dataset) root.dataset.quotePreparation = 'idle';
        return;
      }
      let initialKey;
      try { initialKey = this.getQuotePreparationKey(zones); } catch { return; }
      if (initialKey === this.quotePreparationKey) {
        if (root?.dataset) root.dataset.quotePreparation = this.getQuotePricingState().ready && zones.every(zone => {
          const entry = this.quoteMockupCache?.get(zone.key);
          return entry?.result && entry.key === this.getQuoteMockupKey(zone);
        }) ? 'ready' : 'partial';
        return;
      }
      const intent = this.getQuoteSubmissionIntent(zones);
      const wait = this.quotePreparationWait = new AbortController();
      const assertCurrent = () => {
        if (wait.signal.aborted || this.quotePreparationDisposed || this.isConnected === false || !this.isQuoteSubmissionIntentCurrent(intent)) {
          throw Object.assign(new Error('La configuration a changé.'), { code: 'stale' });
        }
      };
      const state = (name, value) => { if (!this.quotePreparationDisposed && root?.dataset) root.dataset[name] = value; };
      state('quotePreparation', 'pending'); state('quotePricePreparation', 'pending'); state('quoteMockupPreparation', 'pending');
      const task = { promise: null, intent, key: initialKey };
      this.quotePreparationTask = task;
      task.promise = Promise.resolve().then(async () => {
        await this.settleQuotePricing({ assertCurrent, signal: wait.signal });
        const settled = await preparation?.settleAutomaticPreparation?.({ background: true, signal: wait.signal });
        assertCurrent();
        if (settled?.status === 'timeout') throw new Error('preparation_pending');
        await this.waitForStudioPreviews(zones);
        assertCurrent();
        await this.settleQuotePricing({ assertCurrent, signal: wait.signal });
        state('quotePricePreparation', this.getQuotePricingState().ready ? 'ready' : 'unavailable');
        await this.prepareQuoteMockups(zones, { background: true, assertCurrent });
        assertCurrent();
        // Preview measurement can update the physical dimensions used by pricing.
        await this.settleQuotePricing({ assertCurrent, signal: wait.signal });
        assertCurrent();
        const ready = zones.every(zone => {
          const entry = this.quoteMockupCache?.get(zone.key);
          return entry?.result && entry.key === this.getQuoteMockupKey(zone);
        });
        state('quoteMockupPreparation', ready ? 'ready' : 'partial');
        state('quotePricePreparation', this.getQuotePricingState().ready ? 'ready' : 'unavailable');
        state('quotePreparation', ready && this.getQuotePricingState().ready ? 'ready' : 'partial');
        this.quotePreparationKey = this.getQuotePreparationKey(zones);
      }).catch(() => {
        state('quotePreparation', 'failed'); state('quoteMockupPreparation', 'idle');
      }).finally(() => {
        if (this.quotePreparationTask === task) this.quotePreparationTask = null;
        if (this.quotePreparationWait === wait) this.quotePreparationWait = null;
        if (this.quotePreparationRerun) {
          this.quotePreparationRerun = false;
          this.scheduleQuotePreparation();
        }
      });
      return task.promise;
    }

    async prepareQuoteMockups(zones, { background = false, assertCurrent } = {}) {
      const gallery = this.getMediaGallery();
      if (!gallery?.createQuoteMockups) throw new Error('gallery_unavailable');
      // Assets can update independently in CDN caches. An older gallery cannot
      // attest render geometry, so retain its uncached foreground export.
      if (typeof gallery.getQuoteMockupRenderKey !== 'function' || typeof gallery.canPrepareQuoteMockup !== 'function') {
        return background ? [] : this.withQuoteAssetTimeout(gallery.createQuoteMockups(zones));
      }
      this.quoteMockupCache ||= new Map();
      const results = [];
      for (const zone of zones) {
        assertCurrent?.();
        const key = this.getQuoteMockupKey(zone);
        let entry = this.quoteMockupCache.get(zone.key);
        if (entry?.key !== key) entry = null;
        if (!entry?.result && !entry?.promise) {
          if (background && !gallery.canPrepareQuoteMockup?.(zone)) continue;
          entry = { key, result: null, promise: null };
          this.quoteMockupCache.set(zone.key, entry);
          if (this.quoteMockupCache.size > 8) this.quoteMockupCache.delete(this.quoteMockupCache.keys().next().value);
          const current = () => {
            assertCurrent?.();
            if (this.quotePreparationDisposed || this.isConnected === false || this.getQuoteMockupKey(zone) !== key
              || background && (!gallery.canPrepareQuoteMockup?.(zone) || gallery.designInteraction || gallery.designImagePanInteraction)) {
              throw Object.assign(new Error('La configuration a changé.'), { code: 'stale' });
            }
          };
          const root = this.closest?.('product-info') || this.productRoot;
          if (root?.dataset) root.dataset.quoteMockupGenerations = String(Number(root.dataset.quoteMockupGenerations || 0) + 1);
          entry.promise = this.withQuoteAssetTimeout(gallery.createQuoteMockups([zone], { background, assertCurrent: current }))
            .then(values => {
              current();
              const value = values?.find(result => result.key === zone.key);
              if (value?.status === 'generated' && value.file) entry.result = value;
              return value;
            }).finally(() => {
              entry.promise = null;
              if (!entry.result && this.quoteMockupCache.get(zone.key) === entry) this.quoteMockupCache.delete(zone.key);
            });
        }
        const value = entry.result || await entry.promise;
        assertCurrent?.();
        if (value) results.push(value);
      }
      return results;
    }

    async prepareQuoteAssetControls() {
      this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
      const zones = this.getQuoteLogoZones();
      if (!zones.length) return;

      this.syncQuoteOriginalDesignControls(zones);
      const previewReadyKeys = await this.waitForStudioPreviews(zones);
      const previewReadyZones = zones.filter((zone) => previewReadyKeys.has(zone.key));
      const gallery = this.getMediaGallery();
      let results = [];

      try {
        if (!gallery?.createQuoteMockups) throw new Error('gallery_unavailable');
        results = previewReadyZones.length
          ? await this.prepareQuoteMockups(previewReadyZones)
          : [];
      } catch (error) {
        const reason = error?.message === 'quote_mockup_timeout' ? 'timeout' : 'export_failed';
        results = this.quoteMockupFailureZones(zones, reason);
      }

      results = Array.isArray(results) ? results : [];
      results.push(...this.quoteMockupFailureZones(
        zones.filter((zone) => !previewReadyKeys.has(zone.key)),
        'artwork_preview_unavailable',
      ));

      const resultsByKey = new Map((Array.isArray(results) ? results : []).map((result) => [result.key, result]));
      const statusZones = zones.map((zone, index) => {
        const result = resultsByKey.get(zone.key);
        const fallbackImage = String(result?.fallbackImage || '').trim();
        if (!result?.file || result.status !== 'generated') {
          return {
            key: zone.key,
            status: 'unavailable',
            reason: result?.reason || 'export_failed',
            fileName: '',
            size: 0,
            ...(fallbackImage ? { fallbackImage } : {}),
          };
        }

        const assetName = this.getQuoteMockupPropertyName(index + 1);
        const control = this.createQuoteFileControl(assetName, result.file);
        if (!control) {
          return {
            key: zone.key,
            status: 'unavailable',
            reason: 'upload_prepare_failed',
            fileName: '',
            size: 0,
            ...(fallbackImage ? { fallbackImage } : {}),
          };
        }

        this.createQuoteCompanionControl(assetName, 'zone_id', zone.key);
        this.createQuoteCompanionControl(assetName, 'view_id', zone.viewKey);

        return {
          key: zone.key,
          status: 'generated',
          reason: '',
          fileName: result.file.name || '',
          size: Number(result.file.size || 0),
          fallbackImage,
          ...(result.previewAnnotations ? { previewAnnotations: result.previewAnnotations } : {}),
        };
      });

      this.createQuoteStatusControl({ version: 2, zones: statusZones });
      this.createQuotePreviewManifestControl({
        version: 2,
        activeViewId: this.getQuoteActivePreviewZoneKey(zones),
        views: zones.map((zone, index) => {
          const status = statusZones[index] || {};
          return {
            viewId: String(zone.key || '').trim(),
            zoneId: String(zone.key || '').trim(),
            label: String(zone.label || zone.key || '').trim(),
            sourceViewId: String(zone.viewKey || '').trim(),
            fallbackImage: String(status.fallbackImage || '').trim(),
            ...(status.status === 'generated' ? { previewProperty: `_tm_branding_mockup_${index + 1}`, ...(status.previewAnnotations ? { previewAnnotations: status.previewAnnotations } : {}) } : {}),
          };
        }).filter((view) => view.viewId && (view.previewProperty || view.fallbackImage)),
      });
    }

    getQuoteConfigurationKey(zones = this.getQuoteLogoZones()) {
      return JSON.stringify({
        variant: this.getVariantInput()?.value || this.activeVariantId || this.dataset?.activeVariantId || '',
        zones: zones.map((zone) => ({
          key: zone.key,
          viewKey: zone.viewKey,
          files: this.getQuoteArtworkSources(zone).map(({ file, artworkId }) => [
            artworkId, this.getLogoFileKey(file),
            this.getLogoFileKey(this.getStudioPreviewSource(file, zone.key, { artworkId })),
            this.getArtworkProductionSource(file, zone.key, { artworkId }).productionFileKey,
            this.getArtworkProductionSource(file, zone.key, { artworkId }).revision,
          ]),
        })),
      });
    }

    getQuoteSubmissionIntent(zones = this.getQuoteLogoZones()) {
      const root = this.closest?.('product-info') || this.productRoot;
      const orchestration = window.TodayMerchPersonalizationOrchestrator?.get(root);
      const current = orchestration?.readOwner?.();
      const gallery = this.getMediaGallery?.();
      const productId = String(root?.dataset?.productId || '');
      const variantId = String(this.getVariantInput()?.value || this.activeVariantId || this.dataset?.activeVariantId || '');
      // The count is derived from analysis and may become known while waiting.
      // Actual colour edits change the effective File and still require proof.
      const stripDetectedColors = value => value && typeof value === 'object'
        ? Array.isArray(value) ? value.map(stripDetectedColors)
          : Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'colors').map(([key, item]) => [key, stripDetectedColors(item)]))
        : value;
      const geometryKeys = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft',
        'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop', 'previewMode', 'coordinatePlane'];
      const sources = zones.flatMap(zone => this.getQuoteArtworkSources(zone).map(({ file, artworkId }) => {
        const item = current?.uiPlacements?.find(value => value.zoneKey === zone.key && (value.artworkId || 'base') === artworkId);
        const nativeZone = gallery?.designStudioState?.zones?.find(value => value.key === zone.key) || zone;
        const transform = gallery?.getDesignArtworkPlacement?.(nativeZone, artworkId) || item?.renderTransform;
        const source = this.getArtworkProductionSource(file, zone.key, { artworkId });
        return { zoneKey: zone.key, viewKey: zone.viewKey, artworkId, original: file, effective: source.file,
          techniqueId: item?.techniqueId || source.techniqueId || '',
          transform: transform ? Object.fromEntries(geometryKeys.filter(key => transform[key] !== undefined).map(key => [key, transform[key]])) : null };
      }));
      return { sources, productId, variantId, key: JSON.stringify({ productId, variantId,
        quantity: root?.querySelector?.('input[name="quantity"]')?.value || '',
        priority: current?.productionPriority || root?.querySelector?.('[data-production-priority]:checked')?.value || '',
        configurationRevision: current?.snapshot?.configurationRevision || '',
        selection: stripDetectedColors(current?.studio?.personalization || null),
        sources: sources.map(({ original, effective, ...scope }) => scope) }) };
    }

    isQuoteSubmissionIntentCurrent(before) {
      const after = this.getQuoteSubmissionIntent();
      if (before.key !== after.key || before.sources.length !== after.sources.length) return false;
      return before.sources.every((source, index) => {
        const next = after.sources[index];
        if (source.original !== next.original) return false;
        if (source.effective === next.effective) return true;
        // Only a receipt issued by the local engine may cross the waiting
        // boundary. A manual colour/file edit never receives this exception.
        const visited = new Set();
        let file = next.effective;
        let applied = window.TodayMerchArtworkImagePreparation?.getAppliedPreparation?.(file);
        // Several automatic applications can finish during the wait. Every
        // link must attest the same scope and reach the exact starting File.
        while (file !== source.effective && visited.size < 32) {
          if (!(file instanceof File) || !applied || visited.has(applied)) return false;
          visited.add(applied);
          const context = applied?.appliedContext;
          if (applied?.originalFile !== source.original || applied.effectiveFile !== file
            || applied.receipt?.application?.mode !== 'automatic_compatible'
            || String(context?.productId) !== after.productId || String(context?.variantId) !== after.variantId
            || context?.zoneKey !== next.zoneKey || (context.artworkId || 'base') !== next.artworkId
            || context.techniqueId !== next.techniqueId) return false;
          file = applied.baseFile;
          if (file !== source.effective && !applied.baseReceipt) return false;
          applied = file === source.effective ? null
            : window.TodayMerchArtworkImagePreparation?.getAppliedPreparation?.(file, applied.baseReceipt);
        }
        return file === source.effective;
      });
    }

    blockUnacceptedPadPreparation(event) {
      const root = this.closest?.('product-info') || this.productRoot;
      const preparation = window.TodayMerchPersonalizationOrchestrator?.get(root);
      const gate = window.TodayMerchPadPreparationGate?.read(root);
      if (gate ? !gate.required || gate.canConfirm : preparation?.canConfirm?.() !== false) return false;
      event?.preventDefault();
      event?.stopImmediatePropagation?.();
      if (gate?.focus) gate.focus(); else preparation?.focusPadPreparation?.();
      return true;
    }

    blockMissingProjectDesign(event, expectedKey) {
      const root = this.closest?.('product-info') || this.productRoot;
      try {
        window.TodayMerchProductDesignAdmission?.assert(root, expectedKey ? { key: expectedKey } : {});
        return false;
      } catch (error) {
        event?.preventDefault(); event?.stopImmediatePropagation?.();
        this.quoteProductionSourceError?.remove();
        this.quoteProductionSourceError = document.createElement('p');
        this.quoteProductionSourceError.setAttribute('role', 'alert');
        this.quoteProductionSourceError.textContent = error.message;
        this.append(this.quoteProductionSourceError);
        window.TodayMerchProductDesignAdmission?.read(root)?.focus?.();
        return true;
      }
    }

    async settleQuotePricing({ assertCurrent, onPending, timeoutMs = 20000, signal } = {}) {
      const root = this.closest?.('product-info') || this.productRoot;
      const scope = () => JSON.stringify([root?.querySelector('form input[name="id"], input[name="id"]')?.value,
        root?.querySelector('input[name="quantity"]')?.value, root?.querySelector('[data-production-priority]:checked')?.value]);
      const initialScope = scope();
      const pricing = root?.querySelector('product-quantity-pricing-selector'), choice = root?.querySelector('product-personalization-choice');
      // Use the existing supplier quote owner. A pending calculation is not an
      // unknown price and must settle before the native FormData is captured.
      await new Promise((resolve, reject) => {
        let finished = false, timer;
        const events = ['supplier-pricing:change', 'quantity-pricing:change'];
        const finish = error => {
          if (finished) return; finished = true; window.clearTimeout(timer);
          for (const event of events) root?.removeEventListener(event, changed);
          signal?.removeEventListener('abort', aborted);
          error ? reject(error) : resolve();
        };
        const check = () => {
          if (finished) return;
          try {
            if (root?.isConnected === false || scope() !== initialScope) throw Object.assign(new Error('La configuration a changé. Vérifiez votre design avant de poursuivre.'), { code: 'stale' });
            assertCurrent?.();
            const quantity = Number(root?.querySelector('input[name="quantity"]')?.value);
            const state = pricing?.getSupplierPricingState?.(quantity) || pricing?.state;
            const status = state?.supplierStatus || choice?.projectedPricing?.status;
            const pending = status === 'pending' || status === 'priced' && state?.quantity != null && Number(state.quantity) !== quantity;
            if (!pending) return finish();
            onPending?.();
          } catch (error) { finish(error); }
        };
        const changed = () => window.queueMicrotask(check);
        const aborted = () => finish(new DOMException('La préparation a été annulée.', 'AbortError'));
        if (signal?.aborted) { aborted(); return; }
        signal?.addEventListener('abort', aborted, { once: true });
        for (const event of events) root?.addEventListener(event, changed);
        timer = window.setTimeout(() => finish(Object.assign(new Error('Le prix est encore en cours de calcul. Réessayez dans un instant ; votre panier reste inchangé.'), { code: 'pricing_pending' })),
          Math.max(1, Math.min(20000, Number(timeoutMs) || 20000)));
        try { choice?.refresh?.(false); check(); } catch (error) { finish(error); }
      });
    }

    onProductFormSubmit(event) {
      const form = event.target;
      if (!this.isOwnProductForm(form)) return;
      const requestRoot = this.closest?.('product-info') || this.productRoot;
      if (requestRoot?.dataset?.brandApprovalRequest === 'rains') {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        window.TodayMerchProductBrandRequest?.open(requestRoot, event.submitter);
        return;
      }
      // Enter can submit a focused form while a pointer still holds a design.
      // Commit its last queued position before gates, properties and mockups.
      this.getMediaGallery()?.cancelDesignArtworkInteraction?.();
      if (this.blockMissingProjectDesign(event) || this.blockUnacceptedPadPreparation(event) || this.quoteAssetsResubmitting) return;
      if (this.quoteAssetsPreparing) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
        return;
      }

      const zones = this.getQuoteLogoZones();
      if (!zones.length) {
        this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation?.();

      this.quoteAssetsPreparing = true;
      this.quoteProductionSourceError?.remove();
      const submitter = event.submitter;
      // This capture handler runs before ProductForm and can await supplier
      // pricing. Reuse its pending drawer from the first, validated submit.
      form.beginCartAdditionPreview?.(submitter);
      let configurationKey = this.getQuoteConfigurationKey(zones);
      const admissionRoot = this.closest?.('product-info') || this.productRoot;
      let admissionKey = window.TodayMerchProductDesignAdmission?.read(admissionRoot)?.key;
      const preparation = window.TodayMerchPersonalizationOrchestrator?.get(admissionRoot);
      const submissionIntent = preparation?.settleAutomaticPreparation ? this.getQuoteSubmissionIntent(zones) : null;
      let submissionError = null;
      const configurationRoot = this.productRoot !== document ? this.productRoot : null;
      const previousInert = configurationRoot?.inert;
      const previousDisabled = submitter?.disabled;
      const previousBusy = submitter?.getAttribute?.('aria-busy');
      if (configurationRoot) configurationRoot.inert = true;
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute?.('aria-busy', 'true');
      }
      Promise.resolve()
        .then(async () => {
          const warming = this.quotePreparationTask;
          if (warming && this.isQuoteSubmissionIntentCurrent(warming.intent)) await warming.promise;
          // Offer resolution can enable automatic artwork preparation. Resolve
          // it first, then settle the resulting File before exporting controls.
          await this.settleQuotePricing({ assertCurrent: () => {
            if (submissionIntent && !this.isQuoteSubmissionIntentCurrent(submissionIntent)) throw Object.assign(new Error('La configuration a changé. Vérifiez votre design avant de poursuivre.'), { code: 'stale' });
          } });
          if (submissionIntent) {
            await preparation.settleAutomaticPreparation();
            // Preview decoding updates its status and URL. Settle that exact
            // File before freezing the admission key used during export.
            await this.waitForStudioPreviews(zones);
            if (form.isConnected === false || configurationRoot?.isConnected === false || !this.isQuoteSubmissionIntentCurrent(submissionIntent)) {
              throw Object.assign(new Error('La configuration a changé. Vérifiez votre design avant de poursuivre.'), { code: 'stale' });
            }
            // Internal preparation is now settled; capture the exact effective
            // file before creating controls, mockups and the native FormData.
            configurationKey = this.getQuoteConfigurationKey();
            admissionKey = window.TodayMerchProductDesignAdmission?.assert?.(admissionRoot)?.key;
          }
          return this.prepareQuoteAssetControls();
        })
        .catch(error => {
          if (error?.code === 'stale' || error?.code === 'pricing_pending' || error?.name === 'AbortError') { submissionError = error; return; }
          this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
          this.syncQuoteOriginalDesignControls(zones);
          this.createQuoteStatusControl({
            version: 2,
            zones: this.quoteMockupFailureZones(zones, 'export_failed'),
          });
        })
        .then(async () => {
          if (submissionError) return;
          try {
            await this.settleQuotePricing({ assertCurrent: () => {
              if (submissionIntent && !this.isQuoteSubmissionIntentCurrent(submissionIntent)) throw Object.assign(new Error('La configuration a changé. Vérifiez votre design avant de poursuivre.'), { code: 'stale' });
            } });
          } catch (error) { submissionError = error; }
        })
        // A file without a renderable preview can finish entirely in microtasks.
        // Let the initial native submit event finish before requestSubmit, which
        // browsers otherwise ignore as a recursive submission. Keep the controls
        // locked until then and recheck the configuration in the next task.
        .then(() => new Promise((resolve) => window.setTimeout(resolve, 0)))
        .finally(() => {
          this.quoteAssetsPreparing = false;
          if (configurationRoot) configurationRoot.inert = previousInert;
          if (submitter) {
            submitter.disabled = previousDisabled;
            if (previousBusy == null) submitter.removeAttribute?.('aria-busy');
            else submitter.setAttribute?.('aria-busy', previousBusy);
          }
          if (form.isConnected === false || configurationRoot?.isConnected === false) {
            form.failCartAdditionPreview?.(document.documentElement.lang.startsWith('en')
              ? 'The configuration has changed. Please check your design before continuing.'
              : 'La configuration a changé. Vérifiez votre design avant de poursuivre.');
            return;
          }
          if (submissionError) {
            this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
            this.quoteProductionSourceError = document.createElement('p');
            this.quoteProductionSourceError.setAttribute('role', 'alert');
            this.quoteProductionSourceError.textContent = submissionError.message;
            this.append(this.quoteProductionSourceError);
            form.failCartAdditionPreview?.(submissionError.message);
            return;
          }
          if (this.blockMissingProjectDesign(null, admissionKey) || this.blockUnacceptedPadPreparation()) {
            this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
            form.failCartAdditionPreview?.(this.quoteProductionSourceError?.textContent
              || (document.documentElement.lang.startsWith('en')
                ? 'Please check your design before continuing.' : 'Vérifiez votre design avant de poursuivre.'));
            return;
          }
          // Never fall back to submitting the original when the approved
          // adaptation could not be attached to the form.
          if (this.quoteProductionSourceUnavailable) {
            this.quoteProductionSourceError = document.createElement('p');
            this.quoteProductionSourceError.setAttribute('role', 'alert');
            this.quoteProductionSourceError.textContent = document.documentElement.lang.startsWith('en')
              ? 'The selected production file could not be attached. Please try again before continuing.'
              : 'Le fichier de production choisi n’a pas pu être joint. Réessayez avant de continuer.';
            this.append(this.quoteProductionSourceError);
            form.failCartAdditionPreview?.(this.quoteProductionSourceError.textContent);
            return;
          }
          // Theme events may update the variant even while the controls are inert.
          // Reprepare the latest configuration before allowing the form through.
          const configurationChanged = this.getQuoteConfigurationKey() !== configurationKey;
          if (configurationChanged) this.clearGeneratedQuoteAssetControls({ restoreSourceNames: true });
          this.quoteAssetsResubmitting = !configurationChanged;
          const resubmitMessage = document.documentElement.lang.startsWith('en')
            ? 'Check the product details before continuing.' : 'Vérifiez les informations du produit avant de poursuivre.';
          try {
            if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
              form.failCartAdditionPreview?.(resubmitMessage);
              form.reportValidity?.();
              return;
            }
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          } catch {
            form.failCartAdditionPreview?.(resubmitMessage);
          } finally {
            if (!configurationChanged) window.setTimeout(() => {
              this.quoteAssetsResubmitting = false;
              if (form.cartAdditionPendingToken && !form.cartAdditionInFlight && !this.quoteAssetsPreparing)
                form.failCartAdditionPreview?.(resubmitMessage);
            }, 0);
          }
        });
    }

    initializeDrawer() {
      if (!this.trigger || !this.panel) return;

      this.drawer?.destroy();
      this.drawer = window.TodayMerchProductOptionDrawer?.create({
        host: this,
        trigger: this.trigger,
        panel: this.panel,
        prefersReducedMotion: this.prefersReducedMotion,
        staticOpen: this.staticOpen,
      });

      this.drawer?.init({ startsOpen: this.staticOpen });
    }

    get allowMultiple() {
      return this.dataset.allowMultiple === 'true';
    }

    getInitialVariantId() {
      return this.getCurrentVariantId();
    }

    getUrlVariantId() {
      try {
        return String(new URLSearchParams(window.location.search).get('variant') || '').trim();
      } catch (error) {
        return '';
      }
    }

    getVariantInput() {
      const input = this.productRoot?.querySelector?.('input[name="id"]') || document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (input) this.variantInput = input;
      return input || this.variantInput || null;
    }

    getCurrentVariantId() {
      const variantInput = this.getVariantInput();
      return String(variantInput?.value || this.dataset.activeVariantId || this.activeVariantId || this.getUrlVariantId() || '').trim();
    }

    syncActiveVariantId() {
      const currentVariantId = this.getCurrentVariantId();
      if (!currentVariantId || currentVariantId === this.activeVariantId) return;

      this.applyVariant(currentVariantId);
    }

    getActiveOptionWraps() {
      return this.allOptionWraps || this.optionWraps || [];
    }

    getActiveVariantTokens(variantId = this.activeVariantId) {
      const rawVariantId = String(variantId || '').trim();
      const tokens = new Set();
      if (!rawVariantId) return tokens;

      tokens.add(rawVariantId);

      const numericId = rawVariantId.match(/(\d+)$/)?.[1] || '';
      if (numericId) {
        tokens.add(numericId);
        tokens.add(`gid://shopify/ProductVariant/${numericId}`);
      }

      return tokens;
    }

    getVariantLookupKeys(variantId = this.activeVariantId) {
      return Array.from(this.getActiveVariantTokens(variantId));
    }

    getWrapVariantIds(wrap, variantId = this.activeVariantId) {
      const config = this.getTodayStudioConfig();
      const placement = Number(config?.schemaVersion) === 2 ? this.getStudioPlacementForWrap(wrap) : null;
      if (placement) {
        const variantMedia = config.variantMediaV2 && typeof config.variantMediaV2 === 'object'
          ? config.variantMediaV2
          : {};
        const placementId = String(placement.id || '');
        const mappedVariantIds = Object.entries(variantMedia)
          .filter(([, assignments]) => (
            assignments &&
            typeof assignments === 'object' &&
            assignments[placementId]
          ))
          .map(([variantId]) => variantId);
        if (mappedVariantIds.length) return this.normalizeIdList(mappedVariantIds);

        const placementViewIds = new Set((Array.isArray(placement.instances) ? placement.instances : [])
          .map((instance) => String(instance?.viewId || ''))
          .filter(Boolean));
        const viewVariantIds = (Array.isArray(config.views) ? config.views : [])
          .filter((view) => placementViewIds.has(String(view?.key || '')))
          .flatMap((view) => Array.isArray(view?.variantIds) ? view.variantIds : []);
        if (viewVariantIds.length) return this.normalizeIdList(viewVariantIds);
      }

      const serializedIds = String(wrap?.dataset.zoneVariantIds || '').trim();
      if (!serializedIds) {
        const configuredView = this.getStudioConfigViewForWrap(wrap, variantId);
        const configuredIds = configuredView?.variantIds || configuredView?.variant_ids || [];
        const viewIds = Array.isArray(configuredIds)
          ? configuredIds.map((id) => String(id || '').trim()).filter(Boolean)
          : [];
        return viewIds.length ? viewIds : this.getVariantIdsForStudioView(configuredView);
      }

      return serializedIds
        .split(/[,\s|]+/)
        .map((id) => id.trim())
        .filter(Boolean);
    }

    wrapAppliesToVariant(wrap, variantId = this.activeVariantId) {
      const variantIds = this.getWrapVariantIds(wrap, variantId);
      if (!variantIds.length) return true;

      const activeTokens = this.getActiveVariantTokens(variantId);
      if (!activeTokens.size) return true;

      return variantIds.some((variantIdValue) => activeTokens.has(variantIdValue));
    }

    getSelectedVariantFromDocument() {
      const script = document.querySelector('[data-selected-variant]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent || 'null');
      } catch (error) {
        return null;
      }
    }

    normalizeSearchText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    getResourceNumericId(value) {
      return String(value || '').match(/(\d+)(?:\D*)$/)?.[1] || '';
    }

    normalizeIdList(value) {
      const entries = Array.isArray(value) ? value : value ? [value] : [];
      const ids = entries
        .map((entry) => String(entry?.id || entry || '').trim())
        .filter(Boolean);
      return Array.from(new Set(ids.flatMap((id) => {
        const numeric = this.getResourceNumericId(id);
        return numeric && numeric !== id ? [id, numeric] : [id];
      })));
    }

    getImageFileKey(url) {
      if (!url) return '';
      try {
        const parsed = new URL(String(url), window.location.origin);
        return decodeURIComponent(parsed.pathname.split('/').pop() || '').toLowerCase();
      } catch (error) {
        return String(url).split('?')[0].split('/').pop().toLowerCase();
      }
    }

    getVariantMediaConfig() {
      const config = this.getTodayStudioConfig();
      const variantMedia = config?.variantMedia || config?.variant_media || {};
      return variantMedia && typeof variantMedia === 'object' ? variantMedia : {};
    }

    studioViewMatchesVariantMediaAssignment(view, assignment) {
      if (!view || !assignment) return false;
      const viewIds = this.normalizeIdList([view.fileId, view.file_id, view.mediaId, view.media_id]);
      const assignmentIds = this.normalizeIdList([
        assignment.mediaId,
        assignment.media_id,
        assignment.fileId,
        assignment.file_id,
      ]);
      if (viewIds.some((id) => assignmentIds.includes(id))) return true;

      const viewImageKey = this.getImageFileKey(view.image || view.previewImage || view.imageUrl || '');
      const assignmentKeys = [
        assignment.image,
        assignment.thumbnail,
        assignment.previewUrl,
        assignment.preview_url,
      ].map((url) => this.getImageFileKey(url)).filter(Boolean);
      return Boolean(viewImageKey && assignmentKeys.includes(viewImageKey));
    }

    getVariantIdsForStudioView(view) {
      if (!view) return [];
      const variants = this.getVariantMediaConfig().variants || {};
      const ids = [];
      Object.entries(variants).forEach(([variantKey, entry]) => {
        if (!entry || typeof entry !== 'object') return;
        const media = [
          ...(Array.isArray(entry.gallery) ? entry.gallery : []),
          ...(entry.media && typeof entry.media === 'object' ? Object.values(entry.media) : []),
        ];
        if (!media.some((assignment) => this.studioViewMatchesVariantMediaAssignment(view, assignment))) return;
        ids.push(entry.variantId || entry.variant_id || variantKey);
      });
      return this.normalizeIdList(ids);
    }

    getMeaningfulSearchTokens(value) {
      const ignoredTokens = new Set([
        'and',
        'avec',
        'cdn',
        'com',
        'de',
        'des',
        'du',
        'files',
        'for',
        'jpg',
        'jpeg',
        'la',
        'le',
        'les',
        'png',
        'pour',
        'shop',
        'shopify',
        'today',
        'merch',
        'webp',
        'with',
      ]);

      return this.normalizeSearchText(value)
        .split(/\s+/)
        .filter((token) => (
          token.length > 1 &&
          !ignoredTokens.has(token) &&
          !/^\d+$/.test(token) &&
          !/^[a-f0-9]{8,}$/.test(token)
        ));
    }

    getActiveVariantSearchTokens() {
      const variant = this.currentVariant || this.getSelectedVariantFromDocument() || {};
      const values = [
        variant.title,
        variant.public_title,
        variant.name,
        variant.sku,
        variant.option1,
        variant.option2,
        variant.option3,
        ...(Array.isArray(variant.options) ? variant.options : []),
        variant.featured_image?.src,
        variant.featured_media?.preview_image?.src,
        variant.featured_media?.preview_image?.alt,
        variant.featured_media?.alt,
        variant.image?.src,
      ];

      return Array.from(new Set(values.flatMap((value) => this.getMeaningfulSearchTokens(value))));
    }

    getStudioConfigViews() {
      const config = this.getTodayStudioConfig();
      return Array.isArray(config?.views) ? config.views : [];
    }

    getStudioConfigViewForKey(viewKey) {
      const normalizedViewKey = this.normalizeStudioKey(viewKey);
      if (!normalizedViewKey) return null;

      return this.getStudioConfigViews().find((view) =>
        this.normalizeStudioKey(view?.key || view?.view || '') === normalizedViewKey
      ) || null;
    }

    getStudioConfigViewForWrap(wrap, variantId = this.getCurrentVariantId()) {
      return this.getStudioConfigViewForKey(this.getZoneViewKey(wrap, variantId));
    }

    getWrapPlacementGroupKey(wrap) {
      if (this.supplierV2) return wrap?.dataset.zoneKey || '';
      const placement = this.getStudioPlacementForWrap(wrap);
      const placementId = this.normalizeStudioKey(placement?.id || '');
      if (placementId) return placementId;

      const configuredView = this.getStudioConfigViewForWrap(wrap);
      const placementKey = this.normalizeStudioKey(configuredView?.placementKey || configuredView?.placement_key || '');
      if (placementKey) return placementKey;

      const label = this.normalizeStudioKey(configuredView?.label || wrap?.dataset.zoneLabel || '');
      if (label) return label;

      return this.normalizeStudioKey(wrap?.dataset.zoneKey || this.getStudioTabViewKey(wrap));
    }

    getStudioTabPlacementKey(wrap) {
      return this.getWrapPlacementGroupKey(wrap) || this.getStudioTabViewKey(wrap) || this.normalizeStudioKey(wrap?.dataset.zoneKey || '');
    }

    getWrapVariantSearchText(wrap) {
      const configuredView = this.getStudioConfigViewForWrap(wrap);
      return this.normalizeSearchText([
        configuredView?.label,
        configuredView?.placementKey,
        configuredView?.placement_key,
        configuredView?.image,
        configuredView?.previewImage,
        configuredView?.imageUrl,
        configuredView?.alt,
        wrap?.dataset.zoneLabel,
        wrap?.dataset.zoneView,
      ].filter(Boolean).join(' '));
    }

    getVariantPreferredWraps(wraps) {
      if (!wraps.length) return wraps;

      const groups = wraps.reduce((map, wrap) => {
        const groupKey = this.getWrapPlacementGroupKey(wrap) || this.getStudioTabViewKey(wrap) || wrap.dataset.zoneKey || '';
        if (!map.has(groupKey)) map.set(groupKey, []);
        map.get(groupKey).push(wrap);
        return map;
      }, new Map());
      const activeTokens = this.getActiveVariantSearchTokens();
      if (!activeTokens.length) return wraps;

      const filteredWraps = [];

      groups.forEach((groupWraps) => {
        if (groupWraps.length < 2) {
          filteredWraps.push(...groupWraps);
          return;
        }

        const viewTexts = groupWraps.map((wrap) => this.getWrapVariantSearchText(wrap));
        const tokenWeights = activeTokens.reduce((weights, token) => {
          const matchCount = viewTexts.filter((text) => text.includes(token)).length;
          if (matchCount > 0 && matchCount < viewTexts.length) weights.set(token, 1 / matchCount);
          return weights;
        }, new Map());

        if (!tokenWeights.size) {
          filteredWraps.push(...groupWraps);
          return;
        }

        const scoredWraps = groupWraps.map((wrap, index) => {
          const text = viewTexts[index];
          const score = Array.from(tokenWeights.entries()).reduce(
            (total, [token, weight]) => total + (text.includes(token) ? weight : 0),
            0,
          );
          return { wrap, score };
        });
        const bestScore = Math.max(...scoredWraps.map((item) => item.score));
        const preferredWraps = bestScore > 0
          ? scoredWraps.filter((item) => item.score === bestScore).map((item) => item.wrap)
          : groupWraps;

        filteredWraps.push(...preferredWraps);
      });

      return wraps.filter((wrap) => filteredWraps.includes(wrap));
    }

    isStudioWrapPlacement(wrap) {
      if (this.supplierV2) return this.supplierV2.offer(wrap?.dataset.zoneKey)?.kind === '360';
      if (!wrap) return false;
      const placement = this.getStudioPlacementForWrap(wrap);
      const candidates = [
        wrap.dataset.zoneKey,
        wrap.dataset.zoneLabel,
        wrap.dataset.zoneView,
        placement?.id,
        placement?.label,
        placement?.placementKey,
        placement?.placement_key,
        ...(Array.isArray(placement?.legacyKeys) ? placement.legacyKeys : []),
      ];
      return candidates.some((value) => this.normalizeStudioPlacementReference(value) === 'wrap');
    }

    sortStudioWrapsForDisplay(wraps = []) {
      return wraps
        .map((wrap, index) => ({ wrap, index, isWrap: this.isStudioWrapPlacement(wrap) }))
        .sort((left, right) => Number(left.isWrap) - Number(right.isWrap) || left.index - right.index)
        .map((item) => item.wrap);
    }

    getStudioExclusivityGroup(wrap) {
      return this.isStudioWrapPlacement(wrap) ? 'wrap' : 'standard';
    }

    getStudioExclusivityBlocker(wrap) {
      if (!wrap) return null;
      const targetGroup = this.getStudioExclusivityGroup(wrap);
      const blockedGroup = targetGroup === 'wrap' ? 'standard' : 'wrap';
      return (this.optionWraps || []).find((candidate) => (
        candidate !== wrap &&
        this.getStudioExclusivityGroup(candidate) === blockedGroup &&
        this.wrapHasLogo(candidate)
      )) || null;
    }

    isStudioDesignBlockedForWrap(wrap) {
      return Boolean(this.getStudioExclusivityBlocker(wrap));
    }

    getStudioExclusivityMessage(wrap, blocker) {
      if (!wrap || !blocker) return '';
      return this.getStudioExclusivityGroup(wrap) === 'wrap'
        ? this.strings.exclusivityOtherBlocks
        : this.strings.exclusivityWrapBlocks;
    }

    updateStudioExclusivityNotice(blocker = this.getStudioExclusivityBlocker(this.activeZoneWrap)) {
      const hasBlocker = Boolean(blocker && this.activeZoneWrap);
      const activeHasLogo = Boolean(this.activeZoneWrap && this.wrapHasLogo(this.activeZoneWrap));
      const showNotice = hasBlocker;
      if (this.studioExclusivityNotice) this.studioExclusivityNotice.hidden = !showNotice;
      if (this.studioExclusivityText) this.studioExclusivityText.textContent = showNotice ? this.getStudioExclusivityMessage(this.activeZoneWrap, blocker) : '';
      if (this.studioExclusivityAction) {
        const zoneLabel = blocker?.dataset?.zoneLabel || blocker?.dataset?.zoneKey || '';
        this.studioExclusivityAction.hidden = !showNotice;
        this.studioExclusivityAction.disabled = !showNotice;
        this.studioExclusivityAction.dataset.zoneKey = showNotice ? blocker.dataset.zoneKey || '' : '';
        this.studioExclusivityAction.textContent = showNotice
          ? this.formatString('exclusivityAction', { ZONE: zoneLabel }) || zoneLabel
          : '';
      }
      if (this.brandEntry) {
        this.brandEntry.toggleAttribute('inert', hasBlocker);
        if (hasBlocker) {
          this.brandEntry.setAttribute('aria-hidden', 'true');
        } else {
          this.brandEntry.removeAttribute('aria-hidden');
        }
      }
      this.classList.toggle('has-studio-exclusivity-block', hasBlocker);
      this.studioUploadZone?.classList.toggle('has-studio-exclusivity-block', hasBlocker);
      return { blocked: hasBlocker, showNotice, blocker };
    }

    getMediaGallery() {
      return this.productRoot?.querySelector?.('media-gallery') || document.querySelector('media-gallery');
    }

    getAvailableGalleryViewKeys() {
      const mediaGallery = this.getMediaGallery();
      const slides = Array.from(mediaGallery?.querySelectorAll?.('.product__media-item') || []);
      const viewKeys = new Set();
      let visibleIndex = 0;

      for (const slide of slides) {
        // Calibrated auxiliary Studio photos are real image slides. They stay
        // outside the blank-product gallery until a technique is selected.
        if (slide.dataset.studioAuxiliaryVerified === 'true') {
          const auxiliaryView = this.normalizeStudioKey(slide.dataset.markingView || '');
          if (auxiliaryView && slide.querySelector('img[src]')) viewKeys.add(auxiliaryView);
        }
        if (slide.hidden || slide.style.display === 'none' || slide.getAttribute('aria-hidden') === 'true') continue;
        const markingView = this.normalizeStudioKey(slide?.dataset?.markingView || '');
        const mediaId = String(slide?.dataset?.mediaId || '').trim();

        if (markingView) viewKeys.add(markingView);
        if (mediaId) viewKeys.add(this.normalizeStudioKey(`photo-${mediaId}`));
        if (visibleIndex === 0) viewKeys.add('front');
        visibleIndex += 1;
      }

      return viewKeys;
    }

    studioViewsSharePhoto(first, second) {
      if (!first || !second) return false;
      const id = view => this.getResourceNumericId(view.mediaId || view.fileId || view.media_id || view.file_id || '');
      const url = view => String(view.imageUrl || view.image || view.previewImage || '').replace(/^https?:/, '').split('?')[0];
      return Boolean(id(first) && id(first) === id(second) || url(first) && url(first) === url(second));
    }

    wrapHasDistinctBackPhoto(wrap, variantId, view = this.getStudioConfigViewForWrap(wrap, variantId)) {
      const identity = this.getStudioEmplacement(wrap)?.key, wraps = this.allOptionWraps || [];
      if (!identity || identity === 'wrap') return true;
      const rank = (item, index) => this.getStudioEmplacement(item)?.key === 'front' ? -1 : index;
      const priority = rank(wrap, wraps.indexOf(wrap));
      return !wraps.some((other, index) => {
        const key = this.getStudioEmplacement(other)?.key;
        return key && key !== identity && key !== 'wrap' && rank(other, index) < priority
          && this.studioViewsSharePhoto(view, this.getStudioConfigViewForWrap(other, variantId));
      });
    }

    wrapHasAvailableGalleryView(wrap, variantId = this.activeVariantId || this.getCurrentVariantId(), { allowHidden = false } = {}) {
      if (!wrap || wrap.dataset.virtualSupplierPrintPlane === 'true') return false;
      const source = wrap.dataset.sourceStudioZoneKey;
      if (source) wrap = this.getZoneWrap(source);
      if (!wrap) return false;
      const view = this.getStudioConfigViewForWrap(wrap, variantId);
      const zone = this.getStudioConfigZoneForWrap(wrap, variantId);
      if (!view || !zone || !this.wrapAppliesToVariant(wrap, variantId)) return false;
      if (!this.wrapHasDistinctBackPhoto(wrap, variantId, view)) return false;
      const variantIds = this.normalizeIdList(view.variantIds || view.variant_ids || []);
      const activeTokens = this.getActiveVariantTokens(variantId);
      if (variantIds.length && !variantIds.some(id => activeTokens.has(id))) return false;
      const config = this.getTodayStudioConfig();
      if (Number(config?.schemaVersion) === 2 && Object.keys(config.variantMediaV2 || {}).length
        && this.getStudioPlacementForWrap(wrap) && !this.getMappedStudioViewIdForWrap(wrap, variantId)) return false;
      const geometry = Array.isArray(zone.parts) && zone.parts.length ? zone.parts : [zone];
      if (geometry.some(part => {
        const values = [part.x, part.y, part.width, part.height].map(value => this.parseZonePlacementNumber(value));
        const [x, y, width, height] = values;
        return !values.every(Number.isFinite) || x < 0 || y < 0 || width <= 0 || height <= 0
          || x + width > 100 || y + height > 100;
      })) return false;
      const mediaId = this.getResourceNumericId(view.mediaId || view.fileId || view.media_id || view.file_id || '');
      const imagePath = value => {
        try { return new URL(String(value || ''), window.location.origin).pathname; } catch { return ''; }
      };
      const expectedImage = imagePath(view.imageUrl || view.image || view.previewImage || '');
      if (!mediaId && (!expectedImage || expectedImage === '/')) return false;
      return Array.from(this.getMediaGallery()?.querySelectorAll?.('.product__media-item') || []).some(slide => {
        const auxiliary = slide.dataset.studioAuxiliaryVerified === 'true';
        if (!auxiliary && !allowHidden && (slide.hidden || slide.style.display === 'none' || slide.getAttribute('aria-hidden') === 'true')) return false;
        const image = slide.querySelector('img[src]');
        if (!image || image.complete && image.naturalWidth === 0) return false;
        // A matching generic label such as "front" never substitutes another colour/photo.
        return mediaId ? this.getResourceNumericId(slide.dataset.mediaId) === mediaId
          : expectedImage === imagePath(image.currentSrc || image.src || image.getAttribute('src'));
      });
    }

    refreshVariantAvailability(options = {}) {
      const previousVariantId = this.activeVariantId;
      const previousWraps = this.optionWraps || [];
      if (options.syncVariant !== false) this.syncActiveVariantId();
      const availabilityChanged = wraps => previousVariantId !== this.activeVariantId
        || wraps.length !== previousWraps.length
        || wraps.some((wrap, index) => wrap !== previousWraps[index])
        || Boolean(this.activeZoneWrap && !wraps.includes(this.activeZoneWrap));

      const allWraps = this.getActiveOptionWraps();
      if (this.productRoot?.dataset.personalizationExperience === 'xd-v2') {
        const visibleWraps = this.sortStudioWrapsForDisplay(allWraps.filter((wrap) => this.supplierV2?.offer(wrap.dataset.zoneKey)));
        const changed = availabilityChanged(visibleWraps);
        if (options.onlyIfChanged && !changed) return false;
        this.optionWraps = visibleWraps;
        const active = new Set(this.optionWraps);
        (this.allOptionWraps || allWraps).forEach((wrap) => {
          wrap.hidden = !active.has(wrap);
          wrap.querySelectorAll('[data-role="marking-input"], [data-role="logo-file-input"]').forEach((input) => { input.disabled = !active.has(wrap); });
        });
        if (!active.has(this.activeZoneWrap)) { this.activeZoneWrap = null; this.activeZoneKey = ''; }
        this.dataset.zoneCount = String(this.optionWraps.length);
        return changed;
      }
      const choice = this.querySelector('product-personalization-choice');
      const galleryWraps = allWraps.filter((wrap) => this.wrapHasAvailableGalleryView(wrap)
        && (!choice?.initialized || choice.profilePlacement(this.getVariantStateKey(), wrap.dataset.zoneKey).zone));
      const matchingWraps = galleryWraps.filter((wrap) => this.wrapAppliesToVariant(wrap));
      const visibleWraps = this.sortStudioWrapsForDisplay(this.getVariantPreferredWraps(matchingWraps));
      const changed = availabilityChanged(visibleWraps);
      if (options.onlyIfChanged && !changed) return false;
      const activeWraps = new Set(visibleWraps);

      (this.allOptionWraps || []).forEach((wrap) => {
        const isVariantVisible = activeWraps.has(wrap);
        wrap.dataset.studioImageBindingUnavailable = String(!isVariantVisible);
        wrap.hidden = !isVariantVisible;
        wrap.classList.toggle('is-variant-hidden', !isVariantVisible);

        const markingInput = wrap.querySelector('[data-role="marking-input"]');
        const fileInput = wrap.querySelector('[data-role="logo-file-input"]');
        if (markingInput) markingInput.disabled = !isVariantVisible;
        if (fileInput) fileInput.disabled = !isVariantVisible;
      });

      this.optionWraps = visibleWraps;
      this.dataset.zoneCount = String(this.optionWraps.length);
      this.updateAvailabilityVisibility();

      if (this.activeZoneWrap && !activeWraps.has(this.activeZoneWrap)) {
        this.activeZoneWrap = null;
        this.activeZoneKey = '';
      }
      return changed;
    }

    applyVariant(variantId) {
      const nextVariantId = String(variantId || '').trim();
      if (!nextVariantId || nextVariantId === this.activeVariantId) return;
      this.storeCurrentVariantLogoState();
      const previousState = this.variantLogoStates.get(this.getVariantStateKey());
      const inheritance = !this.variantLogoStates.has(this.getVariantStateKey(nextVariantId))
        ? this.supplierV2?.prepareVariantLogoInheritance?.(nextVariantId, previousState)
          || (!this.supplierV2 && this.prepareCompatibleVariantLogoInheritance(nextVariantId, previousState))
        : null;
      if (inheritance) {
        this.variantLogoStates.set(this.getVariantStateKey(nextVariantId), inheritance.state);
        this.inheritedVariantArtwork = { variantId: nextVariantId, placements: inheritance.placements, decodedPreviews: inheritance.decodedPreviews };
        inheritance.activate?.();
      }
      this.scopedLogoImportController?.abort();
      this.activeVariantId = nextVariantId;
      this.dataset.activeVariantId = nextVariantId;
      this.refreshVariantAvailability({ syncVariant: false });
      this.restoreVariantLogoState(nextVariantId);
      this.setupStudioLight();
      this.syncUI();
    }

    prepareCompatibleVariantLogoInheritance(nextVariantId, state) {
      // The Midocean profile binds physical limits to a specific photo part.
      // Other product paths retain their existing per-variant behaviour.
      if (this.productRoot?.dataset.supplierPricing !== 'midocean') return null;
      const choice = this.querySelector('product-personalization-choice');
      const gallery = this.getMediaGallery();
      const previousVariantId = this.getResourceNumericId(this.activeVariantId) || this.activeVariantId;
      const destinationVariantId = this.getResourceNumericId(nextVariantId) || nextVariantId;
      if (!choice?.initialized || !choice.profilePlacement || !choice.configurationSelections
        || !gallery?.designArtworkPlacements || !this.getActiveVariantTokens(gallery.designStudioState?.variantId).has(previousVariantId)) return null;
      const zones = {}, placements = new Map(), selections = [];
      for (const [zoneKey, zoneState] of Object.entries(state?.zones || {})) {
        const wrap = this.getZoneWrap(zoneKey);
        if (!wrap || !this.wrapAppliesToVariant(wrap, nextVariantId) || zoneState.files?.length !== 1
          || !(zoneState.files[0] instanceof File) || (gallery.getDesignArtworkCopies?.(zoneKey) || []).length) continue;
        const previous = choice.profilePlacement(previousVariantId, zoneKey);
        const next = choice.profilePlacement(destinationVariantId, zoneKey);
        if (previous.variant?.identityStatus !== 'exact' || next.variant?.identityStatus !== 'exact'
          || !previous.zone || !next.zone || previous.zone.previewAvailable === false || next.zone.previewAvailable === false
          || previous.zone.key !== next.zone.key || previous.zone.studioPartIds?.length !== 1 || next.zone.studioPartIds?.length !== 1
          || [...(previous.zone.instances || []), ...(next.zone.instances || [])].some(item => item.geometryOverride)) continue;
        const oldIdentity = `${previousVariantId}:${previous.zone.key}`, nextIdentity = `${destinationVariantId}:${next.zone.key}`;
        const selected = choice.configurationSelections.get(oldIdentity);
        const existing = choice.configurationSelections.get(nextIdentity);
        const oldTechnique = previous.zone.techniques?.find(item => item.id === selected?.techniqueId);
        const nextTechnique = next.zone.techniques?.find(item => item.id === (existing?.techniqueId || selected?.techniqueId));
        if (!oldTechnique || !nextTechnique || oldTechnique.id !== nextTechnique.id || nextTechnique.available === false
          || ![oldTechnique.widthMm, oldTechnique.heightMm].every(value => Number.isFinite(value) && value > 0)
          || ['widthMm', 'heightMm', 'shape', 'colorsMode', 'maxColors'].some(key => oldTechnique[key] !== nextTechnique[key])
          || JSON.stringify(oldTechnique.standardMarkingDimensions) !== JSON.stringify(nextTechnique.standardMarkingDimensions)
          || JSON.stringify(oldTechnique.editPlane || null) !== JSON.stringify(nextTechnique.editPlane || null)) continue;
        const previousView = this.getMappedStudioViewIdForWrap(wrap, previousVariantId);
        const nextView = this.getMappedStudioViewIdForWrap(wrap, nextVariantId);
        if (!previousView || !nextView) continue;
        const oldPart = this.getStudioZoneParts(this.getStudioConfigZoneForWrap(wrap, previousVariantId)).find(part => part.id === previous.zone.studioPartIds[0]);
        const nextPart = this.getStudioZoneParts(this.getStudioConfigZoneForWrap(wrap, nextVariantId)).find(part => part.id === next.zone.studioPartIds[0]);
        if (!oldPart || !nextPart || oldPart.shape !== 'rectangle' || oldPart.rotation || oldPart.curvature
          || ['x', 'y', 'width', 'height', 'rotation', 'shape', 'curvature'].some(key => oldPart[key] !== nextPart[key])) continue;
        const saved = gallery.designArtworkPlacements.get(gallery.getScopedDesignZoneKey?.(zoneKey));
        if (!saved || saved.partId && saved.partId !== oldPart.id
          || ![saved.x, saved.y, saved.width, saved.height, saved.rotation || 0].every(Number.isFinite)
          || saved.width <= 0 || saved.height <= 0
          || ['cropLeft', 'cropRight', 'cropTop', 'cropBottom'].some(key => Number(saved[key] || 0) !== 0)) continue;
        const radians = Number(saved.rotation || 0) * Math.PI / 180;
        const halfWidth = (Math.abs(Math.cos(radians)) * saved.width + Math.abs(Math.sin(radians)) * saved.height) / 2;
        const halfHeight = (Math.abs(Math.sin(radians)) * saved.width + Math.abs(Math.cos(radians)) * saved.height) / 2;
        if (saved.x - halfWidth < 0 || saved.x + halfWidth > 100 || saved.y - halfHeight < 0 || saved.y + halfHeight > 100) continue;
        zones[zoneKey] = { selected: zoneState.selected, files: zoneState.files.slice() };
        placements.set(zoneKey, { ...saved, partId: nextPart.id });
        selections.push({ oldIdentity, nextIdentity, selected, previousView, nextView, oldPart, nextPart });
      }
      if (!Object.keys(zones).length || Object.keys(zones).length !== Object.values(state?.zones || {}).filter(zone => zone.files?.length).length) return null;
      return { state: { zones }, placements, activate: () => {
        for (const { oldIdentity, nextIdentity, selected, nextView, oldPart, nextPart } of selections) {
          if (!choice.configurationSelections.has(nextIdentity)) choice.configurationSelections.set(nextIdentity, { ...selected });
          if (!choice.measurementSelections.has(nextIdentity)) {
            const measured = choice.measurementSelections.get(oldIdentity) || choice.measurements || [];
            choice.measurementSelections.set(nextIdentity, measured.filter(item => item.partId === oldPart.id
              && (!item.editPlaneId || oldTechnique.editPlane?.planeId === item.editPlaneId || oldTechnique.editPlane?.id === item.editPlaneId)
              && (!item.variantId || this.getActiveVariantTokens(item.variantId).has(previousVariantId))).map(item => ({ ...item,
              variantId: destinationVariantId, viewId: nextView, partId: nextPart.id })));
          }
        }
      } };
    }

    onThemeVariantChange(event = {}) {
      const variant = event.detail?.variant || event.data?.variant;
      const variantId = variant?.id || event.detail?.variantId || '';
      const nextVariantId = String(variantId || this.getCurrentVariantId() || '').trim();
      if (variant) this.currentVariant = variant;
      if (nextVariantId) this.applyVariant(nextVariantId);
    }

    onVariantInputChange(event = {}) {
      if (event.target?.matches && !event.target.matches('input[name="id"]')) return;
      this.variantInput = this.getVariantInput();
      this.currentVariant = this.getSelectedVariantFromDocument() || this.currentVariant;
      this.applyVariant(this.variantInput?.value || this.getCurrentVariantId() || '');
    }

    getVariantStateKey(variantId = this.activeVariantId) {
      return String(variantId || 'default').trim() || 'default';
    }

    captureLogoState() {
      const zones = {};
      (this.allOptionWraps || this.optionWraps || []).forEach((wrap) => {
        const zoneKey = String(wrap.dataset.zoneKey || '').trim();
        if (!zoneKey) return;

        const input = wrap.querySelector('[data-role="marking-input"]');
        const files = this.getLogoFiles(wrap);
        if (!input?.checked && !files.length) return;

        zones[zoneKey] = {
          selected: Boolean(input?.checked),
          files: files.slice(),
        };
      });

      return { zones };
    }

    storeCurrentVariantLogoState() {
      if (this.isRestoringVariantState) return;
      this.variantLogoStates.set(this.getVariantStateKey(), this.captureLogoState());
    }

    restoreVariantLogoState(variantId = this.activeVariantId) {
      const state = this.variantLogoStates.get(this.getVariantStateKey(variantId)) || { zones: {} };
      this.isRestoringVariantState = true;

      (this.allOptionWraps || this.optionWraps || []).forEach((wrap) => {
        const zoneState = state.zones?.[wrap.dataset.zoneKey] || null;
        const input = wrap.querySelector('[data-role="marking-input"]');
        if (input) input.checked = Boolean(zoneState?.selected);
        this.setLogoFilesForWrap(wrap, zoneState?.files || [], { allowBlockedRestore: true });
      });

      this.isRestoringVariantState = false;

      const restoredLogoZoneKey = Object.entries(state.zones || {}).find(([, zoneState]) => (
        Array.isArray(zoneState?.files) && zoneState.files.length > 0
      ))?.[0] || '';
      const activeZoneHasRestoredLogo = Boolean(state.zones?.[this.activeZoneKey]?.files?.length);
      const restoredLogoWrap = restoredLogoZoneKey ? this.getZoneWrap(restoredLogoZoneKey) : null;

      if (restoredLogoWrap && !activeZoneHasRestoredLogo) {
        this.activeZoneKey = restoredLogoZoneKey;
        this.activeZoneWrap = restoredLogoWrap;
      }
    }

    setLogoFilesForWrap(wrap, files = [], options = {}) {
      const fileInput = wrap?.querySelector('[data-role="logo-file-input"]');
      if (!fileInput) return false;

      const nextFiles = files.filter((file) => (typeof File === 'undefined' ? Boolean(file) : file instanceof File));
      const currentFiles = this.getLogoFiles(wrap);
      const preservesExistingOnly = currentFiles.length > 0 &&
        nextFiles.length > 0 &&
        nextFiles.length < currentFiles.length &&
        nextFiles.every((file) => currentFiles.includes(file));
      if (nextFiles.length && !options.allowBlockedRestore && !preservesExistingOnly
        && wrap.dataset.studioImageBindingUnavailable === 'true') return false;
      if (nextFiles.length && !options.allowBlockedRestore && !preservesExistingOnly && this.isStudioDesignBlockedForWrap(wrap)) {
        this.updateStudioExclusivityNotice(this.getStudioExclusivityBlocker(wrap));
        return false;
      }

      if (!nextFiles.length) {
        this.revokeStudioPreviewUrl(wrap.dataset.zoneKey);
        this.markWrapLogoCleared(wrap);
        try {
          fileInput.value = '';
        } catch (error) {
          // Some browsers are picky about programmatic file input changes.
        }

        if (fileInput.files?.length && typeof DataTransfer !== 'undefined') {
          try {
            fileInput.files = new DataTransfer().files;
          } catch (error) {
            // The dataset below is the source used by the UI if the browser rejects the empty FileList.
          }
        }

        this.syncWrapLogoDataset(wrap);
        return true;
      }

      if (typeof DataTransfer === 'undefined') {
        return false;
      }

      try {
        const transfer = new DataTransfer();
        nextFiles.forEach((file) => transfer.items.add(file));
        fileInput.files = transfer.files;
        fileInput.disabled = false;
        this.revokeStudioPreviewUrl(wrap.dataset.zoneKey, '', { preserveFiles: nextFiles });
        this.unmarkWrapLogoCleared(wrap);
        this.syncWrapLogoDataset(wrap);
        return true;
      } catch (error) {
        return false;
      }
    }

    markWrapLogoCleared(wrap) {
      if (!wrap) return;
      wrap.dataset.logoCleared = 'true';
      delete wrap.dataset.logoFileName;
      delete wrap.dataset.logoFileNames;
      wrap.classList.remove('has-logo-attached');
    }

    unmarkWrapLogoCleared(wrap) {
      if (!wrap) return;
      delete wrap.dataset.logoCleared;
    }

    syncWrapLogoDataset(wrap, fallbackFileNames = null) {
      if (!wrap) return;
      if (wrap.dataset.logoCleared === 'true') {
        delete wrap.dataset.logoFileName;
        delete wrap.dataset.logoFileNames;
        wrap.classList.remove('has-logo-attached');
        return;
      }

      const fileNames = fallbackFileNames || this.getLogoFileNames(wrap);
      if (fileNames.length) {
        wrap.dataset.logoFileName = fileNames[0];
        wrap.dataset.logoFileNames = JSON.stringify(fileNames);
      } else {
        delete wrap.dataset.logoFileName;
        delete wrap.dataset.logoFileNames;
      }
      wrap.classList.toggle('has-logo-attached', fileNames.length > 0);
    }

    get selectedInputs() {
      return this.optionWraps
        .map((wrap) => wrap.querySelector('[data-role="marking-input"]'))
        .filter((input) => input?.checked);
    }

    isOpen() {
      return this.drawer?.isOpen() || false;
    }

    onChange(event) {
      const markingInput = event.target.closest('[data-role="marking-input"]');
      if (markingInput && this.contains(markingInput)) {
        this.setActiveStudioZone(markingInput.dataset.zoneKey, { selectInput: false });
        this.storeCurrentVariantLogoState();
        this.syncUI();
        return;
      }

      const fileInput = event.target.closest('[data-role="logo-file-input"]');
      if (fileInput && this.contains(fileInput)) {
        this.handleLogoFileChange(fileInput, { openMobileEditor: true });
      }
    }

    onClick(event) {
      const previewButton = event.target.closest('[data-role="studio-add-preview"]');
      if (previewButton && this.contains(previewButton)) {
        event.preventDefault();
        event.stopPropagation();
        this.openStudioPreviewInput(previewButton.dataset.zoneKey, Number(previewButton.dataset.fileIndex));
        return;
      }

      const addDesignButton = event.target.closest('[data-role="studio-add-design"]');
      if (addDesignButton && this.contains(addDesignButton)) {
        event.preventDefault();
        event.stopPropagation();
        this.openActiveLogoInput({ append: true });
        return;
      }

      const exclusivityAction = event.target.closest('[data-role="studio-exclusivity-action"]');
      if (exclusivityAction && this.contains(exclusivityAction)) {
        event.preventDefault();
        event.stopPropagation();
        const zoneKey = exclusivityAction.dataset.zoneKey || '';
        if (zoneKey) this.setActiveStudioZone(zoneKey, { selectInput: false, forceBroadcast: true });
        return;
      }

      const zoneTab = event.target.closest('[data-role="zone-tab"]');
      if (zoneTab && this.contains(zoneTab)) {
        event.preventDefault();
        this.setActiveStudioZone(zoneTab.dataset.zoneKey, { selectInput: false, forceBroadcast: true });
        return;
      }

      const removeFileButton = event.target.closest('[data-role="studio-file-remove"]');
      if (removeFileButton && this.contains(removeFileButton)) {
        event.preventDefault();
        event.stopPropagation();
        const fileIndex = Number(removeFileButton.dataset.fileIndex);
        const zoneKey = removeFileButton.dataset.zoneKey || this.studioFileCard?.dataset.zoneKey || this.activeZoneKey;
        if (Number.isInteger(fileIndex) && fileIndex > -1) {
          this.clearLogoFileForZone(zoneKey, fileIndex);
        } else {
          this.clearLogoForZone(zoneKey);
        }
        return;
      }

      const recentLogo = event.target.closest('[data-role="recent-logo"]');
      if (recentLogo && this.contains(recentLogo)) {
        event.preventDefault();
        event.stopPropagation();
        this.applyRecentLogoToActiveZone(recentLogo.dataset.recentLogoId);
        return;
      }

      const studioUploadTrigger = event.target.closest('[data-role="studio-upload-empty"], [data-role="studio-file-replace"], [data-role="recent-logo-upload"]');
      if (studioUploadTrigger && this.contains(studioUploadTrigger)) {
        event.preventDefault();
        this.openActiveLogoInput();
        return;
      }

      return;
    }

    handleLogoFileChange(fileInput, options = {}) {
      const optionWrap = fileInput.closest('[data-role="option-wrap"]');
      if (!optionWrap) return;
      const previous = this.variantLogoStates?.get(this.getVariantStateKey())?.zones?.[optionWrap.dataset.zoneKey];
      const chosenFiles = Array.from(fileInput.files || []);
      if (!this.validateArtworkFiles(chosenFiles)) {
        this.setLogoFilesForWrap(optionWrap, previous?.files || [], { allowBlockedRestore: true });
        this.pendingLogoAppend = null;
        this.syncUI();
        return;
      }

      this.mergePendingLogoFiles(fileInput);
      const nextFiles = Array.from(fileInput.files || []);
      if (nextFiles.length && this.isStudioDesignBlockedForWrap(optionWrap)) {
        this.setLogoFilesForWrap(optionWrap, previous?.files || [], { allowBlockedRestore: true });
        this.pendingLogoAppend = null;
        this.syncUI();
        return;
      }

      this.unmarkWrapLogoCleared(optionWrap);
      this.syncWrapLogoDataset(optionWrap);

      const input = optionWrap.querySelector('[data-role="marking-input"]');
      const fileNames = this.getLogoFileNames(optionWrap);
      if (fileNames.length && input && !input.checked) {
        input.checked = true;
      }

      if (!this.isRestoringVariantState && options.rememberRecent !== false) {
        this.rememberRecentLogos(this.getLogoFiles(optionWrap));
      }
      this.setActiveStudioZone(optionWrap.dataset.zoneKey, { selectInput: false });
      this.syncUI();
      if (options.openMobileEditor && !this.isRestoringVariantState) {
        this.requestMobileDesignEditorForFile(optionWrap, this.getLogoFiles(optionWrap)[0]);
      }
    }

    requestMobileDesignEditorForFile(wrap, file) {
      if (!wrap || !file || this.isRestoringVariantState) return;
      this.getMediaGallery()?.requestMobileDesignEditor?.({
        variantId: this.getVariantStateKey(),
        zoneKey: wrap.dataset.zoneKey || '',
        fileKey: this.getLogoFileKey(file),
      });
    }

    setupStudioLight() {
      this.updateStudioSubzoneTabs();
      if (!this.studioLight || !this.studioTabs) return;
      if (!this.optionWraps.length) {
        this.studioTabs.innerHTML = '';
        this.studioLight.hidden = true;
        this.classList.remove('is-studio-light-ready');
        return;
      }

      const logoWraps = this.optionWraps.filter((wrap) => wrap.dataset.supportsLogo === 'true');
      this.studioZoneWraps = logoWraps.length ? logoWraps : this.optionWraps;
      this.studioTabItems = this.getStudioTabItems(this.studioZoneWraps);
      this.studioTabs.innerHTML = '';

      this.studioTabItems.forEach((item, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'product-marking-selector__studio-tab';
        tab.dataset.role = 'zone-tab';
        tab.dataset.zoneKey = item.wrap.dataset.zoneKey || '';
        tab.dataset.viewKey = item.viewKey || '';
        tab.dataset.placementKey = item.placementKey || '';
        tab.dataset.emplacementKey = item.emplacementKey || '';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

        const labelElement = document.createElement('span');
        labelElement.className = 'product-marking-selector__studio-tab-label';
        labelElement.textContent = item.label;

        const statusElement = document.createElement('span');
        statusElement.className = 'product-marking-selector__studio-tab-status';
        statusElement.setAttribute('aria-hidden', 'true');

        tab.append(labelElement, statusElement);
        this.studioTabs.append(tab);
      });

      if (this.studioUploadEmpty) {
        this.studioUploadEmpty.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openActiveLogoInput();
        };
      }

      if (this.studioFileReplace) {
        this.studioFileReplace.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openActiveLogoInput();
        };
      }

      if (this.studioFileRemove) {
        this.studioFileRemove.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.clearLogoForZone(this.studioFileRemove.dataset.zoneKey || this.activeZoneKey);
        };
      }

      this.studioLight.hidden = false;
      this.classList.add('is-studio-light-ready');
      this.updateAddDesignVisibility();

      const initialWrap = this.getInitialStudioZoneWrap();
      if (initialWrap) {
        this.setActiveStudioZone(initialWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
        if (!window.TodayMerchBrandPlacement?.selectPlacement && this.initialStudioZoneKey === undefined) {
          this.initialStudioZoneKey = initialWrap.dataset.zoneKey;
        }
      }
    }

    setActiveStudioView(viewKey, options = {}) {
      const targetWrap = this.findZoneWrapForView(viewKey);
      if (!targetWrap) return;
      this.setActiveStudioZone(targetWrap.dataset.zoneKey, options);
    }

    setActiveStudioZone(zoneKey, options = {}) {
      if (!zoneKey || !this.studioZoneWraps?.length) return;

      const previousZoneKey = this.activeZoneKey;
      const activeWrap =
        this.studioZoneWraps.find((wrap) => wrap.dataset.zoneKey === zoneKey) ||
        this.optionWraps.find((wrap) => wrap.dataset.zoneKey === zoneKey);

      if (!activeWrap) return;

      if (options.broadcast !== false) this.hasExplicitStudioZoneChoice = true;
      this.activeZoneKey = zoneKey;
      this.activeZoneWrap = activeWrap;
      const activePlacementKey = this.getStudioTabPlacementKey(activeWrap);
      const emplacementKey = this.getZoneState(activeWrap).emplacementKey || this.getStudioEmplacement(activeWrap).key;
      if (options.broadcast !== false) {
        this.studioEmplacementChoices ||= new Map();
        this.studioEmplacementChoices.set(`${this.getVariantStateKey()}|${emplacementKey}`, zoneKey);
      }

      this.querySelectorAll('[data-role="zone-tab"]').forEach((tab) => {
        const isActive = tab.dataset.emplacementKey ? tab.dataset.emplacementKey === emplacementKey
          : tab.dataset.zoneKey === zoneKey || Boolean(activePlacementKey && tab.dataset.placementKey === activePlacementKey);
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (options.selectInput) {
        const input = activeWrap.querySelector('[data-role="marking-input"]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.updateStudioFile();
      this.updateStudioTabs();
      this.updateStudioSubzoneTabs();
      this.renderRecentLogos();

      if (options.broadcast !== false && (options.forceBroadcast || previousZoneKey !== zoneKey)) {
        this.dispatchStudioStateChange();
        this.dispatchStudioZoneChange(activeWrap);
      }
    }

    onGalleryMediaViewSelected(event) {
      const viewKey = event.detail?.viewKey;
      if (!viewKey || event.detail?.source === 'marking-selector') return;

      this.refreshVariantAvailability();
      this.setupStudioLight();

      const matchingWrap = this.findZoneWrapForView(viewKey);
      if (!matchingWrap) return;

      this.setActiveStudioZone(matchingWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
      this.syncUI();
    }

    onGalleryDesignZoneSelected(event) {
      const zoneKey = event.detail?.zoneKey;
      if (!zoneKey) return;

      const matchingWrap = this.optionWraps.find((wrap) => wrap.dataset.zoneKey === zoneKey);
      if (!matchingWrap) return;

      if (['view-switcher', 'zone-hover'].includes(event.detail?.interaction)) {
        this.hasExplicitStudioZoneChoice = true;
        if (this.activeZoneKey === zoneKey) return;

        // The gallery already owns navigation. Only the upload destination and
        // active-zone state change; rebuilding the whole form causes a second
        // panel update and another navigation during the same transition.
        this.setActiveStudioZone(zoneKey, { selectInput: false, broadcast: false });
        this.dispatchStudioStateChange();
        return;
      }

      this.setActiveStudioZone(zoneKey, { selectInput: false });
      this.syncUI();
    }

    onGalleryClearLogo(event) {
      const zoneKey = event.detail?.zoneKey;
      if (!zoneKey) return;

      const matchingWrap = this.getZoneWrap(zoneKey);
      if (!matchingWrap || event.todayMerchMarkingClearHandled) return;
      event.todayMerchMarkingClearHandled = true;

      this.setActiveStudioZone(matchingWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
      this.clearLogoForWrap(matchingWrap);
    }

    onGalleryUpdateLogo(event) {
      const zoneKey = event.detail?.zoneKey;
      const file = event.detail?.file;
      if (!zoneKey || !(file instanceof File)) return;

      const matchingWrap = this.getZoneWrap(zoneKey);
      if (!matchingWrap || event.todayMerchMarkingUpdateHandled) return;
      event.todayMerchMarkingUpdateHandled = true;

      if (event.detail?.action === 'remove-background') {
        const original = event.detail.originalFile, artworkId = event.detail.artworkId || 'base';
        const currentFiles = this.getLogoFiles(matchingWrap);
        if (!original || !currentFiles.includes(original)) return;
        if (event.detail.variantKey && event.detail.variantKey !== this.getVariantStateKey()) return;
        if (event.detail.sourceScopeKey && event.detail.sourceScopeKey !== this.getArtworkProductionScope(zoneKey, { artworkId })?.key) return;
        const production = this.getArtworkProductionSource(original, zoneKey, { artworkId });
        if (production.file !== event.detail.sourceFile || production.productionFileKey !== event.detail.sourceProductionFileKey) return;
        const input = matchingWrap.querySelector('[data-role="marking-input"]');
        if (input) input.checked = true;
        this.setActiveStudioZone(zoneKey, { selectInput: false, broadcast: false });
        this.selectArtworkProductionSource(original, file, { kind: 'adapted', zoneKey, artworkId, techniqueId: event.detail.techniqueId, variantId: event.detail.variantKey });
        return;
      }

      const fileInput = matchingWrap?.querySelector('[data-role="logo-file-input"]');
      if (!matchingWrap || !fileInput || typeof DataTransfer === 'undefined') return;

      const currentFiles = this.getLogoFiles(matchingWrap);
      const nextFiles = [file, ...currentFiles.slice(1)];
      if (!this.setLogoFilesForWrap(matchingWrap, nextFiles)) {
        this.syncUI();
        return;
      }

      const input = matchingWrap.querySelector('[data-role="marking-input"]');
      if (input) input.checked = true;

      this.setActiveStudioZone(zoneKey, { selectInput: false, broadcast: false });
      this.handleLogoFileChange(fileInput);
    }

    getDesignTransformKey(zoneKey, variantId = this.getVariantStateKey()) {
      return `${String(variantId || 'default')}::${String(zoneKey || '')}`;
    }

    onGalleryDesignTransformChange(event) {
      const detail = event.detail;
      const zoneKey = String(detail?.zone || '');
      if (!zoneKey) return;

      const eventVariantId = String(detail?.variantId || this.getVariantStateKey());
      const activeVariantIds = new Set(this.getVariantLookupKeys(this.getVariantStateKey()));
      if (
        eventVariantId &&
        eventVariantId !== 'default' &&
        !this.getVariantLookupKeys(eventVariantId).some((id) => activeVariantIds.has(id))
      ) {
        return;
      }

      this.designTransforms.set(
        this.getDesignTransformKey(zoneKey, this.getVariantStateKey()),
        {
          ...detail,
          variantId: this.getVariantStateKey(),
          zone: zoneKey,
        },
      );
      this.syncArtworkTransformProperty();
    }

    getActiveArtworkTransform() {
      const logoZoneKeys = this.optionWraps
        .filter((wrap) => {
          const input = wrap.querySelector('[data-role="marking-input"]');
          return input?.checked && this.getLogoFileNames(wrap).length > 0;
        })
        .map((wrap) => String(wrap.dataset.zoneKey || ''))
        .filter(Boolean);
      const transforms = logoZoneKeys
        .map((zoneKey) => this.designTransforms.get(
          this.getDesignTransformKey(zoneKey, this.getVariantStateKey()),
        ))
        .filter(Boolean);
      const artworks = transforms.flatMap((transform) => (
        Array.isArray(transform.artworks)
          ? transform.artworks.map((artwork) => ({
              ...artwork,
              view: transform.view || '',
              zone: transform.zone || '',
            }))
          : []
      ));
      const usedPartIds = [...new Set(
        artworks.flatMap((artwork) => (
          Array.isArray(artwork.partIds)
            ? artwork.partIds
            : [artwork.partId].filter(Boolean)
        )).map(String).filter(Boolean),
      )];

      if (!artworks.length && !usedPartIds.length) return null;

      return {
        schemaVersion: 2,
        variantId: this.getVariantStateKey(),
        views: [...new Set(transforms.map((transform) => transform.view).filter(Boolean))],
        zones: [...new Set(transforms.map((transform) => transform.zone).filter(Boolean))],
        artworks,
        usedPartIds,
        physicalLocationCount: usedPartIds.length,
        additionalLocationQuoteRequired: usedPartIds.length > 1,
      };
    }

    syncArtworkTransformProperty() {
      const transform = this.getActiveArtworkTransform();
      this.setPropertyValue(
        this.artworkTransformProperty,
        transform ? JSON.stringify(transform) : '',
        Boolean(transform),
      );
    }

    findZoneWrapForView(viewKey) {
      const normalizedViewKey = this.normalizeStudioKey(viewKey);
      if (!normalizedViewKey) return null;

      if (this.activeZoneWrap && this.getZoneViewKey(this.activeZoneWrap) === normalizedViewKey) {
        return this.activeZoneWrap;
      }

      const logoWrap = this.optionWraps.find((wrap) => this.getZoneViewKey(wrap) === normalizedViewKey && this.wrapHasLogo(wrap));
      if (logoWrap) return logoWrap;

      const preferredWrap = this.optionWraps.find((wrap) => this.getZoneViewKey(wrap) === normalizedViewKey);
      if (preferredWrap) return preferredWrap;

      if (normalizedViewKey === 'sleeve') {
        return this.optionWraps.find((wrap) => {
          const zoneViewKey = this.getZoneViewKey(wrap);
          return zoneViewKey === 'right_sleeve' || zoneViewKey === 'left_sleeve';
        });
      }

      if (normalizedViewKey === 'right_sleeve' || normalizedViewKey === 'left_sleeve') {
        return this.optionWraps.find((wrap) => this.getZoneViewKey(wrap) === 'sleeve');
      }

      return null;
    }

    getCurrentGalleryViewKey() {
      const mediaGallery = this.getMediaGallery();
      const activeSlide =
        mediaGallery?.getActiveMediaSlide?.() ||
        mediaGallery?.querySelector?.('.product__media-item.swiper-slide-active') ||
        mediaGallery?.querySelector?.('.product__media-item');
      return this.normalizeStudioKey(activeSlide?.dataset?.markingView || '');
    }

    getInitialStudioZoneWrap() {
      // Rebuilding tabs after a gallery or variant refresh must preserve the current choice.
      const activeWrap = this.studioZoneWraps.find((wrap) => wrap.dataset.zoneKey === this.activeZoneKey);
      if (activeWrap) return activeWrap;

      const uploadedWrap = this.studioZoneWraps.find((wrap) => this.getLogoFileNames(wrap).length > 0);
      if (uploadedWrap) return uploadedWrap;

      const firstSelectedWrap = this.studioZoneWraps.find((wrap) =>
        wrap.querySelector('[data-role="marking-input"]')?.checked
      );
      if (firstSelectedWrap && this.getStudioTabViewKey(firstSelectedWrap)) return firstSelectedWrap;

      const suggestedWrap = this.getSuggestedStudioPlacement()?.wrap;
      if (suggestedWrap && this.studioZoneWraps.includes(suggestedWrap)) return suggestedWrap;

      const currentGalleryViewKey = this.getCurrentGalleryViewKey();
      const galleryWrap = currentGalleryViewKey ? this.findZoneWrapForView(currentGalleryViewKey) : null;
      if (galleryWrap && this.studioZoneWraps.includes(galleryWrap)) return galleryWrap;

      return this.studioTabItems[0]?.wrap || this.studioZoneWraps[0] || null;
    }

    getSuggestedStudioPlacement(wrap = null) {
      if (this.supplierV2) return null;
      const selectPlacement = window.TodayMerchBrandPlacement?.selectPlacement;
      const config = this.getTodayStudioConfig();
      if (!selectPlacement || !config) return null;

      const wraps = wrap ? [wrap] : this.optionWraps || [];
      const views = Array.isArray(config.mediaViews) && config.mediaViews.length ? config.mediaViews : config.views || [];
      const variantId = this.getCurrentVariantId() || this.activeVariantId;
      const productHandle = this.productRoot?.dataset?.url?.match(/\/products\/([^/?#]+)/)?.[1] || '';
      const candidates = wraps.map((candidateWrap) => {
        const input = candidateWrap.querySelector('[data-role="marking-input"]');
        if (candidateWrap.hidden || candidateWrap.dataset.supportsLogo !== 'true' || input?.disabled) return null;
        if (!candidateWrap.querySelector('[data-role="logo-file-input"]') || !this.wrapAppliesToVariant(candidateWrap)) return null;
        const configured = this.getStudioPlacementForWrap(candidateWrap);
        const zone = this.getStudioConfigZoneForWrap(candidateWrap);
        const placementId = configured?.id || zone?.placementId || zone?.groupId || zone?.group_id || zone?.id || zone?.key;
        if (!placementId) return null;

        // Scope to the actual displayed view, even when one placement has several instances.
        const mappedView = this.getMappedStudioViewIdForWrap(candidateWrap);
        const viewKey = this.getZoneViewKey(candidateWrap);
        const view = views.find((item) => this.normalizeStudioKey(item?.id || item?.key || '') === viewKey);
        const viewId = mappedView || view?.id || view?.key || zone?.view || zone?.viewKey;
        if (!viewId) return null;
        const placement = selectPlacement({ config, variantId, productHandle, placementId, viewId });
        return placement ? { wrap: candidateWrap, placement } : null;
      }).filter(Boolean);

      candidates.sort((left, right) => left.placement.rank - right.placement.rank || left.placement.order - right.placement.order);
      return candidates[0] || null;
    }

    applyInitialStudioRecommendation() {
      // The shared helper may load after the product's deferred scripts.
      // Only its untouched automatic fallback can be updated at that point.
      if (this.hasExplicitStudioZoneChoice || !this.initialStudioZoneKey || this.activeZoneKey !== this.initialStudioZoneKey) return;
      if ((this.optionWraps || []).some((wrap) => (
        wrap.querySelector('[data-role="marking-input"]')?.checked || this.getLogoFileNames(wrap).length > 0
      ))) return;
      const suggestedWrap = this.getSuggestedStudioPlacement()?.wrap;
      if (!suggestedWrap || !this.studioZoneWraps?.includes(suggestedWrap)) return;
      this.setActiveStudioZone(suggestedWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
      this.syncUI();
    }

    dispatchStudioZoneChange(wrap) {
      if (!wrap) return;
      const zone = this.getZoneState(wrap);

      this.dispatchEvent(
        new CustomEvent('todaymerch:marking-zone-change', {
          bubbles: true,
          detail: {
            sectionId: this.dataset.sectionId || '',
            source: 'marking-selector',
            variantId: this.getVariantStateKey(),
            zone,
          },
        }),
      );

      const mediaGallery = this.getMediaGallery();
      mediaGallery?.slideToDesignView?.(zone.viewKey);
    }

    dispatchStudioStateChange() {
      const state = this.getStudioState();
      // Publish a committed variant and physical placement before listeners
      // calculate its price; image rendering can then use that same state.
      this.syncMediaGalleryStudioState(state, { render: false });
      if (this.personalizationResumeSilenced) return;

      const event = new CustomEvent('todaymerch:marking-state-change', {
        bubbles: true,
        detail: state,
      });
      this.dispatchEvent(event);

      this.syncMediaGalleryStudioState(state, {
        galleryRendered: event.todayMerchStudioRenderHandled === this.getMediaGallery(),
      });
    }

    syncMediaGalleryStudioState(state, { render = true, galleryRendered = false } = {}) {
      const mediaGallery = this.getMediaGallery();
      if (!mediaGallery || !state) return;

      mediaGallery.designStudioState = state;
      const inherited = this.inheritedVariantArtwork;
      if (inherited && this.getActiveVariantTokens(inherited.variantId).has(state.variantId)) {
        this.inheritedVariantArtwork = null;
        for (const [zoneKey, placement] of inherited.placements) {
          const zone = state.zones?.find(item => item.key === zoneKey && item.hasLogo);
          const key = zone && mediaGallery.getScopedDesignZoneKey?.(zoneKey);
          if (!key) continue;
          mediaGallery.designArtworkPlacements?.set(key, { ...placement });
          const stateKey = mediaGallery.getDesignArtworkStateKey?.(zoneKey, 'base');
          if (stateKey) mediaGallery.designArtworkImageUpdateModes?.set(stateKey, 'preserve-placement');
          const decoded = inherited.decodedPreviews?.get(zoneKey);
          const original = this.getLogoFiles(this.getZoneWrap(zoneKey))[0];
          const record = decoded && this.getStudioPreviewRecord(original, zoneKey);
          if (stateKey && record?.status === 'ready' && original === decoded.original && record.sourceFile === decoded.sourceFile
            && record.url === decoded.url && zone.previewUrl === decoded.url && zone.productionFileKey === decoded.productionFileKey
            && zone.originalIdentity === decoded.originalIdentity && decoded.image.complete && decoded.image.naturalWidth > 0
            && decoded.image.naturalHeight > 0 && decoded.image.getAttribute('src') === decoded.url
            && Math.abs(record.naturalRatio / (decoded.image.naturalWidth / decoded.image.naturalHeight) - 1) <= .000001
            && Math.abs(placement.imageNaturalRatio / record.naturalRatio - 1) <= .000001) {
            const metric = mediaGallery.getDesignArtworkImageMetricKey?.(zone, decoded.image);
            if (metric) mediaGallery.designArtworkImageMetricKeys?.set(stateKey, metric);
          }
        }
      }
      if (!render) return;
      (state.zones || []).forEach((zone) => {
        if (!zone?.hasLogo) mediaGallery.clearDesignArtworkForZone?.(zone?.key);
      });
      // The native gallery handles this exact event synchronously. Preserve
      // empty-zone cleanup, but render here only if no listener handled it.
      if (galleryRendered) return;

      const activeZone = state.zones?.find((zone) => zone.key === state.activeZoneKey) || state.logoZones?.[0] || state.zones?.[0];
      if (state.hasLogo && activeZone?.viewKey) {
        mediaGallery.slideToDesignView?.(activeZone.viewKey, { render: false });
      }

      mediaGallery.renderDesignStudio?.();
    }

    getStudioState() {
      const zones = this.optionWraps.map((wrap) => this.getZoneState(wrap));
      if (this.usesRecommendedStudioZones()) {
        const recommended = this.getRecommendedStudioZoneKeys(zones);
        zones.forEach(zone => { zone.recommendedForPlacement = recommended.has(zone.key); });
      }
      const activeZone = zones.find((zone) => zone.key === this.activeZoneKey) || zones[0] || null;
      const logoZones = zones.filter((zone) => zone.hasLogo);
      const personalizationChoice = this.querySelector('product-personalization-choice');

      return {
        sectionId: this.dataset.sectionId || '',
        source: 'marking-selector',
        variantId: this.getVariantStateKey(),
        profileKey: this.dataset.profileKey || '',
        activeZoneKey: activeZone?.key || '',
        activeViewKey: activeZone?.viewKey || '',
        hasLogo: logoZones.length > 0,
        zones,
        logoZones,
        ...(personalizationChoice ? { personalization: personalizationChoice.getSelection?.({ zones, logoZones, activeZoneKey: activeZone?.key || '' })
          || { mode: 'none', techniqueId: 'L3', colors: 1, zoneKeys: [] } } : {}),
      };
    }

    getZonePlacement(wrap) {
      const x = this.parseZonePlacementNumber(wrap?.dataset.zoneX);
      const y = this.parseZonePlacementNumber(wrap?.dataset.zoneY);
      const width = this.parseZonePlacementNumber(wrap?.dataset.zoneWidth);
      const height = this.parseZonePlacementNumber(wrap?.dataset.zoneHeight);

      if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

      const boundedX = Math.min(Math.max(x, 0), 99);
      const boundedY = Math.min(Math.max(y, 0), 99);
      return {
        x: boundedX,
        y: boundedY,
        width: Math.min(Math.max(width, 1), 100 - boundedX),
        height: Math.min(Math.max(height, 1), 100 - boundedY),
      };
    }

    getTodayStudioConfig() {
      if (this.todayStudioConfig !== undefined) return this.todayStudioConfig;

      const script = this.productRoot?.querySelector?.('[data-todays-studio-config]') || document.querySelector('[data-todays-studio-config]');
      if (!script) {
        this.todayStudioConfig = null;
        return this.todayStudioConfig;
      }

      try {
        const parsedConfig = JSON.parse(script.textContent || '{}');
        const localizedConfig = window.TodayMerchStudioI18n?.localizeConfigNode?.(script, parsedConfig) || parsedConfig;
        this.todayStudioConfig = window.TodayMerchStudioConfigCompat?.adapt?.(localizedConfig) || localizedConfig;
      } catch (error) {
        this.todayStudioConfig = null;
      }

      return this.todayStudioConfig;
    }

    normalizeStudioPlacementReference(value) {
      const key = this.normalizeStudioKey(value);
      return STUDIO_PLACEMENT_ALIASES[key] || key;
    }

    getStudioPlacementForWrap(wrap) {
      const config = this.getTodayStudioConfig();
      const placements = Array.isArray(config?.placements) ? config.placements : [];
      if (!wrap || !placements.length) return null;

      const activePlacements = placements.filter((placement) => (
        placement && placement.active !== false && placement.active !== 'false'
      ));
      const wrapKey = this.normalizeStudioPlacementReference(wrap.dataset.zoneKey);
      const keyMatch = activePlacements.find((placement) => {
        const stableKeys = [
          placement.id,
          ...(Array.isArray(placement.legacyKeys) ? placement.legacyKeys : []),
        ].map((value) => this.normalizeStudioPlacementReference(value)).filter(Boolean);
        return Boolean(wrapKey && stableKeys.includes(wrapKey));
      });
      if (keyMatch) return keyMatch;

      const wrapLabel = this.normalizeStudioPlacementReference(wrap.dataset.zoneLabel);
      return activePlacements.find((placement) => {
        if (!placement || placement.active === false || placement.active === 'false') return false;
        return Boolean(wrapLabel && this.normalizeStudioPlacementReference(placement.label) === wrapLabel);
      }) || null;
    }

    getMappedStudioViewIdForWrap(wrap, variantId = this.getCurrentVariantId()) {
      const config = this.getTodayStudioConfig();
      const variantMedia = config?.variantMediaV2;
      const placement = this.getStudioPlacementForWrap(wrap);
      if (!placement || !variantMedia || typeof variantMedia !== 'object') return '';

      const variantTokens = this.getActiveVariantTokens(variantId);
      const mappingKey = Array.from(variantTokens).find((key) => (
        variantMedia[key] && typeof variantMedia[key] === 'object'
      ));
      const assignments = mappingKey ? variantMedia[mappingKey] : null;
      if (!assignments) return '';
      const mapped = assignments[String(placement.id || '')];
      return String(mapped?.viewId || mapped || '').trim();
    }

    onGalleryVariantMediaUpdated(event = {}) {
      if (event.detail?.source !== 'media-gallery') return;
      const variantId = String(event.detail?.variantId || '').trim();
      if (variantId && variantId !== this.activeVariantId) {
        this.applyVariant(variantId);
        return;
      }
      this.refreshVariantAvailability();
      this.setupStudioLight();
      this.syncUI();
    }

    getStudioConfigZoneForWrap(wrap, variantId = this.getCurrentVariantId()) {
      const config = this.getTodayStudioConfig();
      const zones = Array.isArray(config?.zones) ? config.zones : [];
      if (!wrap || !zones.length) return null;

      const placement = this.getStudioPlacementForWrap(wrap);
      const wrapKeys = new Set([
        wrap.dataset.zoneKey,
        wrap.dataset.zoneLabel,
      ].map((value) => this.normalizeStudioPlacementReference(value)).filter(Boolean));
      const matchingZones = zones.filter((zone) => {
        if (!zone || zone.active === false || zone.active === 'false') return false;
        if (placement && String(zone.placementId || zone.groupId || '') === String(placement.id || '')) return true;
        const zoneKeys = [
          zone?.groupId,
          zone?.group_id,
          zone?.placementId,
          zone?.placement_id,
          zone?.key,
          zone?.id,
          zone?.label,
          ...(Array.isArray(zone?.legacyKeys) ? zone.legacyKeys : []),
        ].map((value) => this.normalizeStudioPlacementReference(value)).filter(Boolean);
        return zoneKeys.some((key) => wrapKeys.has(key));
      });
      if (!matchingZones.length) return null;

      const mappedViewId = this.getMappedStudioViewIdForWrap(wrap, variantId);
      if (mappedViewId) {
        const mappedZone = matchingZones.find((zone) => String(zone.view || zone.viewKey || '') === mappedViewId) ||
          matchingZones.find((zone) => this.normalizeStudioKey(zone.view || zone.viewKey || '') === this.normalizeStudioKey(mappedViewId));
        if (mappedZone) return mappedZone;
      }

      const explicitViewKey = this.normalizeStudioKey(wrap.dataset.zoneView || '');
      return matchingZones.find((zone) => this.normalizeStudioKey(zone.view || zone.viewKey || '') === explicitViewKey) || matchingZones[0];
    }

    getStudioZonePlacement(studioZone) {
      if (!studioZone) return null;

      const x = this.parseZonePlacementNumber(studioZone.x);
      const y = this.parseZonePlacementNumber(studioZone.y);
      const width = this.parseZonePlacementNumber(studioZone.width);
      const height = this.parseZonePlacementNumber(studioZone.height);

      if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

      const boundedX = Math.min(Math.max(x, 0), 99);
      const boundedY = Math.min(Math.max(y, 0), 99);
      return {
        x: boundedX,
        y: boundedY,
        width: Math.min(Math.max(width, 1), 100 - boundedX),
        height: Math.min(Math.max(height, 1), 100 - boundedY),
      };
    }

    getStudioZoneParts(studioZone) {
      const parts = Array.isArray(studioZone?.parts) ? studioZone.parts : [];
      return parts
        .map((part) => {
          const x = this.parseZonePlacementNumber(part?.x);
          const y = this.parseZonePlacementNumber(part?.y);
          const width = this.parseZonePlacementNumber(part?.width);
          const height = this.parseZonePlacementNumber(part?.height);
          const rotation = this.parseZonePlacementNumber(part?.rotation || part?.angle || 0);
          const curvature = this.parseZonePlacementNumber(part?.curvature || 0);

          if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

          const boundedX = Math.min(Math.max(x, 0), 99);
          const boundedY = Math.min(Math.max(y, 0), 99);
          return {
            id: String(part?.id || ''),
            x: boundedX,
            y: boundedY,
            width: Math.min(Math.max(width, 1), 100 - boundedX),
            height: Math.min(Math.max(height, 1), 100 - boundedY),
            rotation: Number.isFinite(rotation) ? rotation : 0,
            shape: String(part?.shape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle',
            curvature: Number.isFinite(curvature) ? Math.min(Math.max(curvature, -40), 40) : 0,
          };
        })
        .filter(Boolean);
    }

    parseZonePlacementNumber(value) {
      if (value === undefined || value === null || value === '') return NaN;
      return Number(String(value).replace(',', '.'));
    }

    getZoneState(wrap, variantId = this.getCurrentVariantId()) {
      const input = wrap.querySelector('[data-role="marking-input"]');
      const files = this.getLogoFiles(wrap);
      const fileKeys = files.map((logoFile) => this.getLogoFileKey(logoFile)).filter(Boolean);
      const fileNames = this.getLogoFileNames(wrap);
      const zoneKey = wrap.dataset.zoneKey || '';
      const zoneLabel = wrap.dataset.zoneLabel || '';
      const viewKey = this.getZoneViewKey(wrap, variantId);
      const studioZone = this.getStudioConfigZoneForWrap(wrap, variantId);
      const supplierOffer = this.supplierV2?.offer(zoneKey);
      const supplierPlane = supplierOffer?.supplierPrintPlane;
      const placement = supplierPlane ? { x: 0, y: 0, width: 100, height: 100 } : supplierOffer ? Object.fromEntries(Object.entries(supplierOffer.studioGeometry || supplierOffer.geometry).map(([key, value]) => [key, value * 100]))
        : this.getStudioZonePlacement(studioZone) || this.getZonePlacement(wrap);
      const parts = supplierPlane ? [{ id: `${zoneKey}:supplier-print-plane`, x: 0, y: 0, width: 100, height: 100, shape: 'rectangle', rotation: 0, curvature: 0, supplierPrintPlane: true }]
        : supplierOffer ? [{ id: zoneKey, ...Object.fromEntries(Object.entries(supplierOffer.geometry).map(([key, value]) => [key, value * 100])), shape: supplierOffer.shape === 'ellipse' ? 'circle' : 'rectangle', rotation: supplierOffer.planeRotationDeg || 0, curvature: 0 }] : this.getStudioZoneParts(studioZone);
      const supplierPhoto = supplierPlane && ['approved', 'calibrated'].includes(supplierOffer.photoProjectionStatus)
        && supplierOffer.photoPlacement && supplierOffer.photoParts?.length
        ? { photoPlacement: supplierOffer.photoPlacement, photoParts: supplierOffer.photoParts.map(part => ({ ...part, id: part.id || `${zoneKey}:supplier-photo` })) } : null;
      const suggestedGeometry = this.getSuggestedStudioPlacement(wrap)?.placement?.geometry;
      const artworks = (files.length ? files : fileNames).map((item, index) => {
        const logoFile = files[index] || null;
        const fileName = logoFile?.name || fileNames[index] || '';
        const fileKey = logoFile ? this.getLogoFileKey(logoFile) : fileKeys[index] || `${zoneKey}|${fileName}|${index}`;
        const fileFormat = this.getStudioFileFormat(logoFile);
        const artworkId = index === 0 ? 'base' : `uploaded-${index}-${this.normalizeStudioKey(fileKey || fileName || String(index)).slice(0, 48)}`;
        const productionSource = logoFile ? this.getArtworkProductionSource(logoFile, zoneKey, { artworkId }) : null;
        const previewUrl = logoFile ? this.getStudioPreviewUrl(logoFile, zoneKey, fileKey, { artworkId }) : '';
        const previewStatus = (logoFile && this.getStudioPreviewRecord(logoFile, zoneKey, fileKey, { artworkId })?.status) || 'unavailable';

        return {
          id: artworkId,
          index,
          fileKey,
          originalIdentity: productionSource?.originalIdentity || '',
          productionFileKey: productionSource?.productionFileKey || '',
          fileName,
          fileType: logoFile?.type || fileFormat.mime || '',
          fileExtension: fileFormat.extension,
          fileCategory: fileFormat.category,
          previewable: Boolean(previewUrl),
          previewUrl,
          previewStatus,
          previewLabel: previewStatus === 'loading' ? this.strings?.previewPreparing : this.strings?.previewUnavailable,
        };
      });
      const baseArtwork = artworks[0] || {};

      return {
        key: zoneKey,
        label: zoneLabel,
        viewKey,
        placementKey: this.getStudioTabPlacementKey(wrap),
        emplacementKey: this.getStudioEmplacement(wrap).key,
        emplacementLabel: this.getStudioEmplacement(wrap).label,
        ...(supplierOffer ? { techniqueId: supplierOffer.techniqueId, maxWidthMm: supplierOffer.maxWidthMm, maxHeightMm: supplierOffer.maxHeightMm, supplierGeometry: true,
          photoMediaId: supplierOffer.mediaId, photoProjectionStatus: supplierOffer.photoProjectionStatus || (supplierPlane ? 'reference_only' : 'calibrated'),
          ...(!supplierPlane ? { photoPlacement: placement, photoParts: parts } : {}),
          ...(supplierPhoto || {}),
          ...(supplierOffer.photoPreviewMode === 'unfolded_360' ? {
            photoPreviewMode: 'unfolded_360', photoPreviewPlacement: supplierOffer.photoPreviewPlacement,
            photoPreviewParts: supplierOffer.photoPreviewParts } : {}),
          ...(supplierPlane ? { supplierPrintPlane: supplierPlane, editingMode: 'supplier_print_plane' } : {}) } : {}),
        ...(placement || {}),
        parts,
        ...(studioZone?.previewSurface ? { previewSurface: studioZone.previewSurface } : {}),
        ...(suggestedGeometry ? { suggestedGeometry } : {}),
        selected: Boolean(input?.checked),
        supportsLogo: wrap.dataset.supportsLogo === 'true',
        hasLogo: fileNames.length > 0,
        fileKey: baseArtwork.fileKey || '',
        originalIdentity: baseArtwork.originalIdentity || '',
        productionFileKey: baseArtwork.productionFileKey || '',
        fileKeys,
        fileName: baseArtwork.fileName || fileNames[0] || '',
        fileNames,
        fileCount: fileNames.length,
        fileType: baseArtwork.fileType || '',
        fileExtension: baseArtwork.fileExtension || '',
        fileCategory: baseArtwork.fileCategory || 'unknown',
        previewable: Boolean(baseArtwork.previewUrl),
        previewUrl: baseArtwork.previewUrl || '',
        previewStatus: baseArtwork.previewStatus || 'unavailable',
        previewLabel: baseArtwork.previewLabel || '',
        artworks,
      };
    }

    getZoneViewKey(wrap, variantId = this.getCurrentVariantId()) {
      if (!wrap) return '';
      if (this.supplierV2) return this.supplierV2.offer(wrap.dataset.zoneKey)?.viewKey || '';

      const mappedViewId = this.getMappedStudioViewIdForWrap(wrap, variantId);
      if (mappedViewId) return this.normalizeStudioKey(mappedViewId);

      const explicitViewKey = this.normalizeStudioKey(wrap.dataset.zoneView || '');
      if (explicitViewKey) return explicitViewKey;

      const key = this.normalizeStudioKey(wrap.dataset.zoneKey || '');
      const label = this.normalizeStudioKey(wrap.dataset.zoneLabel || '');
      const value = this.normalizeStudioKey(`${key} ${label}`);

      if (!value) return '';

      if (/(^|_)(6|back|dos|rear)(_|$)/.test(value)) {
        return 'back';
      }

      if (
        /(^|_)(right_sleeve|sleeve_right|manche_droite|droite_manche|right_manche)(_|$)/.test(value) ||
        value === '4'
      ) {
        return 'right_sleeve';
      }

      if (
        /(^|_)(left_sleeve|sleeve_left|manche_gauche|gauche_manche|left_manche)(_|$)/.test(value) ||
        value === '5'
      ) {
        return 'left_sleeve';
      }

      if (/(^|_)(sleeve|manche)(_|$)/.test(value)) {
        return 'sleeve';
      }

      if (
        /(^|_)(front|face|devant|coeur|heart|anti_coeur|chest|poitrine)(_|$)/.test(value) ||
        value === '1' ||
        value === '2' ||
        value === '3'
      ) {
        return 'front';
      }

      if (/(^|_)(custom|sur_mesure|surmesure)(_|$)/.test(value)) {
        return 'custom';
      }

      return 'front';
    }

    usesRecommendedStudioZones() {
      return Boolean(this.supplierV2 || this.dataset?.recommendedStudioZones === 'true');
    }

    resolveStudioEmplacement({ key = '', label = '', position = '', kind = '', emplacementKey = '', emplacementLabel = '' } = {}) {
      const normalize = value => this.normalizeStudioKey(value).replace(/^source_/, '').replace(/_[a-f0-9]{8,}$/, '');
      if (emplacementKey) return { key: this.normalizeStudioKey(emplacementKey), label: emplacementLabel || label || emplacementKey };
      if (kind === '360') return { key: 'wrap', label: '360°' };
      const keyReference = normalize(key).replace(/^zone_/, '').replace(/^item_/, '');
      const positionReference = normalize(position).replace(/^item_/, '').replace(/_(?:pad|laser|embroidery)$/, '');
      const directionalSubzone = /^(top|bottom|upper|lower|middle|center|centre)(?:_(pad|laser|print|screen))?$/.test(positionReference)
        && /^(front|back|face|dos)_(top|bottom|upper|lower|middle|center|centre)$/.test(keyReference);
      const reference = directionalSubzone ? keyReference : positionReference || keyReference;
      const authoredSurfaceLabel = /^(tranche du jeu|rabat|poche avant|face principale)$/i.test(String(label).trim());
      // These name distinct faces of a base, not an underside with a subzone.
      if (/^base_(?:front|top)$/.test(reference)) return { key: reference, label: label || position || key };
      // A garment front may contain two distinct chest zones; their geometry and IDs stay separate.
      if (/^chest_(?:left|right)$/.test(reference)
        || !positionReference && /^poitrine_(?:gauche|droite)$/.test(this.normalizeStudioKey(label))) return { key: 'front', label: 'Face' };
      if (reference === 'top' && /^(couvercle|bouchon|capuchon)$/i.test(String(label).trim())) {
        return { key: /^couvercle$/i.test(String(label).trim()) ? 'lid' : 'cap', label };
      }
      const sided = reference.match(/^(side_(?:left|right)|(?:left|right)_handed|side_?\d+|(?:chest|sleeve|shoulder_strap)_(?:left|right)|pouch_side_?\d+)(?:_(?:middle|center|centre|up|upper|top|down|lower|bottom|pad|laser))?$/);
      if (sided) {
        const canonical = sided[1].replace(/side(\d)/, 'side_$1');
        const labels = { side_left: 'Côté gauche', side_right: 'Côté droit', left_handed: 'Côté gauche', right_handed: 'Côté droit',
          chest_left: 'Poitrine gauche', chest_right: 'Poitrine droite', sleeve_left: 'Manche gauche', sleeve_right: 'Manche droite',
          shoulder_strap_left: 'Bretelle gauche', shoulder_strap_right: 'Bretelle droite' };
        const authoredSide = /^(left|right)_handed$/.test(canonical) && /^(face|corps)$/i.test(String(label).trim());
        return { key: canonical, label: authoredSide || authoredSurfaceLabel ? label : labels[canonical] || label.replace(/\s*[—–-]\s*zone.*$/i, '') || position || key };
      }
      const match = reference.match(/^(front|face|back|rear|dos|cap|lid|bouchon|couvercle|bottom|base|top|side|left|right|wrap|360)(?:_(middle|center|centre|up|upper|top|haut|down|lower|bottom|bas|left|right|gauche|droite))?$/)
        || reference.match(/^(cap|lid)_(?:pad|laser)$/);
      if (match) {
        const canonical = ({ face: 'front', rear: 'back', dos: 'back', bouchon: 'cap', couvercle: 'lid', base: 'bottom', 360: 'wrap' })[match[1]] || match[1];
        const names = { front: 'Face', back: 'Dos', cap: 'Bouchon', lid: 'Couvercle', bottom: 'Dessous', top: 'Dessus',
          side: 'Côté', left: 'Côté gauche', right: 'Côté droit', wrap: '360°' };
        const authoredTop = canonical === 'top' && /^(couvercle|bouchon|capuchon)$/i.test(String(label).trim());
        return { key: canonical, label: authoredTop || authoredSurfaceLabel ? label : names[canonical] };
      }
      // Pockets, gussets and unknown supplier positions retain their own identity.
      return { key: this.normalizeStudioKey(key || position || label), label: label || position || key };
    }

    getStudioEmplacement(wrap) {
      const zoneKey = wrap?.dataset.zoneKey || '';
      const offer = this.supplierV2?.offer(zoneKey);
      const zone = this.supplierV2?.zone?.(zoneKey);
      return this.resolveStudioEmplacement({ key: zoneKey, label: wrap?.dataset.zoneLabel || zone?.label || '',
        position: offer?.supplierPrintPlane?.supplierPositionId || offer?.editPlane?.supplierPositionId || offer?.supplierPositionId || zone?.supplierPositionId || '',
        kind: offer?.kind || '', emplacementKey: wrap?.dataset.emplacementKey || zone?.emplacementKey || '',
        emplacementLabel: wrap?.dataset.emplacementLabel || zone?.emplacementLabel || '' });
    }

    getStudioZoneRecommendationRank(zone) {
      const key = this.normalizeStudioKey(`${zone?.key || ''} ${zone?.label || ''}`);
      if (/(^|_)(middle|center|centre)(_|$)/.test(key)) return 0;
      if (/(^|_)(left|right|gauche|droite)(_|$)/.test(key)) return 1;
      if (/(^|_)(up|upper|top|haut)(_|$)/.test(key)) return 1;
      if (/(^|_)(down|lower|bottom|bas)(_|$)/.test(key)) return 2;
      return 0;
    }

    getRecommendedStudioZoneKeys(zones) {
      const recommended = new Set(), placements = new Map();
      const ordered = zones.map((zone, index) => ({ zone, index })).sort((a, b) =>
        Number(Boolean(b.zone.hasLogo)) - Number(Boolean(a.zone.hasLogo))
        || Number(b.zone.key === this.activeZoneKey) - Number(a.zone.key === this.activeZoneKey)
        || this.getStudioZoneRecommendationRank(a.zone) - this.getStudioZoneRecommendationRank(b.zone) || a.index - b.index);
      for (const { zone } of ordered) {
        if (!zone?.key || zone.supportsLogo === false) continue;
        const emplacement = zone.emplacementKey || zone.placementKey || zone.key;
        const keys = placements.get(emplacement) || new Set();
        if (keys.has(zone.key) || keys.size >= 2) continue;
        keys.add(zone.key); placements.set(emplacement, keys); recommended.add(zone.key);
      }
      return recommended;
    }

    getStudioTabItems(wraps) {
      const placementKeys = new Set();
      if (this.usesRecommendedStudioZones()) {
        const states = wraps.map(wrap => this.getZoneState(wrap));
        const recommended = this.getRecommendedStudioZoneKeys(states);
        const groups = new Map();
        wraps.forEach((wrap, index) => {
          const state = states[index];
          const descriptor = this.getStudioEmplacement(wrap);
          const key = state.emplacementKey || descriptor.key;
          if (!key) return;
          if (!groups.has(key)) groups.set(key, { key, label: state.emplacementLabel || descriptor.label, zones: [] });
          groups.get(key).zones.push({ wrap, zoneKey: state.key, viewKey: state.viewKey, label: state.label,
            recommended: recommended.has(state.key), hasLogo: Boolean(state.hasLogo) });
        });
        return [...groups.values()].map(group => {
          const savedChoice = this.studioEmplacementChoices?.get(`${this.getVariantStateKey()}|${group.key}`);
          const selected = group.zones.find(zone => zone.zoneKey === this.activeZoneKey)
            || group.zones.find(zone => zone.zoneKey === savedChoice) || group.zones.find(zone => zone.hasLogo)
            || group.zones.find(zone => zone.recommended) || group.zones[0];
          return { emplacementKey: group.key, label: group.label, wrap: selected.wrap, viewKey: selected.viewKey,
            placementKey: this.getStudioTabPlacementKey(selected.wrap), zones: group.zones };
        });
      }

      return wraps.reduce((items, wrap) => {
        const viewKey = this.getStudioTabViewKey(wrap);
        const placementKey = this.getStudioTabPlacementKey(wrap);
        if (!viewKey || !placementKey || placementKeys.has(placementKey)) return items;

        placementKeys.add(placementKey);
        items.push({
          viewKey,
          placementKey,
          wrap,
          label: this.getStudioDisplayLabel(wrap, viewKey),
        });
        return items;
      }, []);
    }

    updateStudioSubzoneTabs() {
      // Product pages navigate by emplacement (Face, Dos, etc.). Technical
      // subzones stay in Studio state and on the photo, never in a second row.
      this.studioSubzoneTabs?.remove();
      this.studioSubzoneTabs = null;
      this.querySelectorAll('[data-role="studio-subzone-tabs"], [data-role="studio-subzone-tab"], [data-role="studio-subzone-select"]')
        .forEach(control => control.remove());
    }

    getStudioTabViewKey(wrap) {
      const viewKey = this.getZoneViewKey(wrap);
      if (!viewKey) return '';
      return this.normalizeStudioKey(viewKey);
    }

    getStudioDisplayLabel(wrap) {
      const explicitLabel = String(wrap?.dataset.zoneLabel || '').trim();
      if (explicitLabel) return explicitLabel;

      const viewKey = this.getStudioTabViewKey(wrap);
      const viewLabels = {
        front: this.strings.viewFront,
        back: this.strings.viewBack,
        right_sleeve: this.strings.viewRightSleeve,
        left_sleeve: this.strings.viewLeftSleeve,
        sleeve: this.strings.viewSleeve,
        custom: this.strings.viewCustom,
      };
      if (viewLabels[viewKey]) return viewLabels[viewKey];
      return wrap?.dataset.zoneLabel || wrap?.dataset.zoneKey || '';
    }

    normalizeStudioKey(value) {
      const input = String(value || '');
      if (STUDIO_KEY_CACHE.has(input)) return STUDIO_KEY_CACHE.get(input);
      const key = input
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/_+/g, '_');
      // Only immutable text is shared; no zone, media or variant state survives
      // a render. Bound both count and input size for arbitrary imported labels.
      if (input.length <= 1024) {
        if (STUDIO_KEY_CACHE.size >= 512) STUDIO_KEY_CACHE.delete(STUDIO_KEY_CACHE.keys().next().value);
        STUDIO_KEY_CACHE.set(input, key);
      }
      return key;
    }

    openLogoInputForZone({ zoneKey, variantId } = {}) {
      const variant = String(this.getVariantStateKey());
      const wrap = this.getZoneWrap(zoneKey);
      const source = wrap?.querySelector('[data-role="logo-file-input"]');
      const available = () => this.isConnected && String(this.getVariantStateKey()) === variant &&
        this.optionWraps.includes(wrap) && !wrap.hidden && this.wrapAppliesToVariant(wrap) &&
        wrap.dataset.supportsLogo === 'true' && !wrap.querySelector('[data-role="marking-input"]')?.disabled;
      if (String(variantId) !== variant || !source || !available()) return false;
      if (this.isStudioDesignBlockedForWrap(wrap)) {
        this.updateStudioExclusivityNotice(this.getStudioExclusivityBlocker(wrap));
        return false;
      }

      this.scopedLogoImportController?.abort();
      const controller = new AbortController();
      this.scopedLogoImportController = controller;
      const previousFiles = [...this.getLogoFiles(wrap)];
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = source.accept;
      input.multiple = true;
      input.className = 'visually-hidden';
      input.dataset.zoneImportFileInput = '';
      input.setAttribute('aria-hidden', 'true');
      let focusTimer;
      const cleanup = () => controller.abort();
      controller.signal.addEventListener('abort', () => {
        window.clearTimeout(focusTimer);
        input.remove();
        if (this.scopedLogoImportController === controller) this.scopedLogoImportController = null;
      }, { once: true });
      input.addEventListener('cancel', cleanup, { once: true, signal: controller.signal });
      input.addEventListener('change', () => {
        const files = [...input.files];
        const currentFiles = this.getLogoFiles(wrap);
        const unchanged = currentFiles.length === previousFiles.length &&
          currentFiles.every((file, index) => file === previousFiles[index]);
        if (files.length && available() && unchanged && this.validateArtworkFiles(files)) {
          const merged = this.mergeLogoFileLists(previousFiles, files);
          if (this.setLogoFilesForWrap(wrap, merged)) {
            this.pendingLogoAppend = null;
            this.handleLogoFileChange(source, { openMobileEditor: true });
          }
        }
        cleanup();
      }, { once: true, signal: controller.signal });
      window.addEventListener('focus', () => {
        focusTimer = window.setTimeout(() => { if (!input.files?.length) cleanup(); }, 600);
      }, { once: true, signal: controller.signal });
      this.append(input);
      input.click();
      return true;
    }

    openActiveLogoInput(options = {}) {
      if (!this.activeZoneWrap && this.studioZoneWraps?.length) {
        this.setActiveStudioZone(this.studioZoneWraps[0].dataset.zoneKey, { selectInput: false });
      }

      if (options.openPanel) {
        this.drawer?.setOpen(true);
      }

      const fileInput = this.activeZoneWrap?.querySelector('[data-role="logo-file-input"]');
      if (!fileInput) return false;
      if (this.isStudioDesignBlockedForWrap(this.activeZoneWrap)) {
        this.updateStudioExclusivityNotice(this.getStudioExclusivityBlocker(this.activeZoneWrap));
        return false;
      }

      const currentFiles = this.getLogoFiles(this.activeZoneWrap);
      if (options.append && currentFiles.length) {
        this.openAppendLogoInput(fileInput, currentFiles);
        return;
      }

      this.pendingLogoAppend = null;
      fileInput.disabled = false;
      fileInput.click();
    }

    openAppendLogoInput(fileInput, currentFiles = []) {
      if (!fileInput || !currentFiles.length || !this.activeZoneWrap) return;

      const targetWrap = this.activeZoneWrap;
      const appendInput = document.createElement('input');
      appendInput.type = 'file';
      appendInput.accept = fileInput.getAttribute('accept') || '';
      appendInput.multiple = true;
      appendInput.className = 'visually-hidden';
      appendInput.setAttribute('aria-hidden', 'true');

      const cleanup = () => {
        appendInput.remove();
      };

      appendInput.addEventListener(
        'change',
        () => {
          const selectedFiles = Array.from(appendInput.files || []).filter(Boolean);
          if (!selectedFiles.length) {
            cleanup();
            return;
          }
          if (!this.validateArtworkFiles(selectedFiles)) {
            cleanup();
            return;
          }

          const optionWrap = targetWrap;
          const input = optionWrap?.querySelector('[data-role="marking-input"]');
          const mergedFiles = this.mergeLogoFileLists(currentFiles, selectedFiles);
          if (!optionWrap) {
            cleanup();
            return;
          }

          if (!this.setLogoFilesForWrap(optionWrap, mergedFiles)) {
            cleanup();
            this.syncUI();
            return;
          }
          if (input) input.checked = true;
          this.rememberRecentLogos(selectedFiles);
          this.setActiveStudioZone(optionWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
          this.storeCurrentVariantLogoState();
          this.syncUI();
          this.requestMobileDesignEditorForFile(optionWrap, selectedFiles[0]);
          cleanup();
        },
        { once: true },
      );

      appendInput.addEventListener('cancel', cleanup, { once: true });
      window.addEventListener(
        'focus',
        () => {
          window.setTimeout(() => {
            if (appendInput.isConnected && !appendInput.files?.length) cleanup();
          }, 600);
        },
        { once: true },
      );
      this.append(appendInput);
      appendInput.click();
    }

    mergeLogoFileLists(currentFiles = [], selectedFiles = []) {
      return [...currentFiles, ...selectedFiles].filter(Boolean);
    }

    clearActiveLogo() {
      this.clearLogoForWrap(this.activeZoneWrap);
    }

    getZoneWrap(zoneKey) {
      const normalizedZoneKey = String(zoneKey || '').trim();
      if (!normalizedZoneKey) return null;

      return (
        this.optionWraps.find((wrap) => wrap.dataset.zoneKey === normalizedZoneKey) ||
        (this.allOptionWraps || []).find((wrap) => wrap.dataset.zoneKey === normalizedZoneKey) ||
        null
      );
    }

    clearLogoForZone(zoneKey) {
      const optionWrap = this.getZoneWrap(zoneKey) || this.activeZoneWrap;

      if (!optionWrap) return;
      this.setActiveStudioZone(optionWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
      this.clearLogoForWrap(optionWrap);
    }

    clearLogoFileForZone(zoneKey, fileIndex) {
      const optionWrap = this.getZoneWrap(zoneKey) || this.activeZoneWrap;

      if (!optionWrap) return;
      this.setActiveStudioZone(optionWrap.dataset.zoneKey, { selectInput: false, broadcast: false });
      this.clearLogoFileForWrap(optionWrap, fileIndex);
    }

    clearLogoFileForWrap(optionWrap, fileIndex) {
      if (!optionWrap) return;
      const files = this.getLogoFiles(optionWrap);
      const index = Number(fileIndex);

      if (!Number.isInteger(index) || index < 0 || files.length <= 1 || index >= files.length) {
        this.clearLogoForWrap(optionWrap);
        return;
      }

      const removedFile = files[index];
      const nextFiles = files.filter((_, filePosition) => filePosition !== index);
      const input = optionWrap.querySelector('[data-role="marking-input"]');

      if (removedFile) {
        this.rememberRecentLogos([removedFile]);
      }

      if (input) input.checked = true;
      this.setLogoFilesForWrap(optionWrap, nextFiles);
      this.storeCurrentVariantLogoState();
      this.syncUI();
    }

    clearLogoForWrap(optionWrap) {
      if (!optionWrap) return;
      const input = optionWrap.querySelector('[data-role="marking-input"]');
      const currentFiles = this.getLogoFiles(optionWrap);

      if (currentFiles.length) {
        this.rememberRecentLogos(currentFiles);
      }

      if (input) input.checked = false;
      this.setLogoFilesForWrap(optionWrap, []);
      this.storeCurrentVariantLogoState();
      this.syncUI();
    }

    updateStudioTabs() {
      if (!this.studioTabs) return;
      if (this.usesRecommendedStudioZones()) {
        const next = this.getStudioTabItems(this.optionWraps.filter(wrap => wrap.dataset.supportsLogo === 'true'));
        if (JSON.stringify(next.map(item => item.wrap.dataset.zoneKey)) !== JSON.stringify((this.studioTabItems || []).map(item => item.wrap.dataset.zoneKey))) {
          this.setupStudioLight();
          return;
        }
      }

      this.querySelectorAll('[data-role="zone-tab"]').forEach((tab) => {
        const placementKey = tab.dataset.placementKey || '';
        const hasFile = this.optionWraps.some((wrap) => {
          if (placementKey) return this.getStudioTabPlacementKey(wrap) === placementKey && this.wrapHasLogo(wrap);
          return wrap.dataset.zoneKey === tab.dataset.zoneKey && this.wrapHasLogo(wrap);
        });
        tab.classList.toggle('has-file', hasFile);
      });

      this.updateAddDesignVisibility();
    }

    updateAddDesignVisibility() {
      const hasActiveLogo = Boolean(this.activeZoneWrap && this.wrapHasLogo(this.activeZoneWrap));
      const isBlocked = Boolean(this.activeZoneWrap && this.isStudioDesignBlockedForWrap(this.activeZoneWrap));
      const canAddDesign = hasActiveLogo && !isBlocked;
      if (this.studioAddDesign) {
        this.studioAddDesign.hidden = !canAddDesign;
        this.studioAddDesign.disabled = !canAddDesign;
        this.studioAddDesign.setAttribute('aria-hidden', canAddDesign ? 'false' : 'true');
      }
      this.classList.toggle('has-studio-add-design', canAddDesign);
    }

    wrapHasLogo(wrap) {
      return this.getLogoFileNames(wrap).length > 0;
    }

    getLogoFiles(wrap) {
      if (wrap?.dataset.logoCleared === 'true') return [];
      const fileInput = wrap?.querySelector('[data-role="logo-file-input"]');
      return Array.from(fileInput?.files || []).filter(Boolean);
    }

    getLogoFileNames(wrap) {
      if (wrap?.dataset.logoCleared === 'true') return [];
      const files = this.getLogoFiles(wrap);
      if (files.length) {
        return files.map((file) => file.name).filter(Boolean);
      }

      const serializedFileNames = wrap?.dataset.logoFileNames;
      if (serializedFileNames) {
        try {
          const parsed = JSON.parse(serializedFileNames);
          if (Array.isArray(parsed)) {
            return parsed.map((name) => String(name || '').trim()).filter(Boolean);
          }
        } catch (error) {
          // Ignore malformed legacy data and fall back to the single filename below.
        }
      }

      const fileName = String(wrap?.dataset.logoFileName || '').trim();
      return fileName ? [fileName] : [];
    }

    getLogoDisplayName(wrap) {
      const fileNames = this.getLogoFileNames(wrap);
      if (fileNames.length <= 1) return fileNames[0] || '';
      return fileNames.join(', ');
    }

    mergePendingLogoFiles(fileInput) {
      const pending = this.pendingLogoAppend;
      this.pendingLogoAppend = null;

      if (!pending || pending.input !== fileInput || !pending.files?.length || typeof DataTransfer === 'undefined') return;

      const selectedFiles = Array.from(fileInput.files || []);
      if (!selectedFiles.length) return;

      try {
        const transfer = new DataTransfer();
        pending.files.forEach((file) => transfer.items.add(file));
        selectedFiles.forEach((file) => transfer.items.add(file));
        fileInput.files = transfer.files;
      } catch (error) {
        // Some browsers disallow assigning FileList; keep the latest selection in that case.
      }
    }

    getLogoFileKey(file) {
      return [
        file?.name || '',
        file?.size || 0,
        file?.type || '',
        file?.lastModified || 0,
      ].join('|');
    }

    getRecentLogoKey(file) {
      return this.getLogoFileKey(file);
    }

    async getRecentLogoContentKey(file) {
      if (!window.crypto?.subtle || typeof file?.arrayBuffer !== 'function') return this.getRecentLogoKey(file);
      if (!RECENT_LOGO_CONTENT_KEYS.has(file)) {
        const key = file.arrayBuffer()
          .then((bytes) => window.crypto.subtle.digest('SHA-256', bytes))
          .then((digest) => `sha256:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')}`)
          .catch(() => this.getRecentLogoKey(file));
        RECENT_LOGO_CONTENT_KEYS.set(file, key);
      }
      return RECENT_LOGO_CONTENT_KEYS.get(file);
    }

    async prepareRecentLogoStorageItems(items = []) {
      if (!Array.isArray(items)) return [];
      const keyedItems = await Promise.all(items.map(async (item) => {
        if (!this.isRecentLogoFile(item?.file)) return null;
        return { ...item, key: await this.getRecentLogoContentKey(item.file) };
      }));
      return this.normalizeRecentLogoStorageItems(keyedItems);
    }

    getRecentLogoStore() {
      window.TodayMerchRecentLogoStore = window.TodayMerchRecentLogoStore || {
        items: null,
        loading: null,
      };
      return window.TodayMerchRecentLogoStore;
    }

    isRecentLogoFile(file) {
      return typeof File === 'undefined' ? Boolean(file) : file instanceof File;
    }

    createRecentLogoId() {
      this.recentLogoSequence += 1;
      return `recent-logo-${Date.now().toString(36)}-${this.recentLogoSequence}-${Math.random().toString(36).slice(2, 8)}`;
    }

    normalizeRecentLogoStorageItems(items = []) {
      if (!Array.isArray(items)) return [];
      const seen = new Set();

      return items
        .map((item) => {
          const file = item?.file;
          if (!this.isRecentLogoFile(file)) return null;

          const format = this.getStudioFileFormat(file);
          return {
            id: String(item.id || this.createRecentLogoId()),
            key: String(item.key || this.getRecentLogoKey(file)),
            file,
            previewFile: this.isRecentLogoFile(item.previewFile) ? item.previewFile : null,
            vectorizedFile: this.isRecentLogoFile(item.vectorizedFile) ? item.vectorizedFile : null,
            productionFile: this.isRecentLogoFile(item.productionFile) ? item.productionFile : null,
            productionKind: ['vectorized', 'palette', 'adapted'].includes(item.productionKind) ? item.productionKind : '',
            fileName: String(item.fileName || file.name || this.strings.logoFallback),
            fileCategory: item.fileCategory || format.category,
            storedAt: Number(item.storedAt || Date.now()),
          };
        })
        .filter(Boolean)
        .sort((left, right) => right.storedAt - left.storedAt)
        .filter((item) => {
          if (seen.has(item.key)) return false;
          seen.add(item.key);
          return true;
        })
        .slice(0, RECENT_LOGO_LIMIT);
    }

    getRecentLogoStorageItems() {
      if (Array.isArray(this.recentLogoStore?.items)) {
        return this.recentLogoStore.items.slice();
      }

      return (this.recentLogos || []).map((item) => ({
        id: item.id,
        key: item.key,
        file: item.file,
        previewFile: item.previewFile || null,
        vectorizedFile: item.vectorizedFile || null,
        productionFile: item.productionFile || null,
        productionKind: item.productionKind || '',
        fileName: item.fileName,
        fileCategory: item.fileCategory,
        storedAt: item.storedAt,
      }));
    }

    setRecentLogosFromStorage(items = []) {
      const normalizedItems = this.normalizeRecentLogoStorageItems(items);
      this.studioPreviewOverrides = this.studioPreviewOverrides || new Map();
      this.studioVectorizedFiles = this.studioVectorizedFiles || new Map();
      normalizedItems.forEach((item) => {
        if (item.previewFile) this.studioPreviewOverrides.set(this.getLogoFileKey(item.file), item.previewFile);
        if (item.vectorizedFile) this.studioVectorizedFiles.set(this.getLogoFileKey(item.file), item.vectorizedFile);
        const productionFile = item.productionFile || item.vectorizedFile;
        if (productionFile) this.setArtworkProductionSourceRecord(item.file, productionFile, item.productionKind || 'vectorized');
      });
      const previousRecords = this.getStudioPreviewRecords();
      this.recentLogos = normalizedItems.map((item) => {
        const sourceFile = this.getStudioPreviewSource(item.file);
        const previewRecord = previousRecords.find((record) => record.file === item.file && record.sourceFile === sourceFile);
        return { ...item, previewRecord, previewUrl: previewRecord?.url || '' };
      });
      // History reordering must not discard decoded images or repeat conversions.
      previousRecords.forEach((record) => this.releaseStudioPreviewUrl(record.url));
    }

    getRecentLogoPreviewUrl(item) {
      if (!item?.file) return '';
      const sourceFile = this.getStudioPreviewSource(item.file);
      if (item.previewRecord?.sourceFile !== sourceFile) {
        const previousUrl = item.previewRecord?.url;
        item.previewRecord = null;
        item.previewUrl = '';
        this.releaseStudioPreviewUrl(previousUrl);
      }
      if (!this.isStudioRenderableFile(sourceFile)) return '';
      item.previewRecord ||= this.findReusableStudioPreviewRecord(item.file, sourceFile)
        || this.createStudioPreviewRecord(item.file, sourceFile);
      item.previewUrl = item.previewRecord.url || '';
      item.previewStatus = item.previewRecord.status;
      return item.previewUrl;
    }

    async loadRecentLogos() {
      const store = this.recentLogoStore || this.getRecentLogoStore();

      if (Array.isArray(store.items)) {
        this.setRecentLogosFromStorage(store.items);
        this.renderRecentLogos();
        return;
      }

      if (!store.loading) {
        store.loading = this.readRecentLogosFromDatabase()
          .then((items) => this.prepareRecentLogoStorageItems(items))
          .then(async (items) => {
            if (!Array.isArray(store.items)) {
              store.items = items;
              await this.saveRecentLogosToDatabase(items);
            }
            return store.items;
          })
          .catch(() => {
            if (!Array.isArray(store.items)) store.items = [];
            return store.items;
          });
      }

      const items = await store.loading;
      if (!this.initialized) return;

      this.setRecentLogosFromStorage(store.items || items);
      this.renderRecentLogos();
    }

    openRecentLogosDatabase() {
      if (!window.indexedDB) return Promise.resolve(null);

      return new Promise((resolve) => {
        const request = window.indexedDB.open(RECENT_LOGO_DB_NAME, RECENT_LOGO_DB_VERSION);

        request.onupgradeneeded = () => {
          const database = request.result;
          if (!database.objectStoreNames.contains(RECENT_LOGO_STORE_NAME)) {
            database.createObjectStore(RECENT_LOGO_STORE_NAME, { keyPath: 'id' });
          }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
        request.onblocked = () => resolve(null);
      });
    }

    async readRecentLogosFromDatabase() {
      const database = await this.openRecentLogosDatabase();
      if (!database) return [];

      return new Promise((resolve) => {
        const transaction = database.transaction(RECENT_LOGO_STORE_NAME, 'readonly');
        const store = transaction.objectStore(RECENT_LOGO_STORE_NAME);
        const request = store.get(RECENT_LOGO_STATE_ID);

        request.onsuccess = () => {
          resolve(request.result?.items || []);
        };
        request.onerror = () => resolve([]);
        transaction.oncomplete = () => database.close();
        transaction.onerror = () => database.close();
        transaction.onabort = () => database.close();
      });
    }

    async saveRecentLogosToDatabase(items = []) {
      const database = await this.openRecentLogosDatabase();
      if (!database) return;

      await new Promise((resolve) => {
        const transaction = database.transaction(RECENT_LOGO_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(RECENT_LOGO_STORE_NAME);
        store.put({
          id: RECENT_LOGO_STATE_ID,
          items: this.normalizeRecentLogoStorageItems(items),
        });
        transaction.oncomplete = resolve;
        transaction.onerror = resolve;
        transaction.onabort = resolve;
      });

      database.close();
    }

    persistRecentLogoStorageItems(items = []) {
      const normalizedItems = this.normalizeRecentLogoStorageItems(items);
      this.recentLogoStore.items = normalizedItems;
      this.setRecentLogosFromStorage(normalizedItems);
      return this.saveRecentLogosToDatabase(normalizedItems).catch(() => {});
    }

    rememberRecentLogos(files = []) {
      const validFiles = files.filter((file) => this.isRecentLogoFile(file));
      if (!validFiles.length) return;

      const store = this.recentLogoStore || this.getRecentLogoStore();
      const loaded = store.loading || (Array.isArray(store.items) ? Promise.resolve() : this.loadRecentLogos());
      // Imports and removals can overlap while file content or IndexedDB loads.
      // Merge against the latest history and serialize writes to keep all designs.
      store.pending = (store.pending || Promise.resolve()).then(async () => {
        await loaded;
        const keys = await Promise.all(validFiles.map((file) => this.getRecentLogoContentKey(file)));
        const storageItems = this.getRecentLogoStorageItems();

        validFiles.forEach((file, index) => {
          const key = keys[index];
          const existingIndex = storageItems.findIndex((item) => item.key === key);
          const previous = existingIndex > -1 ? storageItems.splice(existingIndex, 1)[0] : null;
          const format = this.getStudioFileFormat(file);
          storageItems.unshift({
            id: previous?.id || this.createRecentLogoId(),
            key,
            file,
            previewFile: this.getStudioPreviewSource(file) !== file ? this.getStudioPreviewSource(file) : null,
            vectorizedFile: this.getArtworkProductionSource(file).kind === 'vectorized' ? this.getArtworkProductionSource(file).file : null,
            productionFile: this.getArtworkProductionSource(file).file !== file ? this.getArtworkProductionSource(file).file : null,
            productionKind: this.getArtworkProductionSource(file).kind,
            fileName: file.name || previous?.fileName || this.strings.logoFallback,
            fileCategory: format.category || previous?.fileCategory || 'unknown',
            storedAt: Date.now(),
          });
        });

        const saved = this.persistRecentLogoStorageItems(storageItems);
        this.renderRecentLogos();
        await saved;
      }).catch(() => {});
      return store.pending;
    }

    renderRecentLogos() {
      if (!this.recentLogosEl || !this.recentLogosList) return;

      const shouldShow = this.shouldShowRecentLogos();
      const isBlocked = Boolean(this.activeZoneWrap && this.isStudioDesignBlockedForWrap(this.activeZoneWrap));
      this.recentLogosEl.hidden = !shouldShow;
      if (this.studioUploadEmpty && this.activeZoneWrap) {
        this.studioUploadEmpty.hidden = isBlocked || (shouldShow && this.productRoot?.dataset.personalizationV2Active !== 'true') || this.wrapHasLogo(this.activeZoneWrap);
      }
      if (!shouldShow) {
        this.recentLogosList.replaceChildren();
        this.recentLogoCards = null;
        return;
      }

      const nextCards = new Map();
      const nodes = [];
      this.recentLogos.slice(0, RECENT_LOGO_LIMIT).forEach((item) => {
        const label = [this.strings.recentLogoAdd, item.fileName].filter(Boolean).join(' : ');
        const previewUrl = this.getRecentLogoPreviewUrl(item);
        const signature = JSON.stringify([label, previewUrl]);
        const existing = this.recentLogoCards?.get(item.id);
        if (existing?.signature === signature) {
          nextCards.set(item.id, existing);
          nodes.push(existing.node);
          return;
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-marking-selector__recent-logo';
        button.dataset.role = 'recent-logo';
        button.dataset.recentLogoId = item.id;
        button.setAttribute('aria-label', label);

        const thumb = document.createElement('span');
        thumb.className = 'product-marking-selector__recent-logo-thumb';
        thumb.setAttribute('aria-hidden', 'true');
        if (previewUrl) {
          const image = document.createElement('img');
          image.src = previewUrl;
          image.alt = '';
          image.width = 44;
          image.height = 44;
          thumb.append(image);
        } else {
          thumb.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8" cy="8" r="1.5"/><path d="m3 17 5-5 4 4 4-6 5 7"/></svg>';
        }

        button.append(thumb);
        nextCards.set(item.id, { signature, node: button });
        nodes.push(button);
      });
      this.reconcileStudioListChildren(this.recentLogosList, nodes);
      this.recentLogoCards = nextCards;
    }

    reconcileStudioListChildren(container, nodes) {
      // Keep unchanged controls attached: replacing a list drops keyboard focus
      // and recreates decoded thumbnails on unrelated configurator updates.
      const retained = new Set(nodes);
      [...container.children].forEach((node) => {
        if (!retained.has(node)) node.remove();
      });
      nodes.forEach((node, index) => {
        const current = container.children[index];
        if (current !== node) container.insertBefore(node, current || null);
      });
    }

    shouldShowRecentLogos() {
      if (!this.recentLogos.length || !this.activeZoneWrap) return false;
      if (this.isStudioDesignBlockedForWrap(this.activeZoneWrap)) return false;
      return !this.wrapHasLogo(this.activeZoneWrap);
    }

    applyRecentLogoToActiveZone(recentLogoId) {
      const recentLogo = this.recentLogos.find((item) => item.id === recentLogoId);
      if (!recentLogo?.file) return;

      if (!this.activeZoneWrap && this.studioZoneWraps?.length) {
        this.setActiveStudioZone(this.studioZoneWraps[0].dataset.zoneKey, { selectInput: false });
      }

      const optionWrap = this.activeZoneWrap;
      const input = optionWrap?.querySelector('[data-role="marking-input"]');
      const fileInput = optionWrap?.querySelector('[data-role="logo-file-input"]');
      if (!optionWrap || !input || !fileInput) return;

      if (!this.setLogoFilesForWrap(optionWrap, [recentLogo.file])) {
        this.syncUI();
        return;
      }
      input.checked = true;
      this.handleLogoFileChange(fileInput, { openMobileEditor: true });
    }

    revokeRecentLogoPreviewUrls() {
      const urls = new Set((this.recentLogos || []).map((item) => item.previewRecord?.url || item.previewUrl).filter(Boolean));
      this.recentLogos = [];
      this.recentLogoPreviewUrls?.clear();
      this.recentLogoPreviewTasks?.clear();
      urls.forEach((url) => this.releaseStudioPreviewUrl(url));
    }

    updateStudioFile() {
      if (!this.studioUploadEmpty || !this.studioFileCard || !this.activeZoneWrap) return;

      const files = this.getLogoFiles(this.activeZoneWrap);
      const fileNames = this.getLogoFileNames(this.activeZoneWrap);
      const zoneLabel = this.activeZoneWrap.dataset.zoneLabel || '';
      const zoneKey = this.activeZoneWrap.dataset.zoneKey || '';

      if (this.studioFileCard) this.studioFileCard.dataset.zoneKey = zoneKey;
      if (this.studioFileRemove) this.studioFileRemove.dataset.zoneKey = zoneKey;

      const uploadLabel = zoneLabel ? `${this.strings.logoDefault} - ${zoneLabel}` : this.strings.logoDefault;
      if (this.studioUploadTitle) {
        this.studioUploadTitle.textContent = this.strings.uploadTitle || (this.staticOpen ? this.strings.logoDefault : uploadLabel);
      }
      if (this.staticOpen) {
        const uploadHelp = this.studioUploadEmpty.querySelector('.product-marking-selector__studio-upload-help')?.textContent?.trim();
        this.studioUploadEmpty.setAttribute('aria-label', [this.strings.uploadTitle || uploadLabel, zoneLabel].filter(Boolean).join(' — '));
        this.studioUploadEmpty.title = [this.strings.uploadTitle || uploadLabel, uploadHelp].filter(Boolean).join(' — ');
      }

      const blockState = this.updateStudioExclusivityNotice();

      if (!fileNames.length) {
        this.studioUploadEmpty.hidden = blockState.blocked || (this.shouldShowRecentLogos() && this.productRoot?.dataset.personalizationV2Active !== 'true');
        this.studioFileCard.hidden = true;
        this.studioFileCard.replaceChildren();
        this.studioFileCards = null;
        if (this.studioFilePreview) {
          this.studioFilePreview.hidden = true;
          this.studioFilePreview.removeAttribute('src');
        }
        if (this.studioFilePlaceholder) this.studioFilePlaceholder.hidden = false;
        return;
      }

      this.studioUploadEmpty.hidden = true;
      this.studioFileCard.hidden = false;

      this.renderStudioFileItems({
        files,
        fileNames,
        zoneKey,
        zoneLabel,
      });
    }

    renderStudioFileItems({ files = [], fileNames = [], zoneKey = '', zoneLabel = '' } = {}) {
      if (!this.studioFileCard) return;

      const cards = [];
      const nextCards = new Map();
      const labels = JSON.stringify(this.strings);

      fileNames.forEach((fileName, index) => {
        const file = files[index] || null;
        const fileKey = file ? this.getLogoFileKey(file) : `${zoneKey}|${fileName}|${index}`;
        const previewUrl = file ? this.getStudioPreviewUrl(file, zoneKey, fileKey) : '';
        const previewRecord = file ? this.getStudioPreviewRecord(file, zoneKey, fileKey) : null;
        const fileFormat = this.getStudioFileFormat(file);
        const isRenderable = this.isStudioRenderableFile(this.getStudioPreviewSource(file, zoneKey));
        const isPreviewLoading = previewRecord?.status === 'loading';
        const isPreviewError = previewRecord?.status === 'error';
        const isPreviewUnavailable = !isPreviewLoading && !previewUrl;
        const isVectorized = Boolean(file && (this.getArtworkProductionSource(file, zoneKey).kind === 'vectorized'
          || this.getStudioVectorizedPreview(file, zoneKey)));
        const key = `${zoneKey}|${index}`;
        const signature = JSON.stringify([fileName, fileKey, previewUrl, fileFormat.category, isRenderable,
          previewRecord?.status, previewRecord?.sourceFormat, previewRecord?.error?.code,
          isVectorized, zoneLabel, this.dataset.manualVectorization, Boolean(this.supplierV2), labels]);
        const existing = this.studioFileCards?.get(key);
        if (existing?.file === file && existing.signature === signature) {
          nextCards.set(key, existing);
          cards.push(existing.node);
          return;
        }
        const card = document.createElement('div');
        card.className = 'product-marking-selector__studio-file-card-item';
        card.dataset.fileIndex = String(index);
        card.classList.toggle('has-document-preview', fileFormat.category === 'document');
        card.classList.toggle('has-renderable-preview', isRenderable);
        card.classList.toggle('is-preview-loading', isPreviewLoading);
        card.classList.toggle('has-preview-error', isPreviewError);

        const thumb = document.createElement('span');
        thumb.className = 'product-marking-selector__studio-file-thumb';
        thumb.setAttribute('aria-hidden', 'true');

        if (previewUrl) {
          const image = document.createElement('img');
          image.className = 'product-marking-selector__studio-file-image';
          image.src = previewUrl;
          image.alt = '';
          image.width = 72;
          image.height = 72;
          thumb.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'product-marking-selector__studio-file-placeholder';
          placeholder.textContent = isPreviewLoading ? '' : this.getStudioFilePlaceholderLabel(file, fileName);
          thumb.append(placeholder);
        }

        const copy = document.createElement('span');
        copy.className = 'product-marking-selector__studio-file-copy';

        const name = document.createElement('span');
        name.className = 'product-marking-selector__studio-file-name';
        name.textContent = fileName;

        const meta = document.createElement('span');
        meta.className = 'product-marking-selector__studio-file-zone';
        meta.setAttribute('role', 'status');
        if (isVectorized && !isPreviewLoading && previewUrl) {
          meta.textContent = this.strings.vectorizedReady;
        } else if (isPreviewLoading) {
          meta.textContent = this.strings.previewPreparing;
        } else if (isPreviewUnavailable) {
          meta.textContent = previewRecord?.error?.code === 'preview_file_too_large'
            ? this.strings.previewTooLarge
            : this.strings.previewUnavailable || this.strings.fileAttached;
        } else if (previewUrl) {
          meta.textContent = previewRecord?.sourceFormat === 'pdf'
            ? this.strings.previewFirstPage
            : this.strings.previewReady;
        } else {
          const designLabel = fileNames.length > 1
            ? this.formatString('designNumber', { NUMBER: index + 1 })
            : '';
          meta.textContent = [designLabel, zoneLabel].filter(Boolean).join(' · ');
        }

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'product-marking-selector__studio-file-remove';
        remove.dataset.role = 'studio-file-remove';
        remove.dataset.zoneKey = zoneKey;
        remove.dataset.fileIndex = String(index);
        const fallbackName = this.formatString('designNumber', { NUMBER: index + 1 });
        remove.setAttribute('aria-label', this.formatString('removeDesign', { NAME: fileName || fallbackName }));
        remove.textContent = '×';

        copy.append(name, meta);
        card.append(thumb, copy, remove);
        if (!this.supplierV2 && this.dataset.manualVectorization !== 'false' && file && /\.(png|jpe?g|webp)$/i.test(file.name || '') && this.strings.vectorize) {
          const vectorize = document.createElement('button');
          vectorize.type = 'button';
          vectorize.className = 'product-marking-selector__vectorize-action';
          vectorize.dataset.role = isVectorized ? 'studio-vectorize-undo' : 'studio-vectorize';
          vectorize.dataset.zoneKey = zoneKey;
          vectorize.dataset.fileIndex = String(index);
          vectorize.textContent = isVectorized ? this.strings.vectorizeUndo : this.strings.vectorize;
          vectorize.setAttribute('aria-label', isVectorized ? this.strings.vectorizeUndo : this.formatString('vectorizeFor', { NAME: fileName }));
          card.append(vectorize);
        }
        if (isPreviewUnavailable && file) {
          const notice = document.createElement('div');
          notice.className = 'product-marking-selector__preview-notice';
          const help = document.createElement('p');
          help.textContent = this.strings.previewHelp;
          const action = document.createElement('button');
          action.type = 'button';
          action.className = 'product-marking-selector__preview-action';
          action.dataset.role = 'studio-add-preview';
          action.dataset.fileIndex = String(index);
          action.dataset.zoneKey = zoneKey;
          action.textContent = this.strings.addPreview;
          action.setAttribute('aria-label', this.formatString('addPreviewFor', { NAME: fileName }));
          notice.append(help, action);
          card.append(notice);
        }
        nextCards.set(key, { signature, file, node: card });
        cards.push(card);
      });

      this.reconcileStudioListChildren(this.studioFileCard, cards);
      this.studioFileCards = nextCards;
    }

    getStudioPreviewUrl(file, zoneKey, fileKey = '', options = {}) {
      if (!file || !zoneKey || typeof URL === 'undefined') return '';

      if (!this.studioPreviewUrls) this.studioPreviewUrls = new Map();
      const sourceFile = this.getStudioPreviewSource(file, zoneKey, options);
      const cacheKey = this.getStudioPreviewCacheKey(zoneKey, this.activeVariantId, fileKey || this.getLogoFileKey(file), options);
      const cached = this.studioPreviewUrls.get(cacheKey);
      if (cached?.file === file && cached.sourceFile === sourceFile) return cached.url || '';

      this.revokeStudioPreviewUrl(zoneKey, fileKey || this.getLogoFileKey(file), options);

      if (!this.isStudioRenderableFile(sourceFile)) return '';

      const shared = this.findReusableStudioPreviewRecord(file, sourceFile);
      const entry = shared ? { ...shared } : this.createStudioPreviewRecord(file, sourceFile);
      this.studioPreviewUrls.set(cacheKey, entry);
      return entry.url || '';
    }

    getStudioPreviewRecords() {
      return [...(this.studioPreviewUrls?.values() || []),
        ...(this.recentLogos || []).map((item) => item.previewRecord).filter(Boolean)];
    }

    findReusableStudioPreviewRecord(file, sourceFile) {
      // Files are immutable: share only the same original and effective File,
      // never a filename or thumbnail with an unrelated production source.
      return this.getStudioPreviewRecords().find((record) => record.file === file && record.sourceFile === sourceFile
        && ((record.status === 'ready' && record.url && Number.isFinite(record.naturalRatio) && record.naturalRatio > 0)
          || (record.status === 'loading' && record.renderToken && record.promise)));
    }

    createStudioPreviewRecord(file, sourceFile) {
      const renderToken = {};
      const entry = {
        file,
        sourceFile,
        url: '',
        status: 'loading',
        error: null,
        naturalRatio: 0,
        sourceFormat: this.getStudioFileFormat(file).extension || '',
        renderToken,
      };
      entry.promise = this.renderStudioFilePreview(sourceFile)
        .then(({ previewUrl, naturalRatio, sourceFormat }) => {
          // A colour/zone can be removed while another still awaits these
          // same immutable bytes. Settle only the surviving scope records.
          const current = this.getStudioPreviewRecords().filter(record => record.renderToken === renderToken);
          if (!current.length) {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            return;
          }

          for (const record of current) {
            record.url = previewUrl || '';
            record.status = record.url ? 'ready' : 'error';
            record.naturalRatio = Number.isFinite(naturalRatio) ? naturalRatio : 0;
            record.sourceFormat = sourceFormat || record.sourceFormat || '';
          }
          this.refreshStudioPreviewConsumers(renderToken);
        })
        .catch((error) => {
          const current = this.getStudioPreviewRecords().filter(record => record.renderToken === renderToken);
          if (!current.length) return;
          for (const record of current) {
            record.status = 'error';
            record.error = error || null;
          }
          this.refreshStudioPreviewConsumers(renderToken);
        });
      return entry;
    }

    refreshStudioPreviewConsumers(renderToken) {
      if (!this.isConnected) return;
      if ([...(this.studioPreviewUrls?.values() || [])].some((record) => record.renderToken === renderToken)) this.syncUI();
      else this.renderRecentLogos();
    }

    getStudioPreviewRecord(file, zoneKey, fileKey = '', options = {}) {
      if (!file || !zoneKey) return null;

      const cacheKey = this.getStudioPreviewCacheKey(zoneKey, this.activeVariantId, fileKey || this.getLogoFileKey(file), options);
      const cached = this.studioPreviewUrls?.get(cacheKey);
      return cached?.file === file && cached.sourceFile === this.getStudioPreviewSource(file, zoneKey, options) ? cached : null;
    }

    getStudioPreviewCacheKey(zoneKey, variantId = this.activeVariantId, fileKey = '', { artworkId = 'base' } = {}) {
      return `${this.getVariantStateKey(variantId)}::${zoneKey || 'zone'}::${fileKey || 'primary'}${artworkId !== 'base' ? `::artwork:${artworkId}` : ''}`;
    }

    getArtworkProductionScope(zoneKey = '', { techniqueId, variantId, artworkId = 'base' } = {}) {
      if (!zoneKey) return null;
      let root = this.productRoot || this.supplierV2?.root;
      if (!root && !this.supplierV2) {
        try { root = this.closest?.('product-info'); } catch (_) { /* Detached source owners have no DOM context. */ }
      }
      const choice = root?.querySelector?.('product-personalization-choice');
      // Read only native intention. A Studio snapshot resolves sources itself.
      const selectedTechnique = String(techniqueId ?? this.supplierV2?.offer?.(zoneKey, variantId ?? this.activeVariantId)?.techniqueId
        ?? (choice?.streamlined && choice.mode === 'none' ? 'L3' : choice?.techniqueId) ?? '').trim();
      if (!selectedTechnique && !this.supplierV2 && !choice && artworkId === 'base') return null;
      const scope = { variantId: this.getVariantStateKey(variantId ?? this.activeVariantId), zoneKey: String(zoneKey), techniqueId: selectedTechnique, artworkId: String(artworkId || 'base') };
      return { ...scope, key: JSON.stringify([scope.variantId, scope.zoneKey, scope.techniqueId, ...(scope.artworkId !== 'base' ? [scope.artworkId] : [])]) };
    }

    getOriginalArtworkProductionSource(file) {
      this.studioOriginalProductionSources ||= new WeakMap();
      let original = this.studioOriginalProductionSources.get(file);
      if (!original) {
        const legacy = this.studioProductionSources?.get(file);
        if (legacy?.file === file && legacy.kind === 'original') original = legacy;
        else {
          this.studioProductionSourceSequence = (this.studioProductionSourceSequence || 0) + 1;
          const revision = this.studioProductionSourceSequence;
          original = Object.freeze({ original: file, file, kind: 'original', revision,
            originalIdentity: legacy?.originalIdentity || `original:${revision}`,
            productionFileKey: `${this.getLogoFileKey(file)}|selection:${revision}` });
        }
        this.studioOriginalProductionSources.set(file, original);
      }
      return original;
    }

    getArtworkProductionBaseSource(file, zoneKey = '', options = {}) {
      if (!file) return { original: file, file, kind: 'original', revision: 0, productionFileKey: '' };
      const scope = this.getArtworkProductionScope(zoneKey, options);
      if (scope) return this.studioScopedProductionSources?.get(file)?.get(scope.key) || this.getOriginalArtworkProductionSource(file);
      // Historical callers remain supported, but explicit technique contexts
      // never inherit their unscoped adaptations or matching file metadata.
      return this.studioProductionSources?.get(file) || this.setArtworkProductionSourceRecord(file, file, 'original');
    }

    getArtworkProductionSource(file, zoneKey = '', options = {}) {
      const scope = this.getArtworkProductionScope(zoneKey, options);
      const current = this.getArtworkProductionScope(zoneKey);
      if (file && zoneKey && (!options.artworkId || options.artworkId === 'base') && scope?.key === current?.key) {
        const scoped = this.supplierV2?.engraving?.source(file, zoneKey);
        if (scoped) return scoped;
      }
      return this.getArtworkProductionBaseSource(file, zoneKey, options);
    }

    async preparePersonalizationResumePreview(file, signal) {
      const root = this.closest?.('product-info') || this.productRoot;
      if (window.TodayMerchProductModules?.has?.(root, 'preparation')) {
        await window.TodayMerchProductModules.ensure(root, 'preparation');
        if (signal?.aborted || !this.isConnected || root?.isConnected === false) throw new Error('resume_preview_changed');
      }
      const decoder = window.TodayMerchArtworkPaletteAdapter;
      if (!decoder?.decodePixels) throw new Error('resume_decoder_unavailable');
      const decoded = await decoder.decodePixels(file, { signal, maxFileBytes: 1500000, previewOnly: true, allowPartialTransparency: true });
      if (decoded.sourceWidth > 8000 || decoded.sourceHeight > 8000 || decoded.sourceWidth * decoded.sourceHeight > 12000000 || signal?.aborted) throw new Error('resume_preview_changed');
      const canvas = document.createElement('canvas'); canvas.width = decoded.width; canvas.height = decoded.height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('resume_canvas_unavailable');
      context.putImageData(new ImageData(new Uint8ClampedArray(decoded.pixels), decoded.width, decoded.height), 0, 0);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      canvas.width = canvas.height = 0;
      if (!blob || signal?.aborted) throw new Error('resume_preview_unavailable');
      const url = URL.createObjectURL(blob), image = new Image();
      try {
        image.src = url; await image.decode();
        if (!image.complete || !image.naturalWidth || !image.naturalHeight || signal?.aborted) throw new Error('resume_preview_unavailable');
        return { url, image, blob, naturalRatio: decoded.sourceWidth / decoded.sourceHeight, sourceFormat: file.type,
          sourceWidth: decoded.sourceWidth, sourceHeight: decoded.sourceHeight, previewOnly: true, resized: decoded.resized };
      } catch (error) { URL.revokeObjectURL(url); throw error; }
    }

    retainPersonalizationDossierAcceptance(originalFile, effectiveFile, { zoneKey, artworkId = 'base', techniqueId, adaptation, originalSha256, effectiveSha256 } = {}) {
      if (!originalFile || !effectiveFile || originalFile === effectiveFile || adaptation?.base !== 'original'
        || adaptation.accepted !== true || !Array.isArray(adaptation.operations)) return null;
      const scope = this.getArtworkProductionScope(zoneKey, { artworkId, techniqueId });
      if (!scope) return null;
      this.personalizationResumeAcceptances ||= new Map();
      let files = this.personalizationResumeAcceptances.get(scope.key);
      if (!files) { files = new WeakMap(); this.personalizationResumeAcceptances.set(scope.key, files); }
      const material = { accepted: true, engine: adaptation.engine, version: adaptation.version, base: 'original', operations: structuredClone(adaptation.operations) };
      const admission = Object.freeze({ productId: String((this.productRoot || this.closest?.('product-info'))?.dataset.productId || ''),
        variantId: String(this.getCurrentVariantId()), zoneKey, artworkId, techniqueId, originalFile, effectiveFile, baseFile: originalFile,
        adaptation: material, originalSha256, effectiveSha256 });
      files.set(effectiveFile, admission);
      return admission;
    }

    getPersonalizationDossierAcceptance(zoneKey, { artworkId = 'base', effectiveFile } = {}) {
      const scope = this.getArtworkProductionScope(zoneKey, { artworkId });
      const original = this.getLogoFiles(this.getZoneWrap(zoneKey)).find(file => {
        const source = this.getArtworkProductionSource(file, zoneKey, { artworkId });
        return source?.file && this.personalizationResumeAcceptances?.get(scope?.key)?.has(effectiveFile || source.file);
      });
      const file = effectiveFile || original && this.getArtworkProductionSource(original, zoneKey, { artworkId }).file;
      const retained = file && this.personalizationResumeAcceptances?.get(scope?.key)?.get(file);
      if (retained?.originalFile === original) return retained;
      if (artworkId !== 'base') return null;
      const root = this.closest?.('product-info') || this.productRoot;
      const owner = this.supplierV2?.engraving || window.TodayMerchArtworkEngraving?.get?.(root);
      return owner?.getPersonalizationDossierAcceptance?.(zoneKey) || null;
    }

    getPersonalizationResumeSelection(zoneKey, { artworkId = 'base', current } = {}) {
      const root = this.closest?.('product-info') || this.productRoot, gallery = this.getMediaGallery();
      const studio = this.getStudioState(), zone = studio.zones?.find(item => item.key === zoneKey);
      const copied = gallery?.getDesignArtworkCopy?.(zoneKey, artworkId);
      const artwork = zone?.artworks?.find(value => value.id === (copied?.sourceUploadId || artworkId));
      const original = current?.selected?.original || this.getLogoFiles(this.getZoneWrap(zoneKey))[artwork?.index || 0];
      if (!zone || !original || !gallery) return null;
      const source = this.getArtworkProductionSource(original, zoneKey, { artworkId }), transform = gallery.getDesignArtworkPlacement(zone, artworkId);
      const choice = root.querySelector('product-personalization-choice');
      const native = choice?.readNativeSelection?.() || choice?.getSelection?.(studio);
      const quote = window.TodayMerchArtworkQuoteController?.get?.(root), context = quote?.context?.();
      const geometry = current?.selected?.dimensions ? { dimensions: current.selected.dimensions, placement: current.selected.placement, techniqueId: current.selected.techniqueId } : this.supplierV2?.readPersonalizationResumeGeometry(zoneKey, transform)
        || choice?.readPersonalizationResumeGeometry?.(zoneKey, transform)
        || context?.entry?.zone?.key === zoneKey && { dimensions: context.dimensions, placement: null };
      const analysis = this.personalizationResumeAnalyses?.get(source.file);
      const colors = analysis?.effectivePalette || analysis?.palette
        || quote && quote.signature === context?.key && (quote.state?.effectivePalette || quote.state?.artworkBinding?.effectivePalette) || [];
      const quantity = root.querySelector('product-quantity-pricing-selector');
      const rawQuantity = quantity?.compactGrid && quantity.selectionMode === 'custom' ? quantity.customInput?.value : root.querySelector('input[name="quantity"]')?.value;
      const allowed = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop'];
      return { productId: String(root.dataset.productId), variantId: String(this.getCurrentVariantId()),
        techniqueId: this.supplierV2?.offer(zoneKey)?.techniqueId || geometry?.techniqueId || native?.techniqueId || '', positionId: zoneKey,
        viewId: geometry?.viewId || zone.viewKey || null, partId: geometry?.partId || zone.partId || null, quantity: Number(rawQuantity),
        productionPriority: root.querySelector('[data-production-priority]:checked')?.value === 'express' ? 'express' : 'standard',
        dimensions: geometry?.dimensions ? { widthMm: geometry.dimensions.widthMm, heightMm: geometry.dimensions.heightMm } : null,
        placement: geometry?.placement || null, renderTransform: transform ? Object.fromEntries(allowed.filter(key => Number.isFinite(transform[key])).map(key => [key, transform[key]])) : null,
        colors: colors.slice() };
    }

    async preparePersonalizationResume({ record, originalFile, effectiveFile, expected, isCurrent, signal }) {
      const root = this.closest?.('product-info') || this.productRoot, gallery = this.getMediaGallery();
      const selection = record?.selection, shaPattern = /^[a-f0-9]{64}$/, uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
      const active = () => !signal?.aborted && this.isConnected && root?.isConnected !== false && isCurrent?.() === true
        && String(this.getCurrentVariantId()) === expected?.variantId;
      if (!active() || !uuidPattern.test(record?.id || '') || !uuidPattern.test(record.scope?.projectId || '') || !record.scope.itemId || !record.scope.designId
        || !Number.isSafeInteger(record.revision) || record.revision < 1 || !shaPattern.test(record.sha256 || '')
        || record.productionReady !== false || selection?.productId !== expected?.productId || selection?.variantId !== expected?.variantId
        || String(root.dataset.productId) !== expected.productId || selection.positionId !== expected.zoneKey || !selection.renderTransform
        || !Number.isSafeInteger(selection.quantity) || selection.quantity < 1 || !['standard', 'express'].includes(selection.productionPriority)
        || !Array.isArray(selection.colors) || !gallery?.preparePersonalizationResumePlacement) throw new Error('resume_context_unavailable');
      const digest = async file => [...new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()))].map(value => value.toString(16).padStart(2, '0')).join('');
      for (const [role, file] of [['original', originalFile], ['effective', effectiveFile]]) {
        const descriptor = record.assets?.[role];
        if (!(file instanceof File) || !descriptor || file.size < 1 || file.size > 1500000 || descriptor.byteLength !== file.size
          || descriptor.fileName !== file.name || descriptor.mimeType !== file.type || !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)
          || !shaPattern.test(descriptor.sha256 || '') || await digest(file) !== descriptor.sha256) throw new Error('resume_file_mismatch');
      }
      const changed = record.assets.original.sha256 !== record.assets.effective.sha256, adaptation = record.adaptation;
      if ((changed || adaptation) && (!adaptation || adaptation.accepted !== true || adaptation.scope !== 'artwork_adaptation'
        || adaptation.originalSha256 !== record.assets.original.sha256 || adaptation.effectiveSha256 !== record.assets.effective.sha256
        || !shaPattern.test(adaptation.baseSha256 || '') || !['original', 'effective'].includes(adaptation.base)
        || adaptation.base === 'original' && adaptation.baseSha256 !== record.assets.original.sha256
        || typeof adaptation.engine !== 'string' || !adaptation.engine || typeof adaptation.version !== 'string' || !adaptation.version
        || !Array.isArray(adaptation.operations) || !adaptation.operations.length || adaptation.operations.length > 32
        || typeof adaptation.actorKey !== 'string' || !adaptation.actorKey || !Number.isFinite(Date.parse(adaptation.acceptedAt)))) throw new Error('resume_adaptation_unavailable');
      const wrap = this.getZoneWrap(expected.zoneKey), input = wrap?.querySelector('[data-role="logo-file-input"]'), marking = wrap?.querySelector('[data-role="marking-input"]');
      const provider = this.supplierV2 || root.querySelector('product-personalization-choice');
      if (!input || !marking || !provider?.preparePersonalizationResumeSelection || typeof DataTransfer === 'undefined') throw new Error('resume_owner_unavailable');
      const transfer = new DataTransfer(); transfer.items.add(originalFile);
      const originalSource = this.getOriginalArtworkProductionSource(originalFile);
      const sourceOptions = { zoneKey: expected.zoneKey, variantId: expected.variantId, techniqueId: selection.techniqueId, forceRevision: true };
      const productionScope = this.getArtworkProductionScope(expected.zoneKey, sourceOptions);
      let preview, providerTicket, engravingTicket, quantityTicket, galleryTicket;
      const before = { files: input.files, disabled: input.disabled, checked: marking.checked, dataset: { ...wrap.dataset },
        activeZoneKey: this.activeZoneKey, activeZoneWrap: this.activeZoneWrap, sequence: this.studioProductionSourceSequence || 0,
        production: this.studioProductionSources?.get(originalFile), scopedProduction: this.studioScopedProductionSources?.get(originalFile), adapted: this.studioProductionAdaptedFiles?.has(effectiveFile),
        variants: this.variantLogoStates, previews: this.studioPreviewUrls, overrides: this.studioPreviewOverrides, vectors: this.studioVectorizedFiles,
        transforms: this.designTransforms, analysis: this.personalizationResumeAnalyses?.get(effectiveFile) };
      let committed = false, disposed = false, mutating = false;
      // The entry's captured key is checked before the synchronous transaction.
      // The transaction itself legitimately changes that key when it restores
      // a different technique. Child owners still check lifetime and variant;
      // no asynchronous preparation is permitted inside this phase.
      const childCurrent = () => mutating ? !signal?.aborted && this.isConnected && root?.isConnected !== false
        && String(this.getCurrentVariantId()) === expected.variantId : active();
      const release = () => { providerTicket?.dispose?.(); engravingTicket?.dispose?.(); quantityTicket?.dispose?.(); if (!committed && preview?.url) URL.revokeObjectURL(preview.url); };
      try {
        if (window.TodayMerchProductModules?.has?.(root, 'preparation')) {
          await window.TodayMerchProductModules.ensure(root, 'preparation');
          if (!active()) throw new Error('resume_context_unavailable');
        }
        // Validate the retained original through the same safe container/SVG
        // decoder, even though only the selected effective file is displayed.
        if (originalFile !== effectiveFile) {
          if (!window.TodayMerchArtworkPaletteAdapter?.decodePixels) throw new Error('resume_decoder_unavailable');
          const original = await window.TodayMerchArtworkPaletteAdapter.decodePixels(originalFile, { signal, maxFileBytes: 1500000, previewOnly: true, allowPartialTransparency: true });
          if (original.sourceWidth > 8000 || original.sourceHeight > 8000 || original.sourceWidth * original.sourceHeight > 12000000) throw new Error('resume_preview_changed');
        }
        preview = await this.preparePersonalizationResumePreview(effectiveFile, signal);
        const analysis = await window.TodayMerchMidoceanArtworkFeasibility?.analyzeFile?.(effectiveFile);
        const palette = analysis?.effectivePalette || analysis?.palette || [];
        if (selection.colors.length && JSON.stringify(selection.colors) !== JSON.stringify(palette)) throw new Error('resume_palette_changed');
        providerTicket = await provider.preparePersonalizationResumeSelection({ selection, record, originalFile, effectiveFile, analysis, expected, image: preview.image, isCurrent: childCurrent, signal });
        const sequence = before.sequence + 1, engraving = Boolean(this.supplierV2 && window.TodayMerchXDArtworkEngraving?.isEngraving(providerTicket.offer));
        const baseFile = engraving ? originalFile : effectiveFile;
        const base = Object.freeze({ original: originalFile, file: baseFile, kind: changed && baseFile !== originalFile ? 'adapted' : 'original', revision: sequence,
          originalIdentity: originalSource.originalIdentity, productionFileKey: `${this.getLogoFileKey(baseFile)}|selection:${sequence}` });
        if (engraving) {
          if (!this.supplierV2.engraving?.prepareResumeSource) throw new Error('resume_engraving_unavailable');
          engravingTicket = await this.supplierV2.engraving.prepareResumeSource({ record, originalFile, effectiveFile, base, offer: providerTicket.offer, zoneKey: expected.zoneKey, preview, isCurrent: childCurrent, signal });
        }
        const quantity = root.querySelector('product-quantity-pricing-selector');
        if (quantity?.preparePersonalizationResumeQuantity) quantityTicket = quantity.preparePersonalizationResumeQuantity(selection.quantity);
        else if (Number(root.querySelector('input[name="quantity"]')?.value ?? selection.quantity) !== selection.quantity) throw new Error('resume_quantity_unavailable');
        const priorities = [...(root.querySelectorAll?.('[data-production-priority]') || [])], priorityState = priorities.map(node => node.checked);
        const priority = priorities.find(node => node.value === selection.productionPriority);
        if (!priority && selection.productionPriority !== 'standard') throw new Error('resume_priority_unavailable');
        galleryTicket = gallery.preparePersonalizationResumePlacement({ variantId: expected.variantId, zoneKey: expected.zoneKey, transform: selection.renderTransform, partId: selection.partId, image: preview.image });
        if (!active()) throw new Error('resume_stale');
        const rollback = () => {
          if (!committed) return;
          this.personalizationResumeSilenced = true; gallery.personalizationResumeSilenced = true;
          input.files = before.files; input.disabled = before.disabled; marking.checked = before.checked;
          for (const key of Object.keys(wrap.dataset)) delete wrap.dataset[key]; Object.assign(wrap.dataset, before.dataset);
          this.activeZoneKey = before.activeZoneKey; this.activeZoneWrap = before.activeZoneWrap; this.studioProductionSourceSequence = before.sequence;
          if (before.production) this.studioProductionSources.set(originalFile, before.production); else this.studioProductionSources?.delete(originalFile);
          if (before.scopedProduction) this.studioScopedProductionSources.set(originalFile, before.scopedProduction);
          else this.studioScopedProductionSources?.delete(originalFile);
          if (!before.adapted) this.studioProductionAdaptedFiles?.delete(effectiveFile);
          this.variantLogoStates = before.variants; this.studioPreviewUrls = before.previews; this.studioPreviewOverrides = before.overrides;
          this.studioVectorizedFiles = before.vectors; this.designTransforms = before.transforms;
          if (before.analysis) this.personalizationResumeAnalyses.set(effectiveFile, before.analysis); else this.personalizationResumeAnalyses?.delete(effectiveFile);
          committed = false;
          // A failing render in one owner must not skip restoration of another
          // owner. Each ticket restores its state before refreshing its view.
          for (const ticket of [galleryTicket, engravingTicket, providerTicket, quantityTicket]) {
            try { ticket?.rollback?.(); } catch (_) { /* Continue restoring the other native owners. */ }
          }
          priorities.forEach((node, index) => { node.checked = priorityState[index]; });
          try { this.syncUI(); gallery.renderDesignStudio?.(); } catch (_) { /* Keep the restored owner state even if a view cannot render. */ }
          this.personalizationResumeSilenced = false; gallery.personalizationResumeSilenced = false;
          try { this.dispatchStudioStateChange?.(); } catch (_) { /* A failed view must not undo restored native state. */ }
        };
        const stillOwns = () => this.getLogoFiles(wrap)[0] === originalFile && this.getArtworkProductionSource(originalFile, expected.zoneKey).file === effectiveFile
          && String(this.getCurrentVariantId()) === expected.variantId;
        return Object.freeze({
          commit: () => {
            if (disposed || committed || !active() || (this.studioProductionSourceSequence || 0) !== before.sequence) return false;
            try {
              // Every owner may fail after its first write. Mark the whole
              // transaction before invoking them so the same rollback covers
              // a partial provider/quantity commit as well as a failed view.
              mutating = true; committed = true; this.personalizationResumeSilenced = true; gallery.personalizationResumeSilenced = true;
              if (providerTicket.commit() !== true || engravingTicket && engravingTicket.commit() !== true || quantityTicket && quantityTicket.commit() !== true) {
                rollback(); return false;
              }
              if (!childCurrent()) throw new Error('resume_stale');
              input.files = transfer.files; input.disabled = false; marking.checked = true;
              this.variantLogoStates = new Map(before.variants); this.studioPreviewUrls = new Map(before.previews);
              this.studioPreviewOverrides = new Map(before.overrides); this.studioVectorizedFiles = new Map(before.vectors); this.designTransforms = new Map(before.transforms);
              if (productionScope) {
                this.studioScopedProductionSources ||= new WeakMap();
                this.studioScopedProductionSources.set(originalFile, new Map(before.scopedProduction));
              }
              const production = this.setArtworkProductionSourceRecord(originalFile, baseFile, base.kind, sourceOptions);
              if (!engravingTicket) {
                const provenance = Object.freeze({ kind: 'personalization-design-journal', id: record.id, revision: record.revision,
                  sha256: record.sha256, originalSha256: record.assets.original.sha256, effectiveSha256: record.assets.effective.sha256 });
                const resumed = Object.freeze({ ...production, kind: changed ? production.kind : 'original',
                  preparation: Object.freeze({ method: 'journal-resume', provenance, requiresProductionReview: true, productionReady: false }) });
                if (productionScope) this.studioScopedProductionSources.get(originalFile).set(productionScope.key, resumed);
                else this.studioProductionSources.set(originalFile, resumed);
                if (!changed && !before.adapted) this.studioProductionAdaptedFiles.delete(effectiveFile);
              }
              const source = engravingTicket?.source || this.getArtworkProductionSource(originalFile, expected.zoneKey, sourceOptions);
              const fileKey = this.getLogoFileKey(originalFile);
              if (!productionScope) {
                if (source.file !== originalFile) this.studioPreviewOverrides.set(fileKey, source.file); else this.studioPreviewOverrides.delete(fileKey);
                this.studioVectorizedFiles.delete(fileKey);
              }
              this.personalizationResumeAnalyses ||= new WeakMap();
              if (analysis) this.personalizationResumeAnalyses.set(source.file, analysis);
              this.studioPreviewUrls.set(this.getStudioPreviewCacheKey(expected.zoneKey, expected.variantId, fileKey),
                { file: originalFile, sourceFile: source.file, url: preview.url, status: 'ready', naturalRatio: preview.naturalRatio, sourceFormat: preview.sourceFormat, promise: null });
              this.activeZoneKey = expected.zoneKey; this.activeZoneWrap = wrap; this.unmarkWrapLogoCleared(wrap); this.syncWrapLogoDataset(wrap);
              priorities.forEach(node => { node.checked = node === priority; });
              const state = this.getStudioState(), zone = state.zones?.find(item => item.key === expected.zoneKey);
              if (galleryTicket.commit({ state, zone }) !== true) throw new Error('resume_gallery_failed');
              this.syncUI(); gallery.renderDesignStudio?.();
              this.retainPersonalizationDossierAcceptance(originalFile, effectiveFile, { zoneKey: expected.zoneKey, artworkId: 'base', techniqueId: selection.techniqueId, adaptation: record.adaptation, originalSha256: record.assets.original.sha256, effectiveSha256: record.assets.effective.sha256 });
              this.personalizationResumeSilenced = false; gallery.personalizationResumeSilenced = false;
              this.dispatchStudioStateChange?.(); quantityTicket?.publish?.();
              if (priority) priority.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            } catch (_) { rollback(); return false; } finally { mutating = false; }
          },
          whenSettled: async () => {
            if (!committed || disposed) return false;
            for (let frame = 0; frame < 180; frame++) {
              await new Promise(resolve => requestAnimationFrame(resolve));
              if (!stillOwns() || signal?.aborted || gallery.designInteraction || gallery.mobileDesignEditor?.gesture) return false;
              const native = this.getPersonalizationResumeSelection(expected.zoneKey), owner = window.TodayMerchArtworkQuoteController?.get?.(root);
              const same = key => JSON.stringify(native?.[key]) === JSON.stringify(selection[key]);
              // These values are synchronous native intention. A later edit
              // belongs to the visitor and must neither be claimed as saved
              // nor overwritten by a timeout rollback.
              if (!['productId', 'variantId', 'techniqueId', 'positionId', 'viewId', 'partId', 'quantity', 'productionPriority', 'renderTransform'].every(same)
                || selection.colors.length && !same('colors')) return false;
              if (frame >= 1 && (!selection.dimensions || same('dimensions')) && (!selection.placement || same('placement'))
                && (!owner || owner.state?.status !== 'pending' && owner.signature === owner.context().key)) {
                return !providerTicket.whenSettled || await providerTicket.whenSettled() === true;
              }
            }
            if (stillOwns()) rollback(); return false;
          },
          rollback,
          dispose: () => { if (disposed) return; disposed = true; release(); },
        });
      } catch (error) { release(); throw error; }
    }

    setArtworkProductionSourceRecord(original, file, kind = 'adapted', options = {}) {
      this.studioProductionSources ||= new WeakMap();
      this.studioProductionAdaptedFiles ||= new WeakSet();
      const scope = this.getArtworkProductionScope(options.zoneKey, options);
      let records;
      if (scope) {
        this.studioScopedProductionSources ||= new WeakMap();
        records = this.studioScopedProductionSources.get(original);
        if (!records) { records = new Map(); this.studioScopedProductionSources.set(original, records); }
      }
      const previous = scope ? records.get(scope.key) : this.studioProductionSources.get(original);
      const sourceKind = file === original ? 'original' : ['vectorized', 'palette'].includes(kind) ? kind : 'adapted';
      if (!options.forceRevision && previous?.file === file && previous.kind === sourceKind && previous.preparation === options.preparation) return previous;
      const identity = this.getOriginalArtworkProductionSource(original).originalIdentity;
      this.studioProductionSourceSequence = (this.studioProductionSourceSequence || 0) + 1;
      const source = Object.freeze({ original, file, kind: sourceKind, revision: this.studioProductionSourceSequence,
        originalIdentity: identity,
        productionFileKey: `${this.getLogoFileKey(file)}|selection:${this.studioProductionSourceSequence}`,
        ...(scope ? { variantId: scope.variantId, zoneKey: scope.zoneKey, techniqueId: scope.techniqueId, artworkId: scope.artworkId } : {}),
        ...(options.preparation ? { preparation: Object.freeze({ ...options.preparation }) } : {}) });
      if (scope) records.set(scope.key, source); else this.studioProductionSources.set(original, source);
      if (file !== original) this.studioProductionAdaptedFiles.add(file);
      return source;
    }

    selectArtworkProductionSource(original, adaptedFile, { kind = 'adapted', zoneKey = '', techniqueId, variantId, artworkId = 'base', preparation, forceRevision = false } = {}) {
      if (!original || adaptedFile && (!(adaptedFile instanceof Blob) || !adaptedFile.size)) return null;
      const file = adaptedFile || original, options = { zoneKey, techniqueId, variantId, artworkId, preparation, forceRevision };
      const scope = this.getArtworkProductionScope(zoneKey, options);
      const previous = this.getArtworkProductionBaseSource(original, zoneKey, options);
      const source = this.setArtworkProductionSourceRecord(original, file, kind, options);
      if (source === previous && this.getStudioPreviewSource(original, zoneKey, options) === file) return source;
      const fileKey = this.getLogoFileKey(original);
      // Legacy thumbnails are indexed by metadata. Scoped production choices
      // must never write there: another face or technique may use the same File.
      if (scope) {
        this.studioScopedPreviewOverrides?.get(original)?.delete(scope.key);
        this.studioScopedVectorizedPreviews?.get(original)?.delete(scope.key);
      }
      if (!scope) {
        this.studioPreviewOverrides ||= new Map();
        this.studioVectorizedFiles ||= new Map();
        if (file === original) this.studioPreviewOverrides.delete(fileKey);
        else this.studioPreviewOverrides.set(fileKey, file);
        if (source.kind === 'vectorized') this.studioVectorizedFiles.set(fileKey, file);
        else this.studioVectorizedFiles.delete(fileKey);
      }
      this.revokeStudioPreviewUrls();
      this.rememberRecentLogos([original]);
      this.syncUI();
      this.dispatchEvent(new CustomEvent('todaymerch:artwork-production-source-change', { bubbles: true,
        detail: { ...source, fileKey, zoneKey } }));
      return source;
    }

    cloneArtworkProductionSource(original, record, { zoneKey = '', artworkId, fromArtworkId = 'base' } = {}) {
      if (!record || record.original !== original || !artworkId || artworkId === fromArtworkId) return null;
      const admission = this.getPersonalizationDossierAcceptance?.(zoneKey, { artworkId: fromArtworkId });
      const source = this.selectArtworkProductionSource(original, record.file, { zoneKey, artworkId, kind: record.kind, preparation: record.preparation });
      if (source && admission?.originalFile === original && admission.effectiveFile === source.file) this.retainPersonalizationDossierAcceptance(original, source.file, { ...admission, artworkId });
      if (source) this.dispatchEvent(new CustomEvent('todaymerch:artwork-source-copied', { bubbles: true,
        detail: { zoneKey, artworkId, fromArtworkId, originalFile: original, effectiveFile: source.file, variantId: String(this.activeVariantId) } }));
      return source;
    }

    restoreArtworkProductionSource(original, record, { zoneKey = '', artworkId = 'base' } = {}) {
      const current = this.getArtworkProductionScope(zoneKey, { artworkId });
      if (!record || record.original !== original || !(record.file instanceof Blob) || !record.file.size
        || record.variantId && record.variantId !== current?.variantId || record.zoneKey && record.zoneKey !== zoneKey
        || record.artworkId && record.artworkId !== artworkId) return null;
      // History restores immutable artwork bytes, never an old supplier quote.
      // A new source revision invalidates all analysis and price consumers.
      return this.selectArtworkProductionSource(original, record.file, { zoneKey, artworkId,
        kind: record.kind, preparation: record.preparation, forceRevision: true });
    }

    getStudioPreviewSource(file, zoneKey = '', options = {}) {
      const source = this.getArtworkProductionSource(file, zoneKey, options);
      if (source.kind !== 'original') return source.file;
      const scope = this.getArtworkProductionScope(zoneKey, options);
      const auxiliary = scope ? this.studioScopedPreviewOverrides?.get(file)?.get(scope.key) : this.studioPreviewOverrides?.get(this.getLogoFileKey(file));
      return auxiliary && !this.studioProductionAdaptedFiles?.has(auxiliary) ? auxiliary : file;
    }

    getStudioVectorizedPreview(file, zoneKey = '', options = {}) {
      if (!file) return null;
      const scope = this.getArtworkProductionScope(zoneKey, options);
      const preview = scope ? this.studioScopedVectorizedPreviews?.get(file)?.get(scope.key)
        : this.studioVectorizedFiles?.get(this.getLogoFileKey(file));
      return preview && this.getStudioPreviewSource(file, zoneKey, options) === preview ? preview : null;
    }

    selectArtworkVectorizedPreview(original, preview, options = {}) {
      if (!original) return null;
      const zoneKey = options.zoneKey || '';
      const scope = this.getArtworkProductionScope(zoneKey, options);
      // Manual vectorization remains an auxiliary preview. It cannot replace
      // the effective production file or inherit another placement's preview.
      const fileKey = this.getLogoFileKey(original);
      let previews, vectors, key;
      if (scope) {
        this.studioScopedPreviewOverrides ||= new WeakMap();
        this.studioScopedVectorizedPreviews ||= new WeakMap();
        previews = this.studioScopedPreviewOverrides.get(original);
        vectors = this.studioScopedVectorizedPreviews.get(original);
        if (!previews) { previews = new Map(); this.studioScopedPreviewOverrides.set(original, previews); }
        if (!vectors) { vectors = new Map(); this.studioScopedVectorizedPreviews.set(original, vectors); }
        key = scope.key;
      } else {
        previews = this.studioPreviewOverrides ||= new Map();
        vectors = this.studioVectorizedFiles ||= new Map();
        key = fileKey;
      }
      if (preview) { vectors.set(key, preview); previews.set(key, preview); }
      else {
        if (previews.get(key) === vectors.get(key)) previews.delete(key);
        vectors.delete(key);
      }
      this.revokeStudioPreviewUrls();
      this.rememberRecentLogos([original]);
      this.syncUI();
      return preview || null;
    }

    openStudioPreviewInput(zoneKey, fileIndex) {
      const wrap = this.optionWraps.find((item) => item.dataset.zoneKey === zoneKey);
      const original = this.getLogoFiles(wrap)[fileIndex];
      if (!original) return;
      const scope = this.getArtworkProductionScope(zoneKey);
      this.pendingStudioPreviewInput?.remove();
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.png,.jpg,.jpeg,.svg,.webp,.pdf';
      input.className = 'visually-hidden';
      input.setAttribute('aria-label', this.formatString('addPreviewFor', { NAME: original.name }));
      const cleanup = () => {
        input.remove();
        if (this.pendingStudioPreviewInput === input) this.pendingStudioPreviewInput = null;
      };
      input.addEventListener('cancel', cleanup, { once: true });
      input.addEventListener('change', () => {
        const preview = input.files?.[0];
        const originalKey = this.getLogoFileKey(original);
        if (preview && this.getLogoFiles(wrap).includes(original) && scope?.key === this.getArtworkProductionScope(zoneKey)?.key) {
          if (scope) {
            this.studioScopedPreviewOverrides ||= new WeakMap();
            let previews = this.studioScopedPreviewOverrides.get(original);
            if (!previews) { previews = new Map(); this.studioScopedPreviewOverrides.set(original, previews); }
            previews.set(scope.key, preview);
          } else this.studioPreviewOverrides.set(originalKey, preview);
          this.revokeStudioPreviewUrls();
          this.rememberRecentLogos([original]);
          this.syncUI();
          this.requestMobileDesignEditorForFile(wrap, original);
        }
        cleanup();
      }, { once: true });
      this.pendingStudioPreviewInput = input;
      this.append(input);
      input.click();
    }

    async waitForStudioPreviews(zones) {
      const readyKeys = new Set();
      const pending = [];
      for (const zone of zones) {
        for (const file of zone.files) {
          this.getStudioPreviewUrl(file, zone.key);
          const record = this.getStudioPreviewRecord(file, zone.key);
          if (record?.promise) pending.push(record.promise);
        }
      }
      try {
        await this.withQuoteAssetTimeout(Promise.allSettled(pending), 15000);
      } catch (error) {
        // The original files remain available even when an image cannot be prepared in time.
      }
      for (const zone of zones) {
        if (zone.files.every((file) => this.getStudioPreviewRecord(file, zone.key)?.status === 'ready')) {
          readyKeys.add(zone.key);
        }
      }
      this.syncUI();
      return readyKeys;
    }

    getStudioFileFormat(file) {
      if (!file) {
        return {
          extension: '',
          mime: '',
          category: 'unknown',
          previewable: false,
        };
      }

      const fileName = String(file.name || '');
      const extensionIndex = fileName.lastIndexOf('.');
      const extension = extensionIndex > -1 ? fileName.slice(extensionIndex + 1).toLowerCase() : '';
      const mime = String(file.type || '').toLowerCase();
      const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'svg', 'webp', 'avif', 'gif']);
      const vectorExtensions = new Set(['svg']);
      const documentExtensions = new Set(['pdf', 'ai', 'eps', 'psd', 'psb']);
      const isMimeImage = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/avif', 'image/gif']).has(mime);
      const isPreviewable = isMimeImage || imageExtensions.has(extension);

      if (vectorExtensions.has(extension) || mime === 'image/svg+xml') {
        return {
          extension,
          mime: mime || 'image/svg+xml',
          category: 'vector',
          previewable: true,
        };
      }

      if (isPreviewable) {
        return {
          extension,
          mime: mime || `image/${extension === 'jpg' ? 'jpeg' : extension}`,
          category: 'raster',
          previewable: true,
        };
      }

      return {
        extension,
        mime,
        category: documentExtensions.has(extension) || mime === 'application/pdf' ? 'document' : 'unknown',
        previewable: false,
      };
    }

    isStudioPreviewableImage(file) {
      return this.getStudioFileFormat(file).previewable;
    }

    isStudioRenderableFile(file) {
      if (!file) return false;

      const format = this.getStudioFileFormat(file);
      return this.isPdfFile(file) || this.isStudioPreviewableImage(file) || ['ai', 'psd'].includes(format.extension);
    }

    getStudioFilePlaceholderLabel(file, fileName = '') {
      const format = this.getStudioFileFormat(file);
      const extension = format.extension || String(fileName || '').split('.').pop() || '';
      return extension ? extension.slice(0, 4).toUpperCase() : '';
    }

    isPdfFile(file) {
      if (!file) return false;

      const fileName = String(file.name || '');
      const mime = String(file.type || '').toLowerCase();
      return mime === 'application/pdf' || /\.pdf$/i.test(fileName);
    }

    async renderStudioFilePreview(file) {
      if (file?.size > STUDIO_PREVIEW_MAX_BYTES) {
        throw Object.assign(new Error('Preview file is too large'), { code: 'preview_file_too_large' });
      }
      const format = this.getStudioFileFormat(file);
      if (format.extension === 'psd') return this.renderPsdFilePreview(file);
      if (format.extension === 'ai') {
        if (await this.isPdfCompatibleArtwork(file)) return this.renderPdfFilePreview(file);
        throw new Error('This Illustrator file has no compatible PDF preview');
      }
      if (this.isPdfFile(file)) return this.renderPdfFilePreview(file);
      if (this.isStudioPreviewableImage(file)) return this.renderImageFilePreview(file);

      throw new Error('This file format does not support local preview');
    }

    async isPdfCompatibleArtwork(file) {
      if (!file?.slice) return false;
      const header = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
      return /%PDF-\d\.\d/.test(String.fromCharCode(...header));
    }

    async renderPsdFilePreview(file) {
      const moduleUrl = this.dataset.psdPreviewUrl;
      const workerUrl = this.dataset.psdWorkerUrl;
      if (!moduleUrl || !workerUrl) throw new Error('PSD preview assets are missing');
      if (!this.psdPreviewModulePromise) {
        this.psdPreviewModulePromise = import(moduleUrl).catch((error) => {
          this.psdPreviewModulePromise = null;
          throw error;
        });
      }
      const { decodePsdPreview } = await this.psdPreviewModulePromise;
      const result = await decodePsdPreview(file, { workerUrl, maxSide: STUDIO_PREVIEW_MAX_SIDE });
      return { previewUrl: URL.createObjectURL(result.blob), naturalRatio: result.naturalRatio, sourceFormat: result.sourceFormat };
    }

    async renderImageFilePreview(file) {
      if (!file || typeof URL === 'undefined' || typeof Image === 'undefined' || typeof document === 'undefined') {
        throw new Error('Image preview is not available in this browser');
      }

      const format = this.getStudioFileFormat(file);
      const sourceUrl = URL.createObjectURL(file);
      const image = new Image();
      image.decoding = 'async';
      let retainedSourceUrl = false;

      try {
        const imageReady = new Promise((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error('Image preview could not be loaded'));
        });
        image.src = sourceUrl;

        if (typeof image.decode === 'function') {
          await image.decode().catch(() => imageReady);
        } else {
          await imageReady;
        }

        const naturalWidth = image.naturalWidth || image.width || 0;
        const naturalHeight = image.naturalHeight || image.height || 0;
        if (!naturalWidth || !naturalHeight || naturalWidth * naturalHeight > 40000000) throw new Error('Image preview has invalid or excessive dimensions');

        // Already bounded static rasters can use the decoded source directly.
        // Keep vectors and animations on the existing flattened preview path.
        if (Math.max(naturalWidth, naturalHeight) <= STUDIO_PREVIEW_MAX_SIDE && await this.isStaticStudioRaster(file)) {
          retainedSourceUrl = true;
          return { previewUrl: sourceUrl, naturalRatio: naturalWidth / naturalHeight,
            sourceFormat: format.extension || format.mime || 'image' };
        }

        const canUpscale = format.category === 'vector';
        const maxSourceSide = Math.max(naturalWidth, naturalHeight, 1);
        const targetMaxSide = canUpscale ? STUDIO_PREVIEW_MAX_SIDE : Math.min(STUDIO_PREVIEW_MAX_SIDE, maxSourceSide);
        const scale = Math.min(STUDIO_PREVIEW_MAX_SIDE / maxSourceSide, targetMaxSide / maxSourceSide);
        const canvasWidth = Math.max(1, Math.round(naturalWidth * scale));
        const canvasHeight = Math.max(1, Math.round(naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const context = canvas.getContext('2d', { alpha: true });
        if (!context) throw new Error('Image preview canvas is not available');

        context.clearRect(0, 0, canvasWidth, canvasHeight);
        context.drawImage(image, 0, 0, canvasWidth, canvasHeight);

        const blob = await new Promise((resolve) => {
          canvas.toBlob((result) => resolve(result), 'image/png');
        });
        if (!blob) throw new Error('Image preview export failed');

        return {
          previewUrl: URL.createObjectURL(blob),
          naturalRatio: canvasWidth / Math.max(canvasHeight, 1),
          sourceFormat: format.extension || format.mime || 'image',
        };
      } finally {
        if (!retainedSourceUrl) URL.revokeObjectURL(sourceUrl);
      }
    }

    async isStaticStudioRaster(file) {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return false;
      const bytes = new Uint8Array(await file.arrayBuffer());
      const view = new DataView(bytes.buffer);
      if (file.type === 'image/jpeg') return bytes.length > 3 && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
      if (file.type === 'image/png') {
        if (bytes.length < 33 || ![137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value)
          || view.getUint32(8) !== 13 || view.getUint32(12) !== 0x49484452) return false;
        for (let offset = 8; offset + 12 <= bytes.length;) {
          const size = view.getUint32(offset), kind = view.getUint32(offset + 4);
          if (size > bytes.length - offset - 12 || kind === 0x6163544c) return false;
          offset += size + 12;
          if (kind === 0x49454e44) return size === 0 && offset === bytes.length;
        }
        return false;
      }
      if (bytes.length < 30 || view.getUint32(0) !== 0x52494646 || view.getUint32(8) !== 0x57454250
        || view.getUint32(4, true) + 8 !== bytes.length) return false;
      let offset = 12;
      for (; offset + 8 <= bytes.length;) {
        const kind = view.getUint32(offset), size = view.getUint32(offset + 4, true);
        if (size > bytes.length - offset - 8 || kind === 0x414e494d || kind === 0x414e4d46
          || (kind === 0x56503858 && (size < 10 || bytes[offset + 8] & 2))) return false;
        offset += size + 8 + (size % 2);
      }
      return offset === bytes.length;
    }

    async getPdfJsModule() {
      if (this.pdfJsModulePromise) return this.pdfJsModulePromise;

      const pdfJsUrl = this.dataset.pdfJsUrl || '';
      const pdfWorkerUrl = this.dataset.pdfWorkerUrl || '';
      if (!pdfJsUrl || !pdfWorkerUrl) throw new Error('PDF.js local assets are missing');

      this.pdfJsModulePromise = import(pdfJsUrl)
        .then((pdfjsLib) => {
          if (pdfjsLib?.GlobalWorkerOptions) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
          }
          return pdfjsLib;
        })
        .catch((error) => {
          this.pdfJsModulePromise = null;
          throw error;
        });

      return this.pdfJsModulePromise;
    }

    async renderPdfFilePreview(file) {
      if (!file?.arrayBuffer || typeof document === 'undefined' || typeof URL === 'undefined') {
        throw new Error('PDF preview is not available in this browser');
      }

      const pdfjsLib = await this.getPdfJsModule();
      const fileBuffer = await file.arrayBuffer();
      let loadingTask = null;
      let pdfDocument = null;
      let page = null;

      try {
        loadingTask = pdfjsLib.getDocument({
          data: new Uint8Array(fileBuffer),
          isEvalSupported: false,
          useWorkerFetch: false,
        });
        pdfDocument = await loadingTask.promise;
        page = await pdfDocument.getPage(1);

        const baseViewport = page.getViewport({ scale: 1 });
        const maxSide = STUDIO_PREVIEW_MAX_SIDE;
        const baseMaxSide = Math.max(baseViewport.width || 0, baseViewport.height || 0, 1);
        const scale = Math.min(3, maxSide / baseMaxSide);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));

        const context = canvas.getContext('2d', { alpha: true });
        if (!context) throw new Error('PDF preview canvas is not available');

        context.clearRect(0, 0, canvas.width, canvas.height);
        await page.render({
          canvasContext: context,
          viewport,
          background: 'rgba(255,255,255,0)',
        }).promise;

        const blob = await new Promise((resolve) => {
          canvas.toBlob((result) => resolve(result), 'image/png');
        });
        if (!blob) throw new Error('PDF preview export failed');

        return {
          previewUrl: URL.createObjectURL(blob),
          naturalRatio: canvas.width / Math.max(canvas.height, 1),
          sourceFormat: 'pdf',
        };
      } finally {
        try {
          if (typeof page?.cleanup === 'function') page.cleanup();
        } catch (error) {
          // The preview is already rendered; cleanup failures should not block the UI.
        }
        try {
          if (typeof pdfDocument?.destroy === 'function') {
            await pdfDocument.destroy();
          } else if (typeof loadingTask?.destroy === 'function') {
            loadingTask.destroy();
          }
        } catch (error) {
          // Ignore PDF.js teardown noise after a successful local render.
        }
      }
    }

    releaseStudioPreviewUrl(url) {
      if (url && !this.getStudioPreviewRecords().some(entry => entry.url === url)) URL.revokeObjectURL(url);
    }

    revokeStudioPreviewUrl(zoneKey, fileKey = '', { preserveFiles = [], artworkId = 'base' } = {}) {
      if (!this.studioPreviewUrls) return;

      if (fileKey) {
        const cacheKey = this.getStudioPreviewCacheKey(zoneKey, this.activeVariantId, fileKey, { artworkId });
        const cached = this.studioPreviewUrls.get(cacheKey);
        this.studioPreviewUrls.delete(cacheKey);
        this.releaseStudioPreviewUrl(cached?.url);
        return;
      }

      const prefix = `${this.getVariantStateKey()}::${zoneKey || 'zone'}::`;
      this.studioPreviewUrls.forEach((cached, cacheKey) => {
        if (!cacheKey.startsWith(prefix)) return;
        if (preserveFiles.includes(cached?.file) && cached.sourceFile === this.getStudioPreviewSource(cached.file, zoneKey)) return;
        this.studioPreviewUrls.delete(cacheKey);
        this.releaseStudioPreviewUrl(cached?.url);
      });
    }

    revokeStudioPreviewUrls() {
      const urls = new Set([...(this.studioPreviewUrls?.values() || [])].map(entry => entry.url).filter(Boolean));
      this.studioPreviewUrls?.clear();
      urls.forEach(url => this.releaseStudioPreviewUrl(url));
    }

    validateArtworkFiles(files = []) {
      const valid = files.every((file) => (
        file.size > 0 && file.size <= ARTWORK_UPLOAD_MAX_BYTES
        && /\.(ai|eps|psd|pdf|svg|png|jpe?g|webp|avif|gif)$/i.test(file.name || '')
      ));
      if (this.studioUploadError) {
        this.studioUploadError.textContent = valid ? '' : this.strings.uploadInvalid;
        this.studioUploadError.hidden = valid;
      }
      return valid;
    }

    onUploadDragOver(event) {
      event.preventDefault();
      if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
      if (this.activeZoneWrap && this.isStudioDesignBlockedForWrap(this.activeZoneWrap)) {
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
        this.updateStudioExclusivityNotice(this.getStudioExclusivityBlocker(this.activeZoneWrap));
        return;
      }
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      this.studioUploadZone?.classList.add('is-dragging');
    }

    onUploadDragLeave(event) {
      if (event.currentTarget !== this.studioUploadZone) return;
      if (event.relatedTarget && this.studioUploadZone?.contains(event.relatedTarget)) return;
      this.studioUploadZone?.classList.remove('is-dragging');
    }

    onUploadDrop(event) {
      event.preventDefault();
      this.studioUploadZone?.classList.remove('is-dragging');

      const files = Array.from(event.dataTransfer?.files || []);
      const fileInput = this.activeZoneWrap?.querySelector('[data-role="logo-file-input"]');
      if (!files.length || !fileInput || typeof DataTransfer === 'undefined') return;
      if (this.isStudioDesignBlockedForWrap(this.activeZoneWrap)) {
        this.updateStudioExclusivityNotice(this.getStudioExclusivityBlocker(this.activeZoneWrap));
        return;
      }
      if (!this.validateArtworkFiles(files)) return;

      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      fileInput.files = transfer.files;
      fileInput.disabled = false;
      this.handleLogoFileChange(fileInput, { openMobileEditor: true });
    }

    updateWrapStates() {
      this.optionWraps.forEach((wrap) => {
        const input = wrap.querySelector('[data-role="marking-input"]');
        const logoFileInput = wrap.querySelector('[data-role="logo-file-input"]');

        const isSelected = Boolean(input?.checked);
        const fileNames = this.getLogoFileNames(wrap);
        const hasLogo = fileNames.length > 0;

        wrap.classList.toggle('is-selected', isSelected);
        wrap.classList.toggle('has-logo-attached', isSelected && hasLogo);

        if (logoFileInput) {
          logoFileInput.disabled = !isSelected;
        }
      });
    }

    syncUI() {
      this.storeCurrentVariantLogoState();
      const selectedInputs = this.selectedInputs;
      const selectedLabels = selectedInputs.map((input) => input.dataset.zoneLabel || input.value).filter(Boolean);
      const selectedKeys = selectedInputs.map((input) => input.dataset.zoneKey).filter(Boolean);

      const logoZoneWraps = this.optionWraps.filter((wrap) => {
        const input = wrap.querySelector('[data-role="marking-input"]');
        return input?.checked && this.getLogoFileNames(wrap).length > 0;
      });

      const logoSummaries = logoZoneWraps
        .map((wrap) => {
          const zoneLabel = wrap.dataset.zoneLabel || '';
          const fileNames = this.getLogoFileNames(wrap);
          return fileNames.length ? `${zoneLabel}: ${fileNames.join(', ')}` : zoneLabel;
        })
        .filter(Boolean);

      const logoKeys = logoZoneWraps.map((wrap) => wrap.dataset.zoneKey).filter(Boolean);
      const summaryText = this.formatSummary(selectedLabels);
      const hasSelection = selectedLabels.length > 0;

      if (this.summaryValue) {
        this.summaryValue.textContent = summaryText;
        this.summaryValue.title = hasSelection ? summaryText : '';
      }

      if (this.summaryMeta) {
        this.summaryMeta.textContent = this.formatCount(selectedLabels.length);
      }

      this.classList.toggle('has-selection', hasSelection);
      this.updateWrapStates();

      this.setPropertyValue(this.selectionProperty, JSON.stringify(selectedKeys), selectedKeys.length > 0);
      this.setPropertyValue(this.logoProperty, JSON.stringify(logoKeys), logoKeys.length > 0);
      this.setPropertyValue(this.zoneKeysProperty, '', false);
      this.setPropertyValue(this.logoZoneKeysProperty, '', false);
      this.syncArtworkTransformProperty();

      if (this.estimateProperty) {
        const estimateValue = this.getEstimateValue(selectedKeys);
        const shouldExposeEstimate = selectedKeys.includes('custom') || selectedKeys.length > 1;
        this.setPropertyValue(this.estimateProperty, estimateValue, shouldExposeEstimate);
      }

      if (this.profileProperty && !this.profileProperty.value) {
        this.profileProperty.value = this.dataset.profileKey || '';
      }

      this.updateStudioTabs();
      this.updateStudioFile();
      this.renderRecentLogos();
      this.dispatchStudioStateChange();
    }

    formatSummary(labels) {
      if (!labels.length) return this.strings.summaryEmpty;
      return labels.join(', ');
    }

    formatCount(count) {
      if (!count) return this.strings.summaryOptional;
      let category = count === 1 ? 'one' : 'other';
      try {
        category = new Intl.PluralRules(document.documentElement.lang || 'en').select(count);
      } catch (error) {
        // The one/other fallback covers browsers without Intl.PluralRules.
      }
      return `${count} ${category === 'one' ? this.strings.countSingular : this.strings.countPlural}`;
    }

    getEstimateValue(selectedKeys) {
      if (selectedKeys.includes('custom')) return 'custom';
      if (selectedKeys.length > 1) return 'multiple';
      return '';
    }

    setPropertyValue(input, value, enabled = true) {
      if (!input) return;
      input.value = value;
      input.disabled = !enabled || !value;
    }
  }

  customElements.define('product-marking-selector', ProductMarkingSelector);
}
